import React, { useState, useEffect, useCallback } from 'react';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '../contexts/NavigationContext';
import { usePersistedState } from '../hooks/usePersistedState';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import AutoCreateWizard from '../components/autocreate/AutoCreateWizard';
import {
  Plus, Trash2, Edit2, Edit3, Play, Upload, ShieldCheck, ShieldOff,
  RotateCw, RotateCcw, ArrowRight, CheckCircle, XCircle, AlertTriangle, Lock,
  Users, Globe, Zap, Clock, ChevronRight, BookOpen, Eye, Beaker,
  Download, Check, Square, CheckSquare, User, UserPlus, ExternalLink,
  Activity, Power, Pause, Server, X, FlaskConical, Shield, ChevronDown, Maximize2, Minimize2,
  Send, MessageSquare, Bot, GitBranch, Sparkles, FileText, Cake,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import { useMarketplaceCatalogue } from '../data/marketplaceRegistry';
import { findMarketplaceForInstalled as sharedFindMarketplaceForInstalled } from '../data/marketplaceFromRegistry';
import { getAgentlets, pingAgentlet, validatePublish, publishSelective, uploadPolicyDocument, listPolicyDocuments, deletePolicyDocument, searchPolicyDocuments, getPolicyDocumentStats, testRunAgent, testRunChat, materializeAgentCanvas, createWorkflow, updateWorkflow, autoCreateAgent, stageAutoCreateRunbook, getWorkflows, ccListFrameworks } from '../services/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PHASE_MAP = { research: 'Build', build: 'Build', decide: 'Decide', act: 'Act' };

// ---------------------------------------------------------------------------
// Matching an installed connector → its marketplace manifest entry.
// The previous inline `find((m) => m.name === conn.name || m.baseUrl === conn.url)`
// missed matches when:
//   (a) the installed connector's `type` field holds the manifest id
//       (e.g. "servicenow") but its `name` was customized by the user
//   (b) the marketplace entry kept its legacy id (e.g. "servicenow-v1")
//       while the installed record stored the canonical manifest id
//   (c) casing drifted between install time and now
// This helper tries multiple match paths to resolve "No marketplace template
// available" errors in the Import from Connector dialog.
// ---------------------------------------------------------------------------
// Marketplace-match helper moved into ../data/marketplaceFromRegistry so
// ConnectorsPage and AgentsPage share one implementation. Keep a thin local
// alias so the rest of this file doesn't have to change.
const findMarketplaceForInstalled = sharedFindMarketplaceForInstalled;

const PHASE_TABS = [
  { key: 'build', name: 'Build', icon: '⚡', desc: 'Create skills, configure guardrails, run evals', lucideReached: 'CheckCircle', lucidePending: 'Clock' },
  { key: 'decide', name: 'Decide', icon: '⚖️', desc: 'Review eval scores, set autonomy levels', lucideReached: 'CheckCircle', lucidePending: 'Clock' },
  { key: 'act', name: 'Act', icon: '▶', desc: 'Assign users, publish to standalone agentlets', lucideReached: 'CheckCircle', lucidePending: 'Clock' },
];

const PHASE_ORDER = ['build', 'decide', 'act'];

// Normalize unknown/early phases (e.g. "research") to "build"
const normalizePhase = (p) => {
  const lower = (p || 'build').toLowerCase();
  return PHASE_ORDER.includes(lower) ? lower : 'build';
};

const EMPTY_SKILL_FORM = {
  name: '', description: '', category: 'iam', riskLevel: 'low',
  autonomy: 'HUMAN_CONTROLLED', inferenceMode: 'PLATFORM_INFERENCE', guardrails: [],
  connectorIds: [], importedEvals: [],
  // v5.3.0 — Skill Type System (rollback: remove these 3 fields)
  skillType: 'standard',
  linkedResourceId: '',
  policyConfig: null,
};

// v5.3.0 — Skill Type definitions. UPDATED P0-2: sourced from the
// canonical constants module so new kinds (e.g. wasm_skill) light up
// here automatically without a per-file edit.
import {
  SKILL_TYPES as SKILL_TYPES_REGISTRY,
  SKILL_TYPE_COLORS as SKILL_TYPE_COLORS_REGISTRY,
} from '../constants/skillTypes';
const SKILL_TYPES = SKILL_TYPES_REGISTRY.map((t) => ({
  id:    t.id,
  label: t.label,
  icon:  t.emoji,
  desc:  t.description,
  color: t.colors.text,
}));

const EMPTY_AGENT_FORM = {
  name: '', type: 'cognitive', phase: 'build', status: 'active', icon: '', gatewayUrl: '',
};

const EMPTY_ASSIGNMENT_FORM = { userId: '', groupId: '', accessLevel: 'execute' };

// Phase-aware color coding — grey → red-orange progression
const PHASE_COLORS = {
  build: { bg: '#f5f5f4', border: '#d6d3d1', text: '#57534e', accent: '#78716c' },
  decide: { bg: '#e3f4f1', border: '#a5d8d0', text: '#085041', accent: '#d97706' },
  act: { bg: '#e3f4f1', border: '#f5b0a8', text: '#9a2f23', accent: '#0d9488' },
};

// Shared button styles — polished design system
const btnBase = {
  padding: '7px 12px', borderRadius: 8, border: '1px solid #e7e5e4',
  background: '#ffffff', cursor: 'pointer', fontSize: 12,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s ease',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};
const btnPrimary = {
  ...btnBase, border: 'none',
  background: 'linear-gradient(135deg, #1c1917 0%, #292524 100%)',
  color: '#ffffff', fontWeight: 600,
  boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
};
const btnDanger = {
  ...btnBase, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b',
};
const btnSuccess = {
  ...btnBase, border: '1px solid #d6d3d1', background: '#e3f4f1', color: '#065f46',
};
const btnWarning = {
  ...btnBase, border: '1px solid #a5d8d0', background: '#e3f4f1', color: '#92400e',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AgentsPage() {
  const {
    agents: rawAgents, users, groups, connectors, assignments, executions,
    loadAgents, loadUsers, loadGroups, loadConnectors, loadExecutions,
    createAgent, deleteAgent: deleteAgentApi,
    addSkill: addSkillApi, updateSkill: updateSkillApi, removeSkill: removeSkillApi, unpublishSkill: unpublishSkillApi,
    pushSkill: pushSkillApi, pushAllSkills: pushAllSkillsApi, advanceSkill: advanceSkillApi, regressSkill: regressSkillApi,
    loadEvals, generateEval, runEval, getEvalTestCases,
    getEvalRuns, updateEvalSuite: updateEvalSuiteApi,
    deleteEvalSuite: deleteEvalSuiteApi, getLlmProviders,
    // Lifecycle
    advancePhase: advancePhaseApi,
    loadPhaseGates, loadSkillReviews, reviewSkill: reviewSkillApi,
    loadAssignments, createAssignment: createAssignmentApi, removeAssignment: removeAssignmentApi,
    publishAgent: publishAgentApi, loadPublications, rollbackPublication: rollbackPubApi,
    // State
    phaseGates, skillReviews, publications,
    loading, error,
    // Connectors ↔ Skills
    loadSkillConnectors, linkSkillConnector, unlinkSkillConnector,
    // Connector Assignments
    getAssignedConnectorIds,
  } = usePlatform();

  const { session, canWrite, canPromote, hasPermission, canManageOwnAgent, isSuperAdmin, currentRole } = useAuth();
  const { addToast } = useToast();
  const nav = useNavigation();

  // Marketplace catalogue (backend registry). Used below to enrich installed
  // connectors with their marketplace template (icon, vendor, LLM flag, etc.).
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();

  // RBAC: hide system agents from non-Super Admin users
  const agents = currentRole === 'Super Admin'
    ? rawAgents
    : rawAgents.filter(a => !a.isSystemAgent && a.category !== 'system');

  const [selectedAgent, setSelectedAgent] = usePersistedState('agents.selectedAgent', null);
  const [phase, setPhase] = usePersistedState('agents.phase', 'build');
  const [agentSortBy, setAgentSortBy] = usePersistedState('agents.sortBy', 'type');

  // Modal states
  const [showNewSkill, setShowNewSkill] = useState(false);
  const [showEditSkill, setShowEditSkill] = useState(false);
  const [showNewAgent, setShowNewAgent] = useState(false);
  // Auto Create flow.
  //   showAutoCreateSource — first modal: source picker
  //   autoCreateSourceKind — second modal: upload + start (runbook | workflow | doc_library)
  //   autoCreateRunId      — AutoCreateWizard mounts on this once a run starts; the
  //                          source/upload modals close, the wizard takes over
  //                          with live SSE pipeline + gap + review screens
  // Reference: RUNBOOK_TO_AGENT_DESIGN.md + AUTO_CREATE_AGENT_MOCKUP.html.
  const [showAutoCreateSource, setShowAutoCreateSource] = useState(false);
  const [autoCreateSourceKind, setAutoCreateSourceKind] = useState(null);
  const [autoCreateRunId, setAutoCreateRunId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  // Published-asset deletion lockdown error (2026-05-04). Set when a
  // delete attempt returns 409 with code='published_asset_deletion'.
  // Shape: the body returned by PublishedAssetDeletionException.toResponseBody()
  // — { error, code, asset:{kind,id,name}, linkedPublishedSkills:[...], remediation }
  const [publishedAssetError, setPublishedAssetError] = useState(null);
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [showGateResult, setShowGateResult] = useState(null);
  const [showEvalDetails, setShowEvalDetails] = useState(null);
  const [confirmDelEvalDetails, setConfirmDelEvalDetails] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(null);
  const [showTestRunPanel, setShowTestRunPanel] = useState(false);

  // Form states
  const [skillForm, setSkillForm] = useState({ ...EMPTY_SKILL_FORM });
  const [editingSkill, setEditingSkill] = useState(null);
  const [agentForm, setAgentForm] = useState({ ...EMPTY_AGENT_FORM });
  const [assignmentForm, setAssignmentForm] = useState({ ...EMPTY_ASSIGNMENT_FORM });
  const [reviewForm, setReviewForm] = useState({ decision: '', justification: '' });

  // Data states
  const [evals, setEvals] = useState([]);
  const [evalDetails, setEvalDetails] = useState({});
  const [evalResults, setEvalResults] = useState({});
  const [loadingEvals, setLoadingEvals] = useState(false);
  const [loadingExecs, setLoadingExecs] = useState(false);
  const [pubPage, setPubPage] = useState(0);
  const [pubTotalPages, setPubTotalPages] = useState(1);
  const [advancingPhase, setAdvancingPhase] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Agentlets Live & Publish Validation
  const [agentlets, setAgentlets] = useState([]);
  const [loadingAgentlets, setLoadingAgentlets] = useState(false);
  const [showPublishValidation, setShowPublishValidation] = useState(false);
  const [validationChecks, setValidationChecks] = useState([]);
  const [validatingPublish, setValidatingPublish] = useState(false);
  const [selectedAgentlets, setSelectedAgentlets] = useState([]);
  const [pingingAgentlet, setPingingAgentlet] = useState(null);
  const [agentletPage, setAgentletPage] = useState(0);
  const AGENTLETS_PER_PAGE = 10;
  const [skillConnectorMap, setSkillConnectorMap] = useState({});
  const [generatingEval, setGeneratingEval] = useState(null);
  const [runningEval, setRunningEval] = useState(null);

  // LLM-powered eval generation
  const [showLlmPicker, setShowLlmPicker] = useState(null); // skillId to generate for
  const [llmProviders, setLlmProviders] = useState([]);
  const [selectedLlmProvider, setSelectedLlmProvider] = useState(null);
  const [selectedLlmModel, setSelectedLlmModel] = useState(null);

  // Assigned connector IDs: 'loading' | Set<UUID>
  // Empty set means no connectors assigned → none available
  const [assignedConnectorIds, setAssignedConnectorIds] = useState('loading');

  // Import from Connector modal
  const [showImportConnector, setShowImportConnector] = useState(false);
  const [importStep, setImportStep] = useState('pick'); // 'pick' | 'select'
  const [importConnectorId, setImportConnectorId] = useState(null);
  const [importSelections, setImportSelections] = useState({ skills: [], guardrails: [], evals: [] });
  const [importingFromConnector, setImportingFromConnector] = useState(false);

  const orgId = session?.orgId;
  const tenantId = session?.tenantId;
  const userId = session?.userId;

  useEffect(() => {
    loadAgents({ orgId, tenantId });
    loadConnectors({ tenantId, orgId });
    loadUsers?.({ tenantId });
    loadGroups?.({ tenantId });
  }, [loadAgents, loadConnectors, loadUsers, loadGroups, orgId, tenantId]);

  // Pick up navigation intent from Overview page (or any cross-page navigation).
  // This runs once on mount and consumes the intent key so it doesn't persist.
  //
  // Two channels feed selection on mount, in priority order:
  //   1. ?agentId=<uuid> URL param  — used by the Auto Create success
  //      banner ("View AutoLets-… in Build →"). The wizard builds an
  //      <a href="/agents?agentId=…"> instead of pre-writing sessionStorage,
  //      so we read it here and strip the param via replaceState so a
  //      refresh doesn't redundantly re-apply it.
  //   2. sessionStorage.agentlets_nav_selectedAgent  — used by Overview
  //      page's bucket cards. Remains a one-shot intent key.
  // URL param wins when both are present (more recent navigation).
  useEffect(() => {
    let consumed = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('agentId');
      if (fromUrl) {
        consumed = fromUrl;
        params.delete('agentId');
        const qs = params.toString();
        const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (_) { /* non-fatal */ }
    if (!consumed) {
      const navIntent = sessionStorage.getItem('agentlets_nav_selectedAgent');
      if (navIntent) consumed = navIntent;
    }
    sessionStorage.removeItem('agentlets_nav_selectedAgent');
    if (consumed) setSelectedAgent(consumed);
  }, []);

  // Auto-select agent: restore from session, or default to first agent
  useEffect(() => {
    if (agents.length === 0) return;
    // If we have a stored selection, verify it still exists in the agents list
    if (selectedAgent) {
      const stillExists = agents.some((a) => String(a.id) === String(selectedAgent));
      if (stillExists) return; // valid selection, keep it
    }
    // Fall back to first agent
    setSelectedAgent(agents[0].id);
  }, [agents, selectedAgent]);

  const currentAgent = agents.find((a) => String(a.id) === String(selectedAgent));

  // Push breadcrumb context for selected agent + phase
  useEffect(() => {
    const crumbs = [];
    if (currentAgent) crumbs.push({ label: currentAgent.name, key: 'agent' });
    if (phase) crumbs.push({ label: phase.charAt(0).toUpperCase() + phase.slice(1), key: 'phase' });
    if (crumbs.length) nav.replaceContext(crumbs);
    return () => nav.clearContext();
  }, [currentAgent?.id, currentAgent?.name, phase]); // eslint-disable-line

  // Load assigned connector IDs when agent changes
  useEffect(() => {
    if (!currentAgent) { setAssignedConnectorIds(new Set()); return; }
    setAssignedConnectorIds('loading');
    getAssignedConnectorIds(currentAgent.id).then((result) => {
      if (result?.ok && result.data?.restricted) {
        // Agent has explicit assignments — only those connectors are allowed
        setAssignedConnectorIds(new Set(result.data.connectorIds));
      } else if (result?.ok && !result.data?.restricted) {
        // No assignments exist — no connectors should be available (must assign first)
        setAssignedConnectorIds(new Set());
      } else {
        setAssignedConnectorIds(new Set());
      }
    }).catch(() => setAssignedConnectorIds(new Set()));
  }, [currentAgent?.id, getAssignedConnectorIds]);

  // Sync phase tab to agent's actual phase (normalize unknown phases to "build")
  useEffect(() => {
    if (currentAgent?.phase) {
      setPhase(normalizePhase(currentAgent.phase));
    }
  }, [currentAgent?.id, currentAgent?.phase]);

  // Load phase-specific data when agent or phase changes
  useEffect(() => {
    if (!currentAgent) return;
    const agentId = currentAgent.id;

    if (phase === 'build') {
      setLoadingEvals(true);
      loadEvals(agentId).then((data) => {
        setEvals(data);
        // Sync evalDetails with loaded evals
        const newEvalDetails = {};
        data.forEach((ev) => {
          newEvalDetails[ev.skillId] = ev;
        });
        setEvalDetails(newEvalDetails);
        setLoadingEvals(false);
      });
    } else if (phase === 'decide') {
      setLoadingEvals(true);
      Promise.all([loadEvals(agentId), loadSkillReviews(agentId)]).then(([evalData]) => {
        setEvals(evalData);
        // Sync evalDetails with loaded evals
        const newEvalDetails = {};
        evalData.forEach((ev) => {
          newEvalDetails[ev.skillId] = ev;
        });
        setEvalDetails(newEvalDetails);
        setLoadingEvals(false);
      });
    } else if (phase === 'act') {
      setLoadingExecs(true);
      setPubPage(0);
      Promise.all([
        loadAssignments(agentId),
        loadPublications(agentId, 0, 10),
        loadExecutions(agentId),
        loadSkillReviews(agentId),
      ]).then(([, pubPageData]) => {
        if (pubPageData && pubPageData.totalPages) setPubTotalPages(pubPageData.totalPages);
        setLoadingExecs(false);
      });
    }

    loadPhaseGates(agentId);
  }, [currentAgent?.id, phase, loadEvals, loadSkillReviews, loadAssignments,
      loadPublications, loadExecutions, loadPhaseGates]);

  // Filtered connectors: only those explicitly assigned to this agent
  // If still loading or no assignments, return empty — connectors must be assigned first
  const availableConnectors = assignedConnectorIds === 'loading'
    ? []
    : connectors.filter((c) => assignedConnectorIds.has(c.id));

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  const agentPhase = normalizePhase(currentAgent?.phase);
  const phaseIndex = (p) => PHASE_ORDER.indexOf(p);
  const isPhaseAccessible = (p) => phaseIndex(p) <= phaseIndex(agentPhase);
  const isCurrentPhase = (p) => p === agentPhase;

  // -----------------------------------------------------------------------
  // Skill CRUD Handlers (Build Phase)
  // -----------------------------------------------------------------------
  const handleAddSkill = async () => {
    if (!hasPermission('skill.create')) {
      addToast('You do not have permission to create skills', 'error');
      return;
    }
    if (!skillForm.name.trim() || !skillForm.description.trim()) {
      addToast('Name and description required', 'warn');
      return;
    }
    const payload = {
      name: skillForm.name, description: skillForm.description,
      category: skillForm.category, riskLevel: skillForm.riskLevel,
      autonomy: skillForm.autonomy, inferenceMode: skillForm.inferenceMode,
      guardrails: skillForm.guardrails,
      // v5.3.0 — Skill Type fields (rollback: remove these 3 lines)
      skillType: skillForm.skillType || 'standard',
      linkedResourceId: skillForm.linkedResourceId || null,
      policyConfig: skillForm.policyConfig || null,
    };
    const result = await addSkillApi(currentAgent.id, payload);
    if (result?.ok) {
      // Link selected connectors — enforce assignment filter
      const allowedIds = new Set(availableConnectors.map((c) => c.id));
      const safeConnectorIds = skillForm.connectorIds.filter((id) => allowedIds.has(id));
      const newSkillId = result.data?.id;
      if (newSkillId && safeConnectorIds.length > 0) {
        await Promise.all(
          safeConnectorIds.map((cid) =>
            linkSkillConnector(currentAgent.id, newSkillId, {
              connectorId: cid, purpose: 'data_source',
            })
          )
        );
      }
      addToast(`Skill "${skillForm.name}" created`, 'success');
      // Generate eval suites for any imported evals
      const newSkillIdForEvals = result.data?.id;
      if (newSkillIdForEvals && skillForm.importedEvals?.length > 0) {
        for (const evalItem of skillForm.importedEvals) {
          try {
            await generateEval(currentAgent.id, { skillId: newSkillIdForEvals, evalType: evalItem.id, evalName: evalItem.name });
          } catch (e) { console.warn('Eval generation skipped:', evalItem.id, e); }
        }
        addToast(`Generated ${skillForm.importedEvals.length} eval suite(s)`, 'success');
      }
      loadAgents({ orgId, tenantId });
    } else {
      addToast(`Failed to create skill: ${result?.error || 'Unknown error'}`, 'error');
    }
    setShowNewSkill(false);
    setSkillForm({ ...EMPTY_SKILL_FORM });
  };

  const handleEditSkill = async () => {
    if (!hasPermission('skill.edit')) {
      addToast('You do not have permission to edit skills', 'error');
      return;
    }
    if (!editingSkill) return;
    const payload = {
      name: skillForm.name, description: skillForm.description,
      category: skillForm.category, riskLevel: skillForm.riskLevel,
      autonomy: skillForm.autonomy, inferenceMode: skillForm.inferenceMode,
      guardrails: skillForm.guardrails,
      // v5.3.0 — Skill Type fields on edit (rollback: remove these 3 lines)
      skillType: skillForm.skillType || 'standard',
      linkedResourceId: skillForm.linkedResourceId || null,
      policyConfig: skillForm.policyConfig || null,
    };
    const result = await updateSkillApi(currentAgent.id, editingSkill.id, payload);
    if (result?.ok) {
      // Reconcile connectors: unlink removed, link added — enforce assignment filter
      const allowedIds = new Set(availableConnectors.map((c) => c.id));
      const safeConnectorIds = skillForm.connectorIds.filter((id) => allowedIds.has(id));
      const existingLinks = skillConnectorMap[editingSkill.id] || [];
      const existingConnIds = existingLinks.map((l) => l.connectorId).filter((id) => allowedIds.has(id));
      const toLink = safeConnectorIds.filter((id) => !existingConnIds.includes(id));
      const toUnlink = existingConnIds.filter((id) => !safeConnectorIds.includes(id));

      await Promise.all([
        ...toLink.map((cid) =>
          linkSkillConnector(currentAgent.id, editingSkill.id, {
            connectorId: cid, purpose: 'data_source',
          })
        ),
        ...toUnlink.map((cid) =>
          unlinkSkillConnector(currentAgent.id, editingSkill.id, cid)
        ),
      ]);

      // Generate eval suites for any imported evals
      if (skillForm.importedEvals?.length > 0) {
        for (const evalItem of skillForm.importedEvals) {
          try {
            await generateEval(currentAgent.id, { skillId: editingSkill.id, evalType: evalItem.id, evalName: evalItem.name });
          } catch (e) { console.warn('Eval generation skipped:', evalItem.id, e); }
        }
        addToast(`Generated ${skillForm.importedEvals.length} eval suite(s)`, 'success');
      }

      addToast(`Skill "${skillForm.name}" updated`, 'success');
      loadAgents({ orgId, tenantId });
    } else {
      addToast(`Failed to update skill: ${result?.error || 'Unknown error'}`, 'error');
    }
    setShowEditSkill(false);
    setEditingSkill(null);
    setSkillForm({ ...EMPTY_SKILL_FORM });
  };

  const openEditSkill = async (skill) => {
    setEditingSkill(skill);
    // Load existing connector links for this skill
    const links = await loadSkillConnectors(currentAgent.id, skill.id);
    setSkillConnectorMap((prev) => ({ ...prev, [skill.id]: links }));
    // Parse guardrails from skill — backend stores as JSONB (could be array of IDs or object with keys)
    let existingGuardrails = [];
    if (Array.isArray(skill.guardrails)) {
      existingGuardrails = skill.guardrails;
    } else if (skill.guardrails && typeof skill.guardrails === 'object') {
      existingGuardrails = Object.keys(skill.guardrails).filter((k) => skill.guardrails[k]);
    }
    // Filter linked connectorIds to only those that are assigned to this agent
    const allowedIds = new Set(availableConnectors.map((c) => c.id));
    const filteredConnectorIds = links
      .map((l) => l.connectorId)
      .filter((id) => allowedIds.has(id));
    setSkillForm({
      name: skill.name || '', description: skill.description || '',
      category: skill.category || 'iam', riskLevel: skill.riskLevel || 'low',
      autonomy: skill.autonomy || 'HUMAN_CONTROLLED',
      inferenceMode: skill.inferenceMode || 'PLATFORM_INFERENCE',
      guardrails: existingGuardrails,
      connectorIds: filteredConnectorIds,
      // v5.3.0 — Persist skill type fields on edit (rollback: remove these 3 lines)
      skillType: skill.skillType || 'standard',
      linkedResourceId: skill.linkedResourceId || '',
      policyConfig: skill.policyConfig || null,
    });
    setShowEditSkill(true);
  };

  const handleDeleteSkill = async (skill) => {
    if (!hasPermission('skill.delete')) {
      addToast('You do not have permission to delete skills', 'error');
      return;
    }
    const result = await removeSkillApi(currentAgent.id, skill.id);
    if (result?.ok) {
      addToast(`Skill "${skill.name}" deleted`, 'success');
      loadAgents({ orgId, tenantId });
      setShowDeleteConfirm(null);
      return;
    }
    // Published-asset lockdown — backend returned 409 with structured
    // payload describing the published linkage. Surface the dedicated
    // error modal instead of a generic toast so the user can read what's
    // blocking and how to remediate (Unpublish via Skill Center).
    if (result?.data?.code === 'published_asset_deletion') {
      setPublishedAssetError(result.data);
      setShowDeleteConfirm(null);
      return;
    }
    addToast(`Failed to delete skill: ${result?.error || 'Unknown error'}`, 'error');
    setShowDeleteConfirm(null);
  };

  // Handler for the Unpublish action exposed on Build-phase skills that
  // carry wasPublished=true. Clears the published flags AND triggers an
  // agentlet re-sync (backend resyncAgentToGateway), making the skill
  // safely deletable. After success, re-load agents so the UI reflects
  // the cleared state and the Delete button enables.
  const handleUnpublishSkill = async (skill) => {
    if (!hasPermission('skill.delete')) {
      addToast('You do not have permission to unpublish skills', 'error');
      return;
    }
    const result = await unpublishSkillApi(currentAgent.id, skill.id);
    if (result?.ok) {
      addToast(
        `Skill "${skill.name}" unpublished — agentlet bundle re-synced. You may now delete the skill.`,
        'success',
      );
      loadAgents({ orgId, tenantId });
      // If the user reached unpublish via the published-asset error
      // modal, dismiss it — the blocker is gone.
      setPublishedAssetError(null);
      return;
    }
    addToast(`Failed to unpublish skill: ${result?.error || 'Unknown error'}`, 'error');
  };

  const handlePushSkill = async (skill) => {
    const result = await pushSkillApi(currentAgent.id, skill.id);
    if (result?.ok) {
      addToast(`Skill "${skill.name}" pushed`, 'success');
      loadAgents({ orgId, tenantId });
    } else {
      addToast(`Failed to push skill`, 'error');
    }
  };

  const handleAdvanceSkill = async (skill) => {
    const result = await advanceSkillApi(currentAgent.id, skill.id);
    if (result?.ok) {
      const data = result.data;
      if (data?.advanced) {
        addToast(data.message || `Skill "${skill.name}" advanced to ${data.toPhase}`, 'success');
        loadAgents({ orgId, tenantId });
      } else {
        addToast(data?.message || `Skill "${skill.name}" cannot advance yet.`, 'warn');
      }
    } else {
      addToast(`Failed to advance skill: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  const handleRegressSkill = async (skill) => {
    if (!window.confirm(
      `Regress "${skill.name}" back to Build phase?\n\n`
      + `This will:\n`
      + `• Clear eval score and review status\n`
      + `• Remove pushed status\n`
      + `• Require new eval runs and reviews to re-promote\n\n`
      + `The skill must go through the full Build→Decide→Act pipeline again before it can be published.`
    )) return;

    const result = await regressSkillApi(currentAgent.id, skill.id);
    if (result?.ok) {
      const data = result.data;
      addToast(data?.message || `Skill "${skill.name}" regressed to Build phase`, 'success');
      loadAgents({ orgId, tenantId });
    } else {
      addToast(`Failed to regress skill: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Eval Handlers (Build Phase)
  // -----------------------------------------------------------------------

  const reloadEvalsState = async () => {
    const updated = await loadEvals(currentAgent.id);
    setEvals(updated);
    const newEvalDetails = {};
    updated.forEach((ev) => { if (ev.skillId) newEvalDetails[ev.skillId] = ev; });
    setEvalDetails(newEvalDetails);
  };

  // Opens the LLM picker modal for eval generation (or regeneration — backend auto-archives old)
  const openGenerateEval = async (skillId) => {
    // Build provider list from connected & tested LLM connectors + Platform Default
    const llmConnectors = (connectors || []).filter((c) => {
      const mc = findMarketplaceForInstalled(marketplaceConnectors, c);
      return mc?.isLlmProvider && c.status === 'connected';
    });
    const providers = [
      { id: '__platform__', name: 'Platform Default', providerType: 'platform', modelId: 'agentlets-eval-v1', icon: '⚡', models: [] },
      ...llmConnectors.map((c) => {
        const mc = findMarketplaceForInstalled(marketplaceConnectors, c);
        return {
          id: c.id,
          name: mc?.name || c.name,
          providerType: mc?.vendor?.toLowerCase() || 'custom',
          icon: mc?.icon || '🔧',
          models: mc?.models || [],
          connectorId: c.id,
        };
      }),
    ];
    setLlmProviders(providers);
    setSelectedLlmProvider(providers[0]?.id || '__platform__');
    setSelectedLlmModel(null);
    setShowLlmPicker(skillId);
  };

  const handleGenerateEval = async (skillId, providerId, modelId) => {
    setShowLlmPicker(null);
    setGeneratingEval(skillId);
    const llmProviderId = providerId === '__platform__' ? undefined : providerId;
    const result = await generateEval(currentAgent.id, {
      skillId,
      llmProviderId,
      llmModelId: modelId || undefined,
    });
    setGeneratingEval(null);

    if (result?.ok) {
      const suite = result.data;
      addToast(`Eval suite created: ${suite.totalTests || 0} context-sensitive test cases generated`, 'success');
      await reloadEvalsState();
    } else {
      const msg = result?.error || 'Unknown error';
      addToast(`Failed to generate eval: ${msg}`, 'error');
    }
  };

  const handleRunEval = async (skillId, evalSuiteId) => {
    const currentEval = evals.find((e) => e.id === evalSuiteId);

    setRunningEval(evalSuiteId);
    const result = await runEval(currentAgent.id, evalSuiteId);
    setRunningEval(null);

    if (result?.ok) {
      const evalRun = result.data;
      setEvalResults((prev) => ({ ...prev, [evalSuiteId]: evalRun }));
      const score = evalRun.score != null ? (evalRun.score * 100).toFixed(0) : '?';
      const runCount = (currentEval?.runs?.length || 0) + 1;
      addToast(`Eval run #${runCount}: ${evalRun.passedTests}/${evalRun.totalTests} passed (${score}%)`, 'success');
      loadAgents({ orgId, tenantId });
      await reloadEvalsState();
      // Pass fresh run stats so handleOpenEvalDetail recomputes from latest data
      handleOpenEvalDetail({
        ...currentEval, id: evalSuiteId, skillId, status: 'completed',
        score: evalRun.score, passedTests: evalRun.passedTests, totalTests: evalRun.totalTests,
      });
    } else {
      addToast(`Failed to run eval: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  const handleDeleteEval = async (evalSuiteId) => {
    const result = await deleteEvalSuiteApi(currentAgent.id, evalSuiteId);
    if (result?.ok !== false) {
      addToast('Eval suite deleted', 'success');
      setShowEvalDetails(null);
      loadAgents({ orgId, tenantId });
      await reloadEvalsState();
    } else {
      addToast(`Failed to delete eval: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  const handleOpenEvalDetail = async (evalSuite) => {
    setShowEvalDetails({ ...evalSuite, testCases: [], runs: [], loadingDetail: true });
    try {
      const [testCases, runs] = await Promise.all([
        getEvalTestCases(currentAgent.id, evalSuite.id),
        getEvalRuns(currentAgent.id, evalSuite.id),
      ]);
      // If runs exist, the eval has been executed — force status to 'completed' for UI consistency
      const resolvedStatus = (runs && runs.length > 0) ? 'completed' : evalSuite.status;
      // Recompute summary stats from actual test case status (source of truth)
      const liveTotal = testCases ? testCases.length : (evalSuite.totalTests || 0);
      const livePassed = testCases
        ? testCases.filter((tc) => tc.status === 'passed' || tc.passed === true).length
        : (evalSuite.passedTests || 0);
      const liveScore = liveTotal > 0 ? livePassed / liveTotal : (evalSuite.score || 0);
      setShowEvalDetails((prev) => prev ? {
        ...prev, testCases, runs,
        status: resolvedStatus,
        score: liveScore,
        passedTests: livePassed,
        totalTests: liveTotal,
        loadingDetail: false,
      } : null);
    } catch (err) {
      // If backend can't find the eval (e.g. after DB reset), show error state
      setShowEvalDetails((prev) => prev ? { ...prev, loadingDetail: false } : null);
      addToast('Could not load eval details — the eval may have been deleted on the server.', 'warn');
    }
  };

  // -----------------------------------------------------------------------
  // Import from Connector Helpers
  // -----------------------------------------------------------------------
  const openImportConnector = () => {
    setImportStep('pick');
    setImportConnectorId(null);
    setImportSelections({ skills: [], guardrails: [], evals: [] });
    setShowImportConnector(true);
  };

  const getMarketplaceForConnector = (connectorId) => {
    const conn = connectors.find((c) => String(c.id) === String(connectorId));
    if (!conn) return null;
    return findMarketplaceForInstalled(marketplaceConnectors, conn);
  };

  const pickImportConnector = (connectorId) => {
    const mc = getMarketplaceForConnector(connectorId);
    if (!mc) {
      addToast('This connector has no marketplace template with importable items', 'warn');
      return;
    }
    setImportConnectorId(connectorId);
    // Default: select all
    setImportSelections({
      skills: mc.skills.map((s) => s.id),
      guardrails: mc.guardrails.map((g) => g.id),
      evals: mc.evals.map((e) => e.name),
    });
    setImportStep('select');
  };

  const toggleImportItem = (category, id) => {
    setImportSelections((prev) => {
      const arr = prev[category];
      const next = arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
      return { ...prev, [category]: next };
    });
  };

  const toggleAllImportCategory = (category, allIds) => {
    setImportSelections((prev) => {
      const allSelected = allIds.every((id) => prev[category].includes(id));
      return { ...prev, [category]: allSelected ? [] : [...allIds] };
    });
  };

  const handleImportFromConnector = async () => {
    if (!importConnectorId || !currentAgent) return;
    const mc = getMarketplaceForConnector(importConnectorId);
    if (!mc) return;

    setImportingFromConnector(true);
    let skillsCreated = 0;
    let guardrailsApplied = 0;
    let evalsSeeded = 0;

    try {
      // 1) Create selected skills and link the connector
      for (const skillId of importSelections.skills) {
        const mSkill = mc.skills.find((s) => s.id === skillId);
        if (!mSkill) continue;

        // Collect guardrails from this skill's inline guardrails + selected top-level guardrails
        const guardrailIds = [];
        if (importSelections.guardrails.length > 0) {
          // Map connector-level guardrail IDs to standard guardrail types where possible
          const guardrailMapping = {
            'g-rate': 'rate_limit', 'g-cost': 'scope_limit', 'g-auth': 'audit_log',
            'g-pii': 'audit_log', 'g-geo': 'scope_limit', 'g-consent': 'approval_required',
            'g-cache': 'rate_limit', 'g-sandbox': 'scope_limit',
          };
          importSelections.guardrails.forEach((gId) => {
            const mapped = guardrailMapping[gId];
            if (mapped && !guardrailIds.includes(mapped)) guardrailIds.push(mapped);
          });
        }

        const payload = {
          name: mSkill.name,
          description: `${mSkill.description} [${mSkill.method} ${mSkill.endpoint}]`,
          category: 'connectors',
          riskLevel: mc.tier === 'premium' ? 'medium' : 'low',
          autonomy: 'HUMAN_CONTROLLED',
          inferenceMode: 'PLATFORM_INFERENCE',
          guardrails: guardrailIds,
          // Connector skill execution metadata — required for live API calls
          endpoint: mSkill.endpoint || null,
          httpMethod: mSkill.method || 'GET',
          baseUrl: mc.baseUrl || null,
          credentialType: mSkill.credentialType || null,
          requiredScopes: mSkill.requiredScopes || null,
          params: mSkill.params || null,
          samplePayload: mSkill.samplePayload || null,
          accessDescription: mSkill.accessDescription || null,
        };

        const result = await addSkillApi(currentAgent.id, payload);
        if (result?.ok) {
          skillsCreated++;
          guardrailsApplied += guardrailIds.length;
          const newSkillId = result.data?.id;
          if (newSkillId) {
            await linkSkillConnector(currentAgent.id, newSkillId, {
              connectorId: importConnectorId, purpose: 'data_source',
            });
            // Generate eval for selected eval types
            if (importSelections.evals.length > 0 && newSkillId) {
              const evalResult = await generateEval(currentAgent.id, { skillId: newSkillId });
              if (evalResult?.ok) evalsSeeded++;
            }
          }
        }
      }

      // If no skills selected but evals were selected and agent already has skills, seed evals for existing skills
      if (importSelections.skills.length === 0 && importSelections.evals.length > 0) {
        const existingSkills = currentAgent.skills || [];
        for (const skill of existingSkills) {
          const evalResult = await generateEval(currentAgent.id, { skillId: skill.id });
          if (evalResult?.ok) evalsSeeded++;
        }
      }

      const parts = [];
      if (skillsCreated) parts.push(`${skillsCreated} skill${skillsCreated > 1 ? 's' : ''}`);
      if (guardrailsApplied) parts.push(`guardrails applied`);
      if (evalsSeeded) parts.push(`${evalsSeeded} eval suite${evalsSeeded > 1 ? 's' : ''} seeded`);
      addToast(`Imported: ${parts.join(', ') || 'nothing selected'}`, parts.length ? 'success' : 'warn');

      loadAgents({ orgId, tenantId });
      // Reload evals
      const updated = await loadEvals(currentAgent.id);
      setEvals(updated);
      const newEvalDetails = {};
      updated.forEach((ev) => { newEvalDetails[ev.skillId] = ev; });
      setEvalDetails(newEvalDetails);
    } catch (err) {
      addToast(`Import failed: ${err.message}`, 'error');
    }

    setImportingFromConnector(false);
    setShowImportConnector(false);
  };

  // -----------------------------------------------------------------------
  // Phase Advancement Handler
  // -----------------------------------------------------------------------
  const handleAdvancePhase = async () => {
    setAdvancingPhase(true);
    const result = await advancePhaseApi(currentAgent.id, userId);
    setAdvancingPhase(false);

    if (result?.ok) {
      const gate = result.data?.gate;
      if (gate?.status === 'failed') {
        setShowGateResult(gate);
        addToast('Phase gate check failed. Review the results.', 'error');
      } else {
        setShowGateResult(gate);
        addToast(`Advanced to ${PHASE_MAP[result.data?.agent?.phase] || 'next phase'}`, 'success');
        loadAgents({ orgId, tenantId });
      }
    } else {
      addToast(`Phase advancement failed: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Skill Review Handler (Decide Phase)
  // -----------------------------------------------------------------------
  const handleReviewSkill = async (skillId, decision, justification = '') => {
    const result = await reviewSkillApi(currentAgent.id, skillId, {
      decision, justification, userId,
    });
    if (result?.ok) {
      addToast(`Skill reviewed: ${decision}`, 'success');
      loadAgents({ orgId, tenantId });
      loadSkillReviews(currentAgent.id);
      setShowReviewModal(null);
      setReviewForm({ decision: '', justification: '' });
    } else {
      addToast(`Review failed: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Assignment Handlers (Act Phase)
  // -----------------------------------------------------------------------
  const handleCreateAssignment = async () => {
    if (!hasPermission('assignment.manage')) {
      addToast('You do not have permission to manage assignments', 'error');
      return;
    }
    if (!assignmentForm.userId && !assignmentForm.groupId) {
      addToast('Select a user or group', 'warn');
      return;
    }
    const payload = {
      ...(assignmentForm.userId ? { userId: assignmentForm.userId } : {}),
      ...(assignmentForm.groupId ? { groupId: assignmentForm.groupId } : {}),
      accessLevel: assignmentForm.accessLevel,
      assignedBy: userId,
    };
    const result = await createAssignmentApi(currentAgent.id, payload);
    if (result?.ok) {
      addToast('Assignment created', 'success');
      loadAssignments(currentAgent.id);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown error'}`, 'error');
    }
    setShowNewAssignment(false);
    setAssignmentForm({ ...EMPTY_ASSIGNMENT_FORM });
  };

  const handleRemoveAssignment = async (assignmentId) => {
    const result = await removeAssignmentApi(currentAgent.id, assignmentId);
    if (result?.ok) {
      addToast('Assignment removed', 'success');
      loadAssignments(currentAgent.id);
    } else {
      addToast(`Failed: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Load Agentlets (Act Phase)
  // -----------------------------------------------------------------------
  const loadAgentletsList = useCallback(async () => {
    setLoadingAgentlets(true);
    const result = await getAgentlets({ tenantId });
    setLoadingAgentlets(false);
    if (result?.ok) {
      // AgentletController returns { agentlets: [...], total, page, size, connected, stale, disconnected }
      if (result.data?.agentlets && Array.isArray(result.data.agentlets)) {
        setAgentlets(result.data.agentlets);
      } else if (Array.isArray(result.data)) {
        setAgentlets(result.data);
      } else if (result.data?.content && Array.isArray(result.data.content)) {
        setAgentlets(result.data.content);
      } else {
        setAgentlets([]);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (currentAgent && phase === 'act') {
      loadAgentletsList();
    }
  }, [currentAgent?.id, phase, loadAgentletsList]);

  const handlePingAgentlet = async (agentletId) => {
    setPingingAgentlet(agentletId);
    const result = await pingAgentlet(agentletId);
    setPingingAgentlet(null);
    if (result?.ok) {
      addToast(`Agentlet is ${result.data?.status || 'reachable'}`, 'success');
      loadAgentletsList(); // Refresh status
    } else {
      addToast(`Ping failed: ${result?.error || 'Unreachable'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Publish Handler (Act Phase)
  // -----------------------------------------------------------------------
  const handlePublish = async () => {
    // Step 1: Show validation modal with live checks
    setShowPublishValidation(true);
    setValidatingPublish(true);
    setValidationChecks([]);
    setSelectedAgentlets([]);

    const result = await validatePublish(currentAgent.id);
    setValidatingPublish(false);

    if (result?.ok && result.data?.checks) {
      setValidationChecks(result.data.checks);
      // Auto-select all agentlets (including disconnected — they'll sync on reconnect)
      setSelectedAgentlets(agentlets.map((a) => a.id));
    } else {
      setValidationChecks([{ check: 'validation_error', passed: false, message: result?.error || 'Failed to run validation' }]);
    }
  };

  const handlePublishSelective = async () => {
    if (selectedAgentlets.length === 0) {
      addToast('Select at least one agentlet to deploy to', 'warn');
      return;
    }
    setPublishing(true);
    const result = await publishSelective(currentAgent.id, selectedAgentlets, userId);
    setPublishing(false);
    setShowPublishValidation(false);

    if (result?.ok) {
      // Check per-agentlet push results if available
      let pushDetail = '';
      try {
        const pubResult = result.data?.publicationResult ? JSON.parse(result.data.publicationResult) : null;
        if (pubResult) {
          const ok = pubResult.successCount || 0;
          const fail = pubResult.failureCount || 0;
          if (fail > 0) {
            const reasons = Object.values(pubResult.agentlets || {}).filter(a => a.status === 'failed').map(a => a.reason).join('; ');
            pushDetail = ` (${ok} pushed, ${fail} failed: ${reasons})`;
          }
        }
      } catch (_) {}
      addToast(`Agent published v${result.data?.version || '?'} to ${selectedAgentlets.length} agentlet(s)${pushDetail}`, pushDetail.includes('failed') ? 'warn' : 'success');
      loadAgents({ orgId, tenantId });
      loadAgentletsList();
    } else {
      addToast(`Publish failed: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  const handleRollback = async (pubId) => {
    const result = await rollbackPubApi(currentAgent.id, pubId, userId);
    if (result?.ok) {
      addToast('Rolled back successfully', 'success');
      // Reload both publications AND agent list so the "Published" status updates
      await Promise.all([
        loadPublications(currentAgent.id),
        loadAgents(),
      ]);
    } else {
      addToast(`Rollback failed: ${result?.error || 'Unknown error'}`, 'error');
    }
  };

  // -----------------------------------------------------------------------
  // Create Agent Handler
  // -----------------------------------------------------------------------
  const handleCreateAgent = async () => {
    if (!agentForm.name.trim()) {
      addToast('Agent name is required', 'warn');
      return;
    }
    const payload = {
      name: agentForm.name.trim(), type: agentForm.type,
      phase: 'build', status: 'active', orgId, tenantId,
      ...(agentForm.icon?.trim() ? { icon: agentForm.icon.trim() } : {}),
      ...(agentForm.gatewayUrl?.trim() ? { gatewayUrl: agentForm.gatewayUrl.trim() } : {}),
    };
    const result = await createAgent(payload);
    if (result?.ok) {
      addToast(`Agent "${agentForm.name}" created in Build phase`, 'success');
      setSelectedAgent(result.data?.id || null);
      loadAgents({ orgId, tenantId });
    } else {
      addToast(`Failed to create agent: ${result?.error || 'Unknown error'}`, 'error');
    }
    setShowNewAgent(false);
    setAgentForm({ ...EMPTY_AGENT_FORM });
  };

  // -----------------------------------------------------------------------
  // Delete Agent Handler
  // -----------------------------------------------------------------------
  const [showDeleteAgentConfirm, setShowDeleteAgentConfirm] = useState(null);

  const handleDeleteAgent = async () => {
    if (!showDeleteAgentConfirm) return;
    const agentId = showDeleteAgentConfirm.id;
    const agentName = showDeleteAgentConfirm.name;
    const result = await deleteAgentApi(agentId);
    if (result?.ok) {
      addToast(`Agent "${agentName}" deleted`, 'success');
      setSelectedAgent(null);
      loadAgents({ orgId, tenantId });
    } else {
      addToast(`Failed to delete agent: ${result?.error || 'Unknown error'}`, 'error');
    }
    setShowDeleteAgentConfirm(null);
  };

  // ===================================================================
  // RENDER
  // ===================================================================
  return (
    <PageContainer>
      {/* ─── Header ─── */}
      {/* Standardised PageHeader so Agent Lifecycle picks up the same
          icon-square / title / subtitle / right-actions arrangement as
          every other landing page. Cake icon mirrors the sidebar group's
          existing 🤖 affordance — agents are the platform's "live"
          artefact, the cake icon ties into the Lifecycle metaphor. */}
      <PageHeader
        icon={Cake}
        title="Agent Lifecycle"
        subtitle="Build, review, and publish agents through gated phases"
        actions={
          <>
            {/* Test Run button — only visible when an Act-phase agent is selected */}
            {currentAgent && agentPhase === 'act' && phase === 'act' && hasPermission('agent.edit') && (
              <button onClick={() => setShowTestRunPanel(true)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 20px', fontSize: 13, borderRadius: 10, fontWeight: 600,
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                background: 'linear-gradient(135deg, #0d9488, #d4380d)', color: '#fff',
                boxShadow: '0 2px 8px rgba(226,74,58,0.25)',
              }}>
                <FlaskConical size={16} /> Test Run
              </button>
            )}
            {hasPermission('agent.create') && (
              <>
                <button
                  onClick={() => setShowAutoCreateSource(true)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 20px', fontSize: 13, borderRadius: 10, fontWeight: 600,
                    border: '1px solid #a5d8d0', cursor: 'pointer', fontFamily: 'inherit',
                    background: 'linear-gradient(180deg, #fef6f4 0%, #fff 70%)',
                    color: '#9a2f23',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                  title="Auto Create from a runbook, an existing workflow, or your policy doc library"
                >
                  <Sparkles size={16} /> Auto Create
                </button>
                <button onClick={() => setShowNewAgent(true)} style={{
                  ...btnPrimary, padding: '10px 20px', fontSize: 13, borderRadius: 10,
                }}>
                  <Plus size={16} /> New Agent
                </button>
              </>
            )}
          </>
        }
      />

      {/* Loading / Error */}
      {loading && agents.length === 0 && (
        <div style={{
          textAlign: 'center', padding: 64, color: '#a8a29e', fontSize: 14,
          background: '#ffffff', borderRadius: 12, border: '1px solid #f5f5f4',
        }}>
          <RotateCw size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 12, color: '#d6d3d1' }} />
          <div>Loading agents...</div>
        </div>
      )}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '14px 18px', marginBottom: 20, color: '#991b1b', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <XCircle size={16} style={{ flexShrink: 0 }} />
          <div><strong>Error:</strong> {error}</div>
        </div>
      )}

      {/* ─── Agent Selector ─── */}
      <div style={{
        marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12,
        background: '#ffffff', padding: '12px 16px', borderRadius: 12,
        border: '1px solid #e7e5e4', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: currentAgent
            ? `linear-gradient(135deg, ${PHASE_COLORS[normalizePhase(currentAgent.phase)]?.accent || '#0d9488'} 0%, ${PHASE_COLORS[normalizePhase(currentAgent.phase)]?.accent || '#0d9488'}99 100%)`
            : '#e7e5e4',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, color: '#fff', flexShrink: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}>
          {(currentAgent?.icon && currentAgent.icon.length <= 2) ? currentAgent.icon : '🤖'}
        </div>
        <select
          value={selectedAgent || ''}
          onChange={(e) => { setSelectedAgent(e.target.value); setShowTestRunPanel(false); }}
          style={{
            padding: '10px 14px', borderRadius: 8, border: '1px solid #e7e5e4',
            background: '#fafaf9', fontSize: 14, flex: 1, minWidth: 200,
            fontWeight: 500, color: '#1c1917', fontFamily: 'inherit',
            cursor: 'pointer', outline: 'none',
          }}
        >
          {(() => {
            const agentLabel = (agent) =>
              `${agent.name} — ${PHASE_MAP[agent.phase?.toLowerCase()] || agent.phase}${agent.published ? ' ✓ Published' : ''}`;

            // Sort helpers
            const sortFn = (a, b) => {
              if (agentSortBy === 'created') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
              if (agentSortBy === 'modified') return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
              return a.name.localeCompare(b.name); // default for type grouping
            };

            if (agentSortBy === 'type') {
              // Group by type: AutoLets, Lets, ExteLets, SysLets.
              // AutoLets is a derivative of native (non-external,
              // non-system) agents distinguished by the `AutoLets-`
              // name prefix that the Auto Create wizard stamps. The
              // Overview page already partitions by this convention
              // (see classifyAgentType in constants/agentTypes.js);
              // mirroring it here so the lifecycle dropdown has the
              // same vocabulary — the buyer who picks an agent on the
              // Overview AutoLets tab shouldn't have to hunt through
              // a flat "Lets" group on Lifecycle.
              const isAutoLet = (a) =>
                typeof a.name === 'string' && a.name.startsWith('AutoLets-');
              const autolets = agents.filter(a => !a.isExternal && !a.isSystemAgent && isAutoLet(a)).sort(sortFn);
              const external = agents.filter(a => a.isExternal && !a.isSystemAgent).sort(sortFn);
              const native_  = agents.filter(a => !a.isExternal && !a.isSystemAgent && !isAutoLet(a)).sort(sortFn);
              const system   = agents.filter(a => a.isSystemAgent).sort(sortFn);

              return (
                <>
                  {autolets.length > 0 && (
                    <optgroup label={`AutoLets (${autolets.length})`}>
                      {autolets.map(agent => (
                        <option key={agent.id} value={agent.id}>✨ {agentLabel(agent)}</option>
                      ))}
                    </optgroup>
                  )}
                  {native_.length > 0 && (
                    <optgroup label={`Lets (${native_.length})`}>
                      {native_.map(agent => (
                        <option key={agent.id} value={agent.id}>{agentLabel(agent)}</option>
                      ))}
                    </optgroup>
                  )}
                  {external.length > 0 && (
                    <optgroup label={`ExteLets (${external.length})`}>
                      {external.map(agent => (
                        <option key={agent.id} value={agent.id}>⇄ {agentLabel(agent)}</option>
                      ))}
                    </optgroup>
                  )}
                  {system.length > 0 && (
                    <optgroup label={`SysLets (${system.length})`}>
                      {system.map(agent => (
                        <option key={agent.id} value={agent.id}>🛡️ {agentLabel(agent)}</option>
                      ))}
                    </optgroup>
                  )}
                </>
              );
            }

            // Flat sorted list for date-based sorts
            const sorted = [...agents].sort(sortFn);
            return sorted.map(agent => (
              <option key={agent.id} value={agent.id}>
                {agent.isSystemAgent ? '🛡️ ' : agent.isExternal ? '⇄ ' : ''}{agentLabel(agent)}
              </option>
            ));
          })()}
        </select>
        {/* Sort selector */}
        <select
          value={agentSortBy}
          onChange={(e) => setAgentSortBy(e.target.value)}
          title="Sort agents"
          style={{
            padding: '9px 10px', borderRadius: 8, border: '1px solid #e7e5e4',
            background: '#fafaf9', fontSize: 12, fontWeight: 500, color: '#78716c',
            cursor: 'pointer', outline: 'none', minWidth: 110,
          }}
        >
          <option value="type">Sort: Type</option>
          <option value="created">Sort: Created</option>
          <option value="modified">Sort: Modified</option>
        </select>
        {currentAgent && canManageOwnAgent(currentAgent) && !currentAgent.isSystemAgent && (
          <button
            onClick={() => setShowDeleteAgentConfirm(currentAgent)}
            title="Delete Agent"
            style={{ ...btnDanger, padding: '9px 14px', fontSize: 12, borderRadius: 8 }}
          >
            <Trash2 size={14} /> Delete
          </button>
        )}
      </div>

      {currentAgent && (
        <>
          {/* ─── SysLets banner ─── */}
          {/* Warm graphite gradient — matches the SysLets accent in
              constants/agentTypes.js. Stone-100/200 light, stone-700
              text, stone-300 border. Stays in the brand's warm-family
              vocabulary so the banner reads as foundation rather than
              an alarm or a foreign accent. */}
          {currentAgent.isSystemAgent && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '14px 20px', marginBottom: 20,
              background: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)',
              borderRadius: 10, border: '1px solid #D6D3D1',
            }}>
              <span style={{ fontSize: 22 }}>🛡️</span>
              <div style={{ minWidth: 200 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1C1917' }}>
                  SysLets — Agentlets System Agent
                </div>
                <div style={{ fontSize: 12, color: '#44403C', marginTop: 2 }}>
                  Platform-managed agent with {currentAgent.skills?.length || 0} system skills. Always active, always in production.
                  {isSuperAdmin?.() ? ' SuperAdmin: full control.' : ' Read-only — only Super Admin can modify system resources.'}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(currentAgent.skills || []).map((s) => (
                  <span key={s.id} style={{
                    padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                    background: '#FFFFFF', color: '#44403C', border: '1px solid #D6D3D1',
                  }}>
                    {s.skillType === 'workflow_skill' ? '⚡' : s.skillType === 'connector_skill' ? '🔌' : s.skillType === 'policy_skill' ? '📋' : '⚙️'} {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ─── Unified Phase Navigation ─── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 0,
            marginBottom: 28,
            background: '#ffffff',
            borderRadius: 14,
            border: '1px solid #e7e5e4',
            padding: 4,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            {PHASE_TABS.map((p, i) => {
              const accessible = isPhaseAccessible(p.key);
              const current = isCurrentPhase(p.key);
              const isSelected = phase === p.key;
              const reached = PHASE_ORDER.indexOf(normalizePhase(agentPhase)) >= i;
              const colors = PHASE_COLORS[p.key];
              return (
                <React.Fragment key={p.key}>
                  <button
                    onClick={() => accessible && setPhase(p.key)}
                    disabled={!accessible}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: 10,
                      border: 'none',
                      background: isSelected
                        ? `linear-gradient(135deg, ${colors.bg} 0%, #ffffff 100%)`
                        : 'transparent',
                      boxShadow: isSelected ? `0 2px 8px ${colors.accent}20, inset 0 0 0 1px ${colors.border}` : 'none',
                      color: !accessible ? '#d6d3d1' : isSelected ? colors.text : reached ? '#57534e' : '#a8a29e',
                      fontSize: 13,
                      fontWeight: isSelected ? 650 : 500,
                      cursor: accessible ? 'pointer' : 'not-allowed',
                      opacity: accessible ? 1 : 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontFamily: 'inherit',
                      transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => {
                      if (accessible && !isSelected) e.currentTarget.style.background = '#fafaf9';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {/* Phase status icon — borrowed from progress bar */}
                    {reached
                      ? <CheckCircle size={15} color={colors.accent} style={{ flexShrink: 0 }} />
                      : <Clock size={15} color={accessible ? '#a8a29e' : '#d6d3d1'} style={{ flexShrink: 0 }} />}
                    <span style={{ letterSpacing: '-0.01em' }}>{p.name}</span>
                    {current && (
                      <span style={{
                        fontSize: 9,
                        padding: '2px 7px',
                        borderRadius: 6,
                        background: colors.accent,
                        color: '#ffffff',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        lineHeight: 1.4,
                      }}>
                        CURRENT
                      </span>
                    )}
                    {!accessible && (
                      <span style={{ fontSize: 10, color: '#d6d3d1', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ShieldOff size={10} /> Locked
                      </span>
                    )}
                  </button>
                  {/* Chevron connector between tabs */}
                  {i < PHASE_TABS.length - 1 && (
                    <ChevronRight size={14} color={
                      PHASE_ORDER.indexOf(normalizePhase(agentPhase)) > i ? '#78716c' : '#e7e5e4'
                    } style={{ flexShrink: 0, margin: '0 -2px' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* ============ BEGIN v5.3.0 — Linked Workflows (rollback: remove this block) ============ */}
          {currentAgent && (
            <LinkedWorkflowsSection
              agent={currentAgent}
              agents={agents}
              orgId={orgId}
              tenantId={tenantId}
              canWrite={canWrite}
              addToast={addToast}
            />
          )}
          {/* ============ END v5.3.0 — Linked Workflows ============ */}

          {/* ============ BUILD PHASE ============ */}
          {phase === 'build' && (
            <BuildPhasePanel
              agent={currentAgent}
              evals={evals}
              evalDetails={evalDetails}
              evalResults={evalResults}
              loadingEvals={loadingEvals}
              canWrite={canWrite}
              canPromote={canPromote}
              hasPermission={hasPermission}
              isCurrentPhase={isCurrentPhase('build')}
              agentPhase={agentPhase}
              onAddSkill={() => setShowNewSkill(true)}
              onImportFromConnector={openImportConnector}
              onEditSkill={openEditSkill}
              onDeleteSkill={(skill) => setShowDeleteConfirm(skill)}
              onUnpublishSkill={handleUnpublishSkill}
              onPushSkill={handlePushSkill}
              onGenerateEval={openGenerateEval}
              onRunEval={handleRunEval}
              onDeleteEval={handleDeleteEval}
              onViewEvalDetail={handleOpenEvalDetail}
              generatingEval={generatingEval}
              runningEval={runningEval}
              onAdvancePhase={handleAdvancePhase}
              advancingPhase={advancingPhase}
              onAdvanceSkill={handleAdvanceSkill}
              onRegressSkill={handleRegressSkill}
            />
          )}

          {/* ============ DECIDE PHASE ============ */}
          {phase === 'decide' && (
            <DecidePhasePanel
              agent={currentAgent}
              evals={evals}
              skillReviews={skillReviews}
              loadingEvals={loadingEvals}
              canWrite={canWrite}
              canPromote={canPromote}
              hasPermission={hasPermission}
              isCurrentPhase={isCurrentPhase('decide')}
              agentPhase={agentPhase}
              onReviewSkill={handleReviewSkill}
              onShowReviewModal={setShowReviewModal}
              onAdvancePhase={handleAdvancePhase}
              advancingPhase={advancingPhase}
            />
          )}

          {/* ============ ACT PHASE ============ */}
          {phase === 'act' && (
            <ActPhasePanel
              agent={currentAgent}
              agents={agents}
              users={users}
              groups={groups}
              assignments={assignments}
              publications={publications}
              executions={executions}
              loadingExecs={loadingExecs}
              canWrite={canWrite}
              hasPermission={hasPermission}
              isCurrentPhase={isCurrentPhase('act')}
              onAddAssignment={() => setShowNewAssignment(true)}
              onRemoveAssignment={handleRemoveAssignment}
              onPublish={handlePublish}
              onRollback={handleRollback}
              onRefreshExecs={() => {
                setLoadingExecs(true);
                loadExecutions(currentAgent.id).then(() => {
                  setLoadingExecs(false);
                });
              }}
              publishing={publishing}
              skillReviews={skillReviews}
              agentlets={agentlets}
              loadingAgentlets={loadingAgentlets}
              onRefreshAgentlets={loadAgentletsList}
              onPingAgentlet={handlePingAgentlet}
              pingingAgentlet={pingingAgentlet}
              agentletPage={agentletPage}
              onAgentletPageChange={setAgentletPage}
              agentletsPerPage={AGENTLETS_PER_PAGE}
              onAddSkill={() => setShowNewSkill(true)}
              onImportFromConnector={openImportConnector}
              onEditSkill={openEditSkill}
              onDeleteSkill={(skill) => setShowDeleteConfirm(skill)}
              onUnpublishSkill={handleUnpublishSkill}
              onAdvanceSkill={handleAdvanceSkill}
              onRegressSkill={handleRegressSkill}
              onPushSkill={handlePushSkill}
              pubPage={pubPage}
              pubTotalPages={pubTotalPages}
              onPubPageChange={(newPage) => {
                setPubPage(newPage);
                loadPublications(currentAgent.id, newPage, 10).then((pageData) => {
                  if (pageData && pageData.totalPages) setPubTotalPages(pageData.totalPages);
                });
              }}
            />
          )}
        </>
      )}

      {/* ========== MODALS ========== */}

      {/* Add Skill Modal */}
      <Modal isOpen={showNewSkill} title="Add New Skill" onClose={() => setShowNewSkill(false)} wide
        actions={<>
          <button onClick={() => setShowNewSkill(false)} style={btnBase}>Cancel</button>
          <button onClick={handleAddSkill} style={btnPrimary}>Create</button>
        </>}
      >
        <SkillFormWithImport skillForm={skillForm} setSkillForm={setSkillForm} connectors={availableConnectors} agentId={currentAgent?.id} />
      </Modal>

      {/* Edit Skill Modal */}
      <Modal isOpen={showEditSkill} title={`Edit Skill: ${editingSkill?.name || ''}`}
        onClose={() => { setShowEditSkill(false); setEditingSkill(null); }}
        wide
        actions={<>
          <button onClick={() => { setShowEditSkill(false); setEditingSkill(null); }} style={btnBase}>Cancel</button>
          <button onClick={handleEditSkill} style={btnPrimary}>Save Changes</button>
        </>}
      >
        <SkillFormWithImport skillForm={skillForm} setSkillForm={setSkillForm} connectors={availableConnectors} agentId={currentAgent?.id} />
      </Modal>

      {/* Delete Skill Confirmation */}
      <Modal isOpen={!!showDeleteConfirm} title="Confirm Delete" onClose={() => setShowDeleteConfirm(null)}
        actions={<>
          <button onClick={() => setShowDeleteConfirm(null)} style={btnBase}>Cancel</button>
          <button
            onClick={() => handleDeleteSkill(showDeleteConfirm)}
            disabled={showDeleteConfirm?.skillPhase && showDeleteConfirm?.skillPhase !== 'build'}
            style={{
              ...btnDanger,
              ...(showDeleteConfirm?.skillPhase && showDeleteConfirm?.skillPhase !== 'build'
                ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
            }}
          >Delete</button>
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 14, color: '#52525b', margin: 0 }}>
            Are you sure you want to delete skill <strong>"{showDeleteConfirm?.name}"</strong>? This action cannot be undone.
          </p>
          {/* Warning: sole workflow skill on published agent */}
          {showDeleteConfirm?.skillType === 'workflow_skill'
            && currentAgent?.publishVersion > 0
            && currentAgent?.skills?.filter(s => s.skillType === 'workflow_skill').length <= 1 && (
            <div style={{
              padding: '10px 14px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              fontSize: 12,
              color: '#991b1b',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
              <span>
                <strong>This is the only workflow skill</strong> on a published agent.
                Deleting it will leave the agent without any executable workflow capability.
                The backend will block this deletion — add another workflow skill first or unpublish the agent.
              </span>
            </div>
          )}
          {/* Warning: non-Build phase skill cannot be deleted */}
          {showDeleteConfirm?.skillPhase && showDeleteConfirm?.skillPhase !== 'build' && (
            <div style={{
              padding: '10px 14px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              fontSize: 12,
              color: '#991b1b',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}>
              <Lock size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong>This skill is in {showDeleteConfirm.skillPhase.toUpperCase()} phase and is locked.</strong>{' '}
                You must regress it to Build phase before it can be deleted. This ensures no orphan
                definitions are left on published agentlets.
              </span>
            </div>
          )}
          {/* Warning: previously-published skill being deleted */}
          {showDeleteConfirm?.wasPublished && showDeleteConfirm?.skillPhase === 'build' && (
            <div style={{
              padding: '8px 14px',
              background: '#fefce8',
              border: '1px solid #fde68a',
              borderRadius: 6,
              fontSize: 12,
              color: '#854d0e',
            }}>
              This skill was previously published. Deleting it will mark the agent as
              needing re-publish to remove the stale definition from agentlets.
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Published-asset deletion lockdown error modal (2026-05-04) ───
          Surfaces the structured 409 from the backend
          (PublishedAssetDeletionException) when a user tries to delete a
          skill / workflow / framework binding that has been published.
          The error body has shape:
            { error, code: "published_asset_deletion",
              asset: {kind, id, name},
              linkedPublishedSkills: [...],
              remediation }
          We render the asset-specific message (skill / workflow / binding)
          and surface a quick-action: for SKILL kind we offer one-click
          Unpublish (handleUnpublishSkill) so the user doesn't have to
          dismiss this dialog and find the button elsewhere. */}
      {publishedAssetError && (
        <Modal
          isOpen={!!publishedAssetError}
          title={(() => {
            const k = publishedAssetError.asset?.kind;
            if (k === 'skill') return 'Cannot delete published skill';
            if (k === 'workflow') return 'Cannot delete workflow with published skill';
            if (k === 'framework_binding') return 'Cannot archive binding with published skill';
            return 'Cannot delete published asset';
          })()}
          onClose={() => setPublishedAssetError(null)}
          actions={<>
            <button onClick={() => setPublishedAssetError(null)} style={btnBase}>
              Close
            </button>
            {publishedAssetError.asset?.kind === 'skill' && (
              <button
                onClick={() => {
                  // Reconstruct a minimal skill object for the handler.
                  // wasPublished is true by definition (that's why we're
                  // here); skill.id comes straight from the asset payload.
                  const skill = {
                    id: publishedAssetError.asset.id,
                    name: publishedAssetError.asset.name,
                    wasPublished: true,
                  };
                  handleUnpublishSkill(skill);
                }}
                style={{
                  ...btnBase,
                  background: '#0d9488',
                  color: '#fff',
                  border: '1px solid #0d9488',
                }}
              >
                <Upload size={14} style={{ transform: 'rotate(180deg)' }} /> Unpublish now
              </button>
            )}
          </>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 14, color: '#3f3f46', margin: 0, lineHeight: 1.55 }}>
              {publishedAssetError.error}
            </p>
            {publishedAssetError.linkedPublishedSkills?.length > 0 && (
              <div style={{
                padding: '10px 12px',
                background: '#e3f4f1',
                border: '1px solid #fecaca',
                borderRadius: 6,
                fontSize: 12,
                color: '#991b1b',
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  Published skill{publishedAssetError.linkedPublishedSkills.length > 1 ? 's' : ''} blocking this action:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {publishedAssetError.linkedPublishedSkills.map((s) => (
                    <li key={s.skillId} style={{ marginBottom: 2 }}>
                      <strong>{s.name}</strong>
                      {' '}— phase: {s.phase}
                      {s.wasPublished ? ', wasPublished' : ''}
                      {s.pushed ? ', pushed' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {publishedAssetError.remediation && (
              <div style={{
                padding: '10px 12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 6,
                fontSize: 12,
                color: '#166534',
                lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>How to proceed</div>
                {publishedAssetError.remediation}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Import from Connector Modal */}
      <Modal
        isOpen={showImportConnector}
        title={importStep === 'pick' ? 'Import from Connector' : 'Select Items to Import'}
        onClose={() => setShowImportConnector(false)}
        wide
        actions={<>
          {importStep === 'select' && (
            <button onClick={() => setImportStep('pick')} style={btnBase}>← Back</button>
          )}
          <button onClick={() => setShowImportConnector(false)} style={btnBase}>Cancel</button>
          {importStep === 'select' && (
            <button
              onClick={handleImportFromConnector}
              disabled={importingFromConnector || (importSelections.skills.length === 0 && importSelections.guardrails.length === 0 && importSelections.evals.length === 0)}
              style={btnPrimary}
            >
              {importingFromConnector ? 'Importing...' : 'Import Selected'}
            </button>
          )}
        </>}
      >
        {importStep === 'pick' && (
          <div>
            <p style={{ fontSize: 13, color: '#71717a', margin: '0 0 16px 0' }}>
              Choose an installed connector that has a marketplace template to import skills, guardrails, and evals from.
            </p>
            {availableConnectors.length === 0 ? (
              <div style={{ fontSize: 13, color: '#a1a1aa', padding: '24px 0', textAlign: 'center' }}>
                {connectors.length === 0
                  ? 'No connectors installed. Create or install connectors first.'
                  : 'No connectors assigned to this agent. Assign connectors in Team Management first.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {availableConnectors.map((c) => {
                  const mc = findMarketplaceForInstalled(marketplaceConnectors, c);
                  const hasTemplate = !!mc;
                  return (
                    <button
                      key={c.id}
                      onClick={() => hasTemplate && pickImportConnector(c.id)}
                      disabled={!hasTemplate}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                        borderRadius: 8, border: `1px solid ${hasTemplate ? '#e4e4e7' : '#f4f4f5'}`,
                        background: hasTemplate ? '#ffffff' : '#fafafa',
                        cursor: hasTemplate ? 'pointer' : 'not-allowed', textAlign: 'left',
                        opacity: hasTemplate ? 1 : 0.5, width: '100%',
                      }}
                    >
                      <span style={{ fontSize: 22 }}>{mc?.icon || '🔌'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: '#71717a' }}>
                          {hasTemplate
                            ? `${mc.skills.length} skills · ${mc.guardrails.length} guardrails · ${mc.evals.length} evals`
                            : 'No marketplace template available'}
                        </div>
                      </div>
                      {hasTemplate && <ChevronRight size={16} color="#a1a1aa" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {importStep === 'select' && (() => {
          const mc = getMarketplaceForConnector(importConnectorId);
          if (!mc) return <div style={{ color: '#a1a1aa' }}>Connector template not found.</div>;

          const SectionHeader = ({ label, category, allIds }) => {
            const allSelected = allIds.length > 0 && allIds.every((id) => importSelections[category].includes(id));
            const someSelected = allIds.some((id) => importSelections[category].includes(id));
            return (
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 0', borderBottom: '1px solid #f4f4f5', marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>
                  {label} ({importSelections[category].length}/{allIds.length})
                </div>
                <button
                  onClick={() => toggleAllImportCategory(category, allIds)}
                  style={{ ...btnBase, fontSize: 11, padding: '3px 8px' }}
                >
                  {allSelected ? <><CheckSquare size={12} /> Unselect All</> : <><Square size={12} /> Select All</>}
                </button>
              </div>
            );
          };

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: '#f9fafb', borderRadius: 8, border: '1px solid #f0f0f0',
              }}>
                <span style={{ fontSize: 20 }}>{mc.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{mc.name}</div>
                  <div style={{ fontSize: 11, color: '#71717a' }}>{mc.vendor} · v{mc.version}</div>
                </div>
              </div>

              {/* Skills */}
              <div>
                <SectionHeader label="Skills" category="skills" allIds={mc.skills.map((s) => s.id)} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {mc.skills.map((s) => {
                    const selected = importSelections.skills.includes(s.id);
                    return (
                      <label key={s.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
                        borderRadius: 6, cursor: 'pointer',
                        background: selected ? '#f5f5f4' : '#fafaf9',
                        border: `1px solid ${selected ? '#d6d3d1' : '#f4f4f5'}`,
                      }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleImportItem('skills', s.id)}
                          style={{ marginTop: 2 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: '#18181b' }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
                            {s.method} {s.endpoint}
                          </div>
                          <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 2 }}>{s.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Guardrails */}
              <div>
                <SectionHeader label="Guardrails" category="guardrails" allIds={mc.guardrails.map((g) => g.id)} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {mc.guardrails.map((g) => {
                    const selected = importSelections.guardrails.includes(g.id);
                    const sevColor = g.severity === 'critical' ? '#ef4444'
                      : g.severity === 'high' ? '#d97706' : '#78716c';
                    return (
                      <label key={g.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                        borderRadius: 6, cursor: 'pointer',
                        background: selected ? '#ede9fe' : '#fafaf9',
                        border: `1px solid ${selected ? '#ddd6fe' : '#f4f4f5'}`,
                      }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleImportItem('guardrails', g.id)}
                        />
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', background: sevColor, flexShrink: 0,
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: '#18181b' }}>{g.rule}</div>
                          <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 2 }}>
                            Severity: {g.severity}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Evals */}
              <div>
                <SectionHeader label="Evals" category="evals" allIds={mc.evals.map((e) => e.name)} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {mc.evals.map((e) => {
                    const selected = importSelections.evals.includes(e.name);
                    const typeColor = { functional: '#78716c', negative: '#ef4444', guardrail: '#8b5cf6', cost: '#d97706', security: '#0d9488' }[e.type] || '#71717a';
                    return (
                      <label key={e.name} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                        borderRadius: 6, cursor: 'pointer',
                        background: selected ? '#f5f5f4' : '#fafaf9',
                        border: `1px solid ${selected ? '#d6d3d1' : '#f4f4f5'}`,
                      }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleImportItem('evals', e.name)}
                        />
                        <span style={{
                          padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                          background: `${typeColor}15`, color: typeColor, border: `1px solid ${typeColor}30`,
                        }}>{e.type}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: '#18181b' }}>{e.name}</div>
                          <div style={{ fontSize: 11, color: '#71717a', marginTop: 1 }}>{e.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Test Run Side Panel */}
      {showTestRunPanel && currentAgent && agentPhase === 'act' && (
        <TestRunSidePanel
          agent={currentAgent}
          onClose={() => setShowTestRunPanel(false)}
          onTestPassed={() => {
            // Refresh agent data so lastTestRunPassed is up to date
            loadAgents({ orgId, tenantId });
          }}
        />
      )}

      {/* ── Auto Create flow (v1: locked UX shape, 501 backend stub) ────
          Two-step modal sequence:
            1. AutoCreateSourceModal — pick the source kind
            2. AutoCreateUploadModal — upload / select source, then submit
          On submit, the backend returns 501 with a "coming 1.1" body which
          the upload modal renders in-place rather than as an error toast. */}
      {showAutoCreateSource && !autoCreateSourceKind && !autoCreateRunId && (
        <AutoCreateSourceModal
          onClose={() => setShowAutoCreateSource(false)}
          onPick={(kind) => setAutoCreateSourceKind(kind)}
        />
      )}
      {showAutoCreateSource && autoCreateSourceKind && !autoCreateRunId && (
        <AutoCreateUploadModal
          sourceKind={autoCreateSourceKind}
          onBack={() => setAutoCreateSourceKind(null)}
          onClose={() => {
            setShowAutoCreateSource(false);
            setAutoCreateSourceKind(null);
          }}
          onRunStarted={(runId) => {
            // Pipeline started — close the source / upload pair and
            // hand off to the live wizard. The wizard owns the SSE
            // subscription and the gap / review interaction screens.
            setShowAutoCreateSource(false);
            setAutoCreateSourceKind(null);
            setAutoCreateRunId(runId);
          }}
        />
      )}
      {autoCreateRunId && (
        <AutoCreateWizard
          runId={autoCreateRunId}
          onClose={() => {
            setAutoCreateRunId(null);
            // Refresh the agent list so a freshly-committed agent shows up.
            // The platform context auto-refreshes; no explicit reload needed.
          }}
        />
      )}

      {/* New Agent Modal */}
      <Modal isOpen={showNewAgent} title="Create New Agent" onClose={() => setShowNewAgent(false)}
        actions={<>
          <button onClick={() => setShowNewAgent(false)} style={btnBase}>Cancel</button>
          <button onClick={handleCreateAgent} style={btnPrimary}>Create Agent</button>
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {renderField('Agent Name', agentForm.name, (v) => setAgentForm({ ...agentForm, name: v }))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={agentForm.type} onChange={(e) => setAgentForm({ ...agentForm, type: e.target.value })} style={inputStyle}>
                <option value="cognitive">Cognitive</option>
                <option value="reactive">Reactive</option>
                <option value="autonomous">Autonomous</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
          {renderField('Icon URL (optional)', agentForm.icon, (v) => setAgentForm({ ...agentForm, icon: v }))}
          {renderField('Gateway URL (optional)', agentForm.gatewayUrl, (v) => setAgentForm({ ...agentForm, gatewayUrl: v }))}
          <div style={{ fontSize: 11, color: '#a1a1aa', lineHeight: 1.5 }}>
            Agents always start in the <strong>Build</strong> phase. Advance through gated phases to publish.
          </div>
        </div>
      </Modal>

      {/* v5.4.0 — Assignment modal removed; all assignment CRUD now lives in Team → Assignments */}

      {/* Review Skill Modal (Decide Phase) */}
      <Modal isOpen={!!showReviewModal} title={`Review Skill: ${showReviewModal?.name || ''}`} wide
        onClose={() => { setShowReviewModal(null); setReviewForm({ decision: '', justification: '' }); }}
        actions={<>
          <button onClick={() => { setShowReviewModal(null); setReviewForm({ decision: '', justification: '' }); }} style={btnBase}>Cancel</button>
          <button onClick={() => {
            if (!reviewForm.decision) {
              addToast('Select a decision', 'warn');
              return;
            }
            handleReviewSkill(showReviewModal.id, reviewForm.decision, reviewForm.justification);
          }} style={btnPrimary}>Confirm Review</button>
        </>}
      >
        {showReviewModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#0c4a6e' }}>
                <strong>Eval Score:</strong> {showReviewModal.evalScore != null ? (showReviewModal.evalScore * 100).toFixed(0) + '%' : 'N/A'}
              </div>
              <div style={{ fontSize: 12, color: '#0c4a6e', marginTop: 4 }}>
                <strong>Risk Level:</strong> {showReviewModal.riskLevel}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Autonomy Decision</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" name="decision" value="autonomous" checked={reviewForm.decision === 'autonomous'}
                    onChange={(e) => setReviewForm({ ...reviewForm, decision: e.target.value })}
                  />
                  <ShieldCheck size={14} style={{ color: '#0d9488' }} />
                  <span>Autonomous (requires score ≥ 90%)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" name="decision" value="human_approval" checked={reviewForm.decision === 'human_approval'}
                    onChange={(e) => setReviewForm({ ...reviewForm, decision: e.target.value })}
                  />
                  <Eye size={14} style={{ color: '#0c4a6e' }} />
                  <span>Human Approval</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" name="decision" value="blocked" checked={reviewForm.decision === 'blocked'}
                    onChange={(e) => setReviewForm({ ...reviewForm, decision: e.target.value })}
                  />
                  <ShieldOff size={14} style={{ color: '#ef4444' }} />
                  <span>Block Skill</span>
                </label>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Justification</label>
              <textarea
                value={reviewForm.justification}
                onChange={(e) => setReviewForm({ ...reviewForm, justification: e.target.value })}
                style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit' }}
                placeholder="Explain the reasoning for this decision..."
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Agent Confirmation */}
      <Modal isOpen={!!showDeleteAgentConfirm} title="Delete Agent" onClose={() => setShowDeleteAgentConfirm(null)}
        actions={<>
          <button onClick={() => setShowDeleteAgentConfirm(null)} style={btnBase}>Cancel</button>
          <button onClick={handleDeleteAgent} style={{ ...btnDanger, fontWeight: 600 }}>
            <Trash2 size={14} /> Permanently Delete
          </button>
        </>}
      >
        {showDeleteAgentConfirm && (
          <div style={{ fontSize: 14, color: '#52525b' }}>
            <div style={{
              padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, marginBottom: 16,
            }}>
              <strong style={{ color: '#991b1b' }}>Warning:</strong> This action is permanent and cannot be undone.
            </div>
            <p style={{ margin: '0 0 12px 0' }}>
              You are about to delete <strong>{showDeleteAgentConfirm.name}</strong>. This will:
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.8 }}>
              <li>Remove all skills, evals, and configurations</li>
              <li>Delete all user/group assignments</li>
              <li>Unregister from all connected agentlet gateways</li>
              <li>Destroy any active deployments and containers</li>
              <li>Remove all publication and execution history</li>
            </ul>
            {showDeleteAgentConfirm.published && (
              <div style={{
                padding: '10px 14px', background: '#e3f4f1', border: '1px solid #a5d8d0',
                borderRadius: 8, marginTop: 16, fontSize: 12, color: '#92400e',
              }}>
                <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                This agent is currently published. Deleting it will terminate the live agentlet.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Publish Validation Modal — Eval-style detailed checks */}
      <Modal isOpen={showPublishValidation} title="Publish New Version" onClose={() => setShowPublishValidation(false)} wide
        actions={<>
          <button onClick={() => setShowPublishValidation(false)} style={btnBase}>Cancel</button>
          <button onClick={handlePublishSelective}
            disabled={publishing || validatingPublish || validationChecks.filter(c => c.severity === 'error' && !c.passed).length > 0}
            style={{
              ...btnSuccess, fontWeight: 600,
              opacity: (publishing || validatingPublish || validationChecks.filter(c => c.severity === 'error' && !c.passed).length > 0) ? 0.5 : 1,
              cursor: (publishing || validatingPublish || validationChecks.filter(c => c.severity === 'error' && !c.passed).length > 0) ? 'not-allowed' : 'pointer',
            }}>
            <Upload size={14} /> {publishing ? 'Publishing...' : `Deploy to ${selectedAgentlets.length} Agentlet(s)`}
          </button>
        </>}
      >
        <div style={{ fontSize: 14, color: '#52525b' }}>
          {validatingPublish ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#a1a1aa' }}>
              <RotateCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <div style={{ marginTop: 12, fontSize: 14, fontWeight: 500 }}>Running pre-publish validations...</div>
              <div style={{ marginTop: 4, fontSize: 12 }}>Checking assignments, skills, evals, and agentlet connectivity</div>
            </div>
          ) : (
            <>
              {/* Summary bar */}
              {validationChecks.length > 0 && (() => {
                const passed = validationChecks.filter(c => c.passed).length;
                const errors = validationChecks.filter(c => !c.passed && c.severity === 'error').length;
                const warnings = validationChecks.filter(c => !c.passed && c.severity === 'warning').length;
                const total = validationChecks.length;
                const allPassed = errors === 0 && warnings === 0;
                return (
                  <div style={{
                    padding: '12px 16px', borderRadius: 10, marginBottom: 16,
                    background: allPassed ? '#f5f5f4' : errors > 0 ? '#fef2f2' : '#e3f4f1',
                    border: `1px solid ${allPassed ? '#d6d3d1' : errors > 0 ? '#fecaca' : '#a5d8d0'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {allPassed ? <CheckCircle size={18} style={{ color: '#57534e' }} />
                        : errors > 0 ? <XCircle size={18} style={{ color: '#ef4444' }} />
                        : <AlertTriangle size={18} style={{ color: '#d97706' }} />}
                      <span style={{ fontWeight: 600, fontSize: 14, color: allPassed ? '#065f46' : errors > 0 ? '#991b1b' : '#92400e' }}>
                        {allPassed ? 'All checks passed — ready to publish'
                          : errors > 0 ? `${errors} check(s) failed — resolve before publishing`
                          : `${warnings} warning(s) — publishing is possible but review recommended`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, fontWeight: 600 }}>
                      <span style={{ color: '#57534e' }}>{passed} passed</span>
                      {errors > 0 && <span style={{ color: '#ef4444' }}>{errors} failed</span>}
                      {warnings > 0 && <span style={{ color: '#d97706' }}>{warnings} warn</span>}
                    </div>
                  </div>
                );
              })()}

              {/* Detailed checks */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Validation Checks ({validationChecks.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {validationChecks.map((ck, i) => {
                    const severityColors = {
                      ok: { bg: '#f5f5f4', border: '#d6d3d1', icon: '#57534e', title: '#065f46', text: '#047857' },
                      warning: { bg: '#e3f4f1', border: '#a5d8d0', icon: '#d97706', title: '#92400e', text: '#a16207' },
                      error: { bg: '#fef2f2', border: '#fecaca', icon: '#ef4444', title: '#991b1b', text: '#dc2626' },
                    };
                    const colors = severityColors[ck.passed ? 'ok' : (ck.severity || 'error')];
                    const fixActions = {
                      navigate_assignments: { label: 'Go to Assignments', action: () => { setShowPublishValidation(false); } },
                      navigate_decide: { label: 'Go to Decide Phase', action: () => { setShowPublishValidation(false); setPhase('decide'); } },
                      navigate_build: { label: 'Go to Build Phase', action: () => { setShowPublishValidation(false); setPhase('build'); } },
                      navigate_agentlets: { label: 'View Setup Guide', action: () => {} },
                      activate_agent: { label: 'Activate Agent', action: async () => {
                        const { updateAgent: updateAgentApi } = await import('../services/api');
                        await updateAgentApi(currentAgent.id, { status: 'active' });
                        handlePublish();
                      }},
                      open_test_run: { label: 'Open Test Run', action: () => {
                        setShowPublishValidation(false);
                        setShowTestRunPanel(true);
                      }},
                    };
                    const fix = ck.fixAction ? fixActions[ck.fixAction] : null;

                    return (
                      <div key={i} style={{
                        padding: '12px 16px', borderRadius: 8,
                        background: colors.bg, border: `1px solid ${colors.border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          {ck.passed ? (
                            <CheckCircle size={16} style={{ color: colors.icon, flexShrink: 0, marginTop: 2 }} />
                          ) : ck.severity === 'warning' ? (
                            <AlertTriangle size={16} style={{ color: colors.icon, flexShrink: 0, marginTop: 2 }} />
                          ) : (
                            <XCircle size={16} style={{ color: colors.icon, flexShrink: 0, marginTop: 2 }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: colors.title }}>
                                {ck.check.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                              </div>
                              {!ck.passed && ck.severity && (
                                <span style={{
                                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                                  padding: '2px 6px', borderRadius: 4,
                                  background: ck.severity === 'error' ? '#fee2e2' : '#fef3c7',
                                  color: ck.severity === 'error' ? '#dc2626' : '#d97706',
                                }}>{ck.severity}</span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: colors.text, marginTop: 2 }}>
                              {ck.message}
                            </div>
                            {!ck.passed && ck.recommendation && (
                              <div style={{
                                fontSize: 11, color: '#52525b', marginTop: 6, padding: '6px 10px',
                                background: 'rgba(255,255,255,0.7)', borderRadius: 6,
                                borderLeft: `3px solid ${colors.icon}`, lineHeight: 1.5,
                              }}>
                                <strong>Recommendation:</strong> {ck.recommendation}
                              </div>
                            )}
                            {!ck.passed && fix && (
                              <button onClick={fix.action} style={{
                                marginTop: 8, fontSize: 11, fontWeight: 600, padding: '4px 10px',
                                borderRadius: 5, border: `1px solid ${colors.border}`, background: '#fff',
                                color: colors.title, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}>
                                <ArrowRight size={11} /> {fix.label}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Agentlet Selector — shown when agentlets exist (even disconnected) */}
              {agentlets.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Target Agentlets ({agentlets.length})
                  </div>
                  <div style={{ fontSize: 12, color: '#71717a', marginBottom: 10 }}>
                    Select which agentlet containers to deploy this version to. Disconnected agentlets will receive the update when they reconnect.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {agentlets.map((ag) => {
                      const isSelected = selectedAgentlets.includes(ag.id);
                      const isDisconnected = ag.status === 'disconnected';
                      const isStale = ag.status === 'stale';
                      return (
                        <div key={ag.id} onClick={() => {
                          setSelectedAgentlets((prev) =>
                            isSelected ? prev.filter((id) => id !== ag.id) : [...prev, ag.id]
                          );
                        }} style={{
                          padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                          background: isSelected ? '#e3f4f1' : isDisconnected ? '#fafafa' : '#fafafa',
                          border: `1px solid ${isSelected ? '#0d9488' : isDisconnected ? '#fecaca' : '#e4e4e7'}`,
                          display: 'flex', alignItems: 'center', gap: 10,
                          transition: 'all 0.15s ease',
                          opacity: isDisconnected ? 0.7 : 1,
                        }}>
                          {isSelected ? (
                            <CheckSquare size={16} style={{ color: '#0d9488' }} />
                          ) : (
                            <Square size={16} style={{ color: isDisconnected ? '#d4d4d8' : '#a1a1aa' }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: isDisconnected ? '#71717a' : '#18181b' }}>
                              {ag.displayName || ag.agentletId}
                            </div>
                            <div style={{ fontSize: 11, color: '#71717a' }}>
                              {ag.gatewayUrl} • {ag.skillCount || 0} skill(s) • v{ag.version || '?'}
                              {isDisconnected && ' • Will sync on reconnect'}
                            </div>
                          </div>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                            background: ag.status === 'connected' ? '#dcfce7' : isStale ? '#fef3c7' : '#fef2f2',
                            color: ag.status === 'connected' ? '#16a34a' : isStale ? '#d97706' : '#ef4444',
                          }}>{ag.status}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No agentlets at all message */}
              {agentlets.length === 0 && !validatingPublish && (
                <div style={{
                  padding: '16px', borderRadius: 8, marginBottom: 12,
                  background: '#fafaf9', border: '1px solid #e7e5e4', textAlign: 'center',
                }}>
                  <Server size={20} style={{ color: '#a8a29e', marginBottom: 6 }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#57534e', marginBottom: 4 }}>No agentlets registered</div>
                  <div style={{ fontSize: 11, color: '#a8a29e' }}>
                    Start a standalone agentlet container to deploy to.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* ============ LLM PICKER MODAL — Redesigned ============ */}
      <Modal isOpen={!!showLlmPicker} title="Generate Eval Suite" onClose={() => setShowLlmPicker(null)}
        wide
        actions={<>
          <button onClick={() => setShowLlmPicker(null)} style={btnBase}>Cancel</button>
          <button
            onClick={() => handleGenerateEval(showLlmPicker, selectedLlmProvider, selectedLlmModel)}
            disabled={selectedLlmProvider !== '__platform__' && !selectedLlmModel}
            style={{
              ...btnSuccess, fontWeight: 600,
              opacity: (selectedLlmProvider !== '__platform__' && !selectedLlmModel) ? 0.5 : 1,
              cursor: (selectedLlmProvider !== '__platform__' && !selectedLlmModel) ? 'not-allowed' : 'pointer',
            }}>
            <Beaker size={14} /> Generate Test Cases
          </button>
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Info banner */}
          <div style={{ padding: '12px 14px', borderRadius: 8, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', marginBottom: 4 }}>
              Context-Sensitive Eval Generation
            </div>
            <div style={{ fontSize: 11, color: '#0284c7' }}>
              Select an LLM provider to analyze your skill's metadata, guardrails, and connector
              configuration to generate tailored test cases. The skill details will be passed to
              the selected LLM to produce context-aware eval scenarios.
            </div>
          </div>

          {/* Provider selector */}
          <div>
            <label style={labelStyle}>LLM Provider</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {llmProviders.map((p) => {
                const isSelected = selectedLlmProvider === p.id;
                return (
                  <label key={p.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                    borderRadius: 8, cursor: 'pointer',
                    background: isSelected ? '#f5f5f4' : '#fafaf9',
                    border: `2px solid ${isSelected ? '#0d9488' : '#e4e4e7'}`,
                    transition: 'all 0.15s',
                  }}>
                    <input type="radio" name="llmProvider" checked={isSelected}
                      onChange={() => { setSelectedLlmProvider(p.id); setSelectedLlmModel(p.models?.[0]?.id || null); }}
                      style={{ accentColor: '#0d9488' }} />
                    <span style={{ fontSize: 18 }}>{p.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#71717a' }}>
                        {p.providerType === 'platform' ? 'agentlets-eval-v1 · built-in' : `${p.models?.length || 0} models available · connected`}
                      </div>
                    </div>
                    {p.providerType === 'platform' && (
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 700 }}>
                        BUILT-IN
                      </span>
                    )}
                    {p.providerType !== 'platform' && (
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>
                        CONNECTED
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            {/* Hint when no LLM connectors are connected */}
            {llmProviders.length <= 1 && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: '#e3f4f1', border: '1px solid #a5d8d0', fontSize: 11, color: '#92400e' }}>
                <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                No LLM connectors connected. Add an Anthropic, OpenAI, Gemini, or Azure OpenAI connector
                on the Connectors page to use external models for eval generation.
              </div>
            )}
          </div>

          {/* Model selector — only for non-platform providers */}
          {(() => {
            const provider = llmProviders.find((p) => p.id === selectedLlmProvider);
            if (!provider || provider.providerType === 'platform' || !provider.models?.length) return null;
            const tierColors = { recommended: '#0d9488', premium: '#0a766b', fast: '#78716c' };
            return (
              <div>
                <label style={labelStyle}>Model</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {provider.models.map((m) => {
                    const isSelected = selectedLlmModel === m.id;
                    return (
                      <label key={m.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        borderRadius: 6, cursor: 'pointer',
                        background: isSelected ? '#f5f5f4' : '#fafaf9',
                        border: `1.5px solid ${isSelected ? '#d6d3d1' : '#e4e4e7'}`,
                        transition: 'all 0.15s',
                      }}>
                        <input type="radio" name="llmModel" checked={isSelected}
                          onChange={() => setSelectedLlmModel(m.id)} style={{ accentColor: '#0d9488' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: '#18181b' }}>{m.name}</div>
                          <div style={{ fontSize: 10, color: '#71717a' }}>{m.id} · {m.context} context</div>
                        </div>
                        <span style={{
                          fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                          background: `${tierColors[m.tier] || '#71717a'}15`,
                          color: tierColors[m.tier] || '#71717a',
                          border: `1px solid ${tierColors[m.tier] || '#71717a'}30`,
                        }}>{m.tier}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* What will be generated */}
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#f9fafb', border: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#52525b', marginBottom: 4 }}>Generation will include:</div>
            <div style={{ fontSize: 11, color: '#71717a', lineHeight: 1.6 }}>
              • Functional tests (happy path + boundary conditions)<br />
              • Negative tests (invalid input + unauthorized access)<br />
              • Security tests (injection defense, if high/critical risk)<br />
              • Guardrail tests (enforcement validation, if guardrails configured)<br />
              • Performance tests (SLA threshold compliance)<br />
              • Category-specific tests (based on skill type)
            </div>
          </div>
        </div>
      </Modal>

      {/* ============ EVAL DETAIL MODAL — Redesigned (Read-Only + Run Once) ============ */}
      <Modal
        isOpen={!!showEvalDetails}
        title={showEvalDetails ? showEvalDetails.name : 'Eval Details'}
        onClose={() => { setShowEvalDetails(null); setConfirmDelEvalDetails(false); }}
        wide
        actions={<>
          {hasPermission('skill.delete') && showEvalDetails && (
            confirmDelEvalDetails ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11.5, color: '#78716c' }}>Delete this eval suite?</span>
                <button onClick={() => { handleDeleteEval(showEvalDetails.id); setConfirmDelEvalDetails(false); }} style={btnDanger}>Delete</button>
                <button onClick={() => setConfirmDelEvalDetails(false)} style={btnBase}>Cancel</button>
              </span>
            ) : (
              <button onClick={() => setConfirmDelEvalDetails(true)} style={btnDanger}><Trash2 size={12} /> Delete</button>
            )
          )}
          {hasPermission('eval.run') && showEvalDetails && showEvalDetails.status !== 'archived' && (
            <button onClick={() => handleRunEval(showEvalDetails.skillId, showEvalDetails.id)}
              disabled={runningEval === showEvalDetails?.id}
              style={showEvalDetails.status === 'completed' ? { ...btnBase, border: '1px solid #d6d3d1', background: '#f5f5f4', color: '#065f46' } : btnSuccess}>
              <Play size={12} /> {runningEval === showEvalDetails?.id ? 'Running...' : showEvalDetails.status === 'completed' ? 'Re-run Eval' : 'Run Eval'}
            </button>
          )}
          <button onClick={() => setShowEvalDetails(null)} style={btnBase}>Close</button>
        </>}
      >
        {showEvalDetails && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Score + Stats Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
              {[
                { label: 'Score', value: showEvalDetails.score != null ? `${(showEvalDetails.score * 100).toFixed(0)}%` : '—',
                  color: showEvalDetails.score >= 0.9 ? '#57534e' : showEvalDetails.score >= 0.7 ? '#d97706' : '#ef4444' },
                { label: 'Status', value: showEvalDetails.status },
                { label: 'Passed', value: String(showEvalDetails.passedTests || 0), color: '#57534e' },
                { label: 'Failed', value: String((showEvalDetails.totalTests || 0) - (showEvalDetails.passedTests || 0)), color: '#ef4444' },
                { label: 'Total', value: String(showEvalDetails.totalTests || 0) },
              ].map((s) => (
                <div key={s.label} style={{ textAlign: 'center', padding: '8px 6px', borderRadius: 8, background: '#f9fafb', border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 9, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color || '#18181b', marginTop: 2 }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* LLM Provider Info */}
            {showEvalDetails.llmProvider && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#71717a', padding: '6px 10px', borderRadius: 6, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <Zap size={12} color="#0284c7" />
                Generated by <strong style={{ color: '#0369a1' }}>{showEvalDetails.llmProvider}</strong>
                {showEvalDetails.llmModel && <span> ({showEvalDetails.llmModel})</span>}
              </div>
            )}

            {/* Run status notice */}
            {showEvalDetails.status === 'completed' && (
              <div style={{ padding: '8px 12px', borderRadius: 6, background: '#f5f5f4', border: '1px solid #d6d3d1', fontSize: 11, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                <ShieldCheck size={12} color="#16a34a" />
                Eval completed — review recommendations below, make fixes, then click <strong style={{ marginLeft: 2 }}>Re-run Eval</strong> to verify improvements. Run history tracks score progression.
              </div>
            )}

            {/* Phase Readiness */}
            {(() => {
              const score = showEvalDetails.score;
              const isReady = score != null && score >= 0.7 && showEvalDetails.status === 'completed';
              return (
                <div style={{
                  padding: '10px 14px', borderRadius: 8,
                  background: isReady ? '#e3f4f1' : score == null ? '#f9fafb' : '#fef2f2',
                  border: `1px solid ${isReady ? '#a5d8d0' : score == null ? '#f0f0f0' : '#fecaca'}`,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {isReady ? <CheckCircle size={16} color="#0d9488" /> : score == null ? <Clock size={16} color="#a1a1aa" /> : <AlertTriangle size={16} color="#ef4444" />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: isReady ? '#065f46' : score == null ? '#52525b' : '#991b1b' }}>
                      {isReady ? 'Ready for Phase Promotion' : score == null ? 'Awaiting First Run' : 'Not Ready — Improvements Needed'}
                    </div>
                    <div style={{ fontSize: 11, color: '#71717a' }}>
                      {!score ? 'Run the eval suite to execute all test cases and generate recommendations.'
                        : score < 0.7 ? `Score ${(score * 100).toFixed(0)}% is below the 70% minimum. Follow the recommendations below, then re-run.`
                        : score < 0.9 ? `Score ${(score * 100).toFixed(0)}% passes minimum (70%) but below 90% for autonomous operation. Re-run after fixes.`
                        : `Score ${(score * 100).toFixed(0)}% — meets autonomous threshold (≥90%).`}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Test Cases with inline fix actions for failures */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#18181b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Beaker size={14} /> Test Cases ({showEvalDetails.testCases?.length || 0})
              </div>
              {showEvalDetails.loadingDetail ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#a1a1aa', fontSize: 12 }}>Loading...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(showEvalDetails.testCases || []).map((tc, idx) => {
                    const statusIcon = tc.status === 'passed' ? <CheckCircle size={14} color="#57534e" />
                      : tc.status === 'failed' ? <XCircle size={14} color="#ef4444" />
                      : <Clock size={14} color="#a1a1aa" />;
                    const statusBg = tc.status === 'passed' ? '#e3f4f1' : tc.status === 'failed' ? '#fef2f2' : '#f9fafb';
                    const typeColors = { functional: '#78716c', negative: '#ef4444', security: '#dc2626', guardrail: '#8b5cf6', performance: '#d97706' };
                    const typeColor = typeColors[tc.testType] || '#71717a';
                    const sevColors = { critical: '#dc2626', high: '#d97706', medium: '#78716c', low: '#a1a1aa' };

                    // Derive quick-fix context from actual output
                    const actual = tc.actualOutput || {};
                    const evalField = actual.evaluatedField;
                    const suggestedFix = actual.suggestedFix;
                    const failReason = actual.reason;
                    const evalMethod = actual.evaluationMethod;

                    // Resolve the skill to show current config values
                    const skill = currentAgent?.skills?.find((s) => s.id === showEvalDetails.skillId);

                    // Build a quick-fix config map: which field is broken, current value, and what to set
                    const quickFix = tc.status === 'failed' && skill ? (() => {
                      const scenario = tc.input?.scenario || '';
                      switch (scenario) {
                        case 'boundary':
                          return { field: 'inferenceFallback', label: 'Inference Fallback', current: skill.inferenceFallback || '(none)',
                            options: ['default_response', 'queue', 'reject_gracefully'],
                            desc: 'How the skill handles edge-case inputs when primary inference fails' };
                        case 'invalid_input':
                        case 'injection':
                        case 'guardrail_violation':
                          return { field: 'guardrails', label: 'Guardrails', current: skill.guardrails ? JSON.stringify(skill.guardrails) : '(none)',
                            addGuardrail: scenario === 'injection' ? 'inputSanitization'
                              : scenario === 'invalid_input' ? 'inputValidation'
                              : 'policyEnforcement',
                            desc: scenario === 'injection' ? 'Add input sanitization to block SQL/XSS/prompt injection'
                              : scenario === 'invalid_input' ? 'Add input validation to reject malformed requests'
                              : 'Add guardrail policies to enforce safety boundaries' };
                        case 'unauthorized':
                        case 'privilege_escalation':
                          return { field: 'autonomy', label: 'Autonomy Level', current: skill.autonomy || '(none)',
                            options: ['HUMAN_CONTROLLED', 'SUPERVISED', 'ASSISTED'],
                            desc: 'Reduce autonomy to require authorization checks' };
                        case 'performance':
                          return { field: 'maxExecutionMs', label: 'SLA Threshold (ms)', current: skill.maxExecutionMs || '(not set)',
                            inputType: 'number', defaultValue: 5000,
                            desc: 'Set the maximum acceptable execution time' };
                        case 'happy_path':
                        case 'idempotency':
                          return { field: 'description', label: 'Skill Description', current: skill.description || '(empty)',
                            inputType: 'text',
                            desc: 'Add a meaningful description so the eval can validate behavior' };
                        default: return null;
                      }
                    })() : null;

                    // Handler for inline quick-fix apply
                    const handleQuickFix = async (fieldName, value) => {
                      if (!skill) return;
                      const payload = { [fieldName]: value };
                      const result = await updateSkillApi(currentAgent.id, skill.id, payload);
                      if (result?.ok) {
                        addToast(`Updated ${fieldName} — re-run the eval to verify`, 'success');
                        loadAgents({ orgId, tenantId });
                      } else {
                        addToast(`Failed to update: ${result?.error || 'unknown error'}`, 'error');
                      }
                    };

                    // Handler for adding a guardrail
                    const handleAddGuardrail = async (guardrailType) => {
                      if (!skill) return;
                      const existing = Array.isArray(skill.guardrails) ? skill.guardrails
                        : skill.guardrails && typeof skill.guardrails === 'object' ? Object.keys(skill.guardrails)
                        : [];
                      // Build new guardrails object with the missing policy
                      const newGuardrails = {};
                      existing.forEach(g => { newGuardrails[typeof g === 'string' ? g : g.type || g] = true; });
                      newGuardrails[guardrailType] = true;
                      const result = await updateSkillApi(currentAgent.id, skill.id, { guardrails: newGuardrails });
                      if (result?.ok) {
                        addToast(`Added "${guardrailType}" guardrail — re-run the eval to verify`, 'success');
                        loadAgents({ orgId, tenantId });
                      } else {
                        addToast(`Failed to update guardrails: ${result?.error || 'unknown'}`, 'error');
                      }
                    };

                    return (
                      <div key={tc.id || idx} style={{ padding: '10px 12px', borderRadius: 8, background: statusBg, border: `1px solid ${tc.status === 'passed' ? '#a5d8d0' : tc.status === 'failed' ? '#fecaca' : '#f0f0f0'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          {statusIcon}
                          <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: `${typeColor}15`, color: typeColor, border: `1px solid ${typeColor}30` }}>
                            {tc.testType || 'functional'}
                          </span>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sevColors[tc.severity] || '#a1a1aa' }} />
                          <span style={{ fontSize: 10, color: '#a1a1aa' }}>{tc.severity}</span>
                          <Badge status={tc.status}>{tc.status}</Badge>
                          {evalMethod && (
                            <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: evalMethod === 'llm' ? '#dbeafe' : '#f3f4f6', color: evalMethod === 'llm' ? '#1e40af' : '#6b7280' }}>
                              {evalMethod === 'llm' ? 'LLM eval' : 'config check'}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b', marginBottom: 4 }}>{tc.description || `Test #${idx + 1}`}</div>

                        {/* Failure reason — prominent for failed tests */}
                        {tc.status === 'failed' && failReason && (
                          <div style={{
                            padding: '6px 10px', borderRadius: 6, marginBottom: 6,
                            background: '#fee2e2', border: '1px solid #fecaca', fontSize: 11, color: '#991b1b',
                          }}>
                            <strong>Why it failed:</strong> {failReason}
                          </div>
                        )}

                        {/* Suggested fix — actionable */}
                        {tc.status === 'failed' && suggestedFix && (
                          <div style={{
                            padding: '6px 10px', borderRadius: 6, marginBottom: 6,
                            background: '#e3f4f1', border: '1px solid #a5d8d0', fontSize: 11, color: '#92400e',
                          }}>
                            <strong>Fix:</strong> {suggestedFix}
                          </div>
                        )}

                        {/* V169 / Wave 3 — Quick Fix is forbidden on
                            AutoLets (the Quick Fix path mutates skill
                            metadata, which RunbookManagedGuard 409s on
                            for is_runbook_managed=true agents). Replace
                            with an actionable hint pointing the user at
                            the new-version path. See GOLDEN_CEG_RULES.md
                            §1, §9.2. */}
                        {quickFix && currentAgent?.isRunbookManaged === true && (
                          <div style={{
                            padding: '8px 10px', borderRadius: 6, marginBottom: 6,
                            background: '#f5f5f4', border: '1px solid #d6d3d1',
                            fontSize: 11, color: '#44403c',
                          }}>
                            <strong style={{ color: '#292524' }}>This is an AutoLet — Quick Fix isn't available.</strong>{' '}
                            Skills are immutable once created. To change <code style={{ background: '#fff', border: '1px solid #d6d3d1', padding: '1px 4px', borderRadius: 3 }}>{quickFix.label}</code>,
                            {' '}{currentAgent.sourceWorkflowId ? 'publish a revised version of the source workflow' : 'upload a revised runbook'} to create v{((currentAgent.sourceWorkflowId ? currentAgent.sourceWorkflowVersion : currentAgent.runbookVersion) ?? 1) + 1}.
                          </div>
                        )}

                        {/* Quick-fix inline panel for failed tests */}
                        {quickFix && currentAgent?.isRunbookManaged !== true && (
                          <div style={{
                            padding: '8px 10px', borderRadius: 6, marginBottom: 6,
                            background: '#f5f5f4', border: '1px solid #d6d3d1',
                          }}>
                            <div style={{ fontSize: 10, color: '#0c4a6e', marginBottom: 4 }}>
                              <strong>Quick Fix:</strong> {quickFix.desc}
                            </div>
                            <div style={{ fontSize: 10, color: '#78716c', marginBottom: 6 }}>
                              Field: <code style={{ background: '#f0eaea', padding: '1px 4px', borderRadius: 3 }}>{quickFix.label}</code>
                              &nbsp;→&nbsp;Current: <code style={{ background: '#fee2e2', padding: '1px 4px', borderRadius: 3 }}>
                                {typeof quickFix.current === 'string' && quickFix.current.length > 50
                                  ? quickFix.current.slice(0, 50) + '...' : String(quickFix.current)}</code>
                            </div>

                            {/* Option buttons (for autonomy, fallback) */}
                            {quickFix.options && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {quickFix.options.map((opt) => (
                                  <button key={opt}
                                    onClick={() => handleQuickFix(quickFix.field, opt)}
                                    style={{
                                      padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                      border: '1px solid #a5d8d0', background: '#fff', color: '#0a766b',
                                      cursor: 'pointer', transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={(e) => { e.target.style.background = '#e3f4f1'; }}
                                    onMouseLeave={(e) => { e.target.style.background = '#fff'; }}
                                  >
                                    Set to "{opt.replace(/_/g, ' ')}"
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* Add guardrail button */}
                            {quickFix.addGuardrail && (
                              <button
                                onClick={() => handleAddGuardrail(quickFix.addGuardrail)}
                                style={{
                                  padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                  border: '1px solid #c084fc', background: '#fff', color: '#7c3aed',
                                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                                }}
                              >
                                <Plus size={10} /> Add "{quickFix.addGuardrail}" guardrail
                              </button>
                            )}

                            {/* Number input (for SLA) */}
                            {quickFix.inputType === 'number' && (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input type="number" defaultValue={quickFix.defaultValue}
                                  id={`qf-${tc.id}-${quickFix.field}`}
                                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #a5d8d0', fontSize: 11, width: 100 }}
                                />
                                <span style={{ fontSize: 10, color: '#78716c' }}>ms</span>
                                <button
                                  onClick={() => {
                                    const val = parseInt(document.getElementById(`qf-${tc.id}-${quickFix.field}`)?.value);
                                    if (val > 0) handleQuickFix(quickFix.field, val);
                                  }}
                                  style={{
                                    padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                    border: '1px solid #a5d8d0', background: '#e3f4f1', color: '#0a766b', cursor: 'pointer',
                                  }}
                                >
                                  Apply
                                </button>
                              </div>
                            )}

                            {/* Text input (for description) */}
                            {quickFix.inputType === 'text' && (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <input type="text" placeholder="Enter skill description..."
                                  id={`qf-${tc.id}-${quickFix.field}`}
                                  defaultValue={typeof quickFix.current === 'string' && !quickFix.current.startsWith('(') ? quickFix.current : ''}
                                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #a5d8d0', fontSize: 11, flex: 1 }}
                                />
                                <button
                                  onClick={() => {
                                    const val = document.getElementById(`qf-${tc.id}-${quickFix.field}`)?.value;
                                    if (val) handleQuickFix(quickFix.field, val);
                                  }}
                                  style={{
                                    padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                    border: '1px solid #a5d8d0', background: '#e3f4f1', color: '#0a766b', cursor: 'pointer',
                                  }}
                                >
                                  Save
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Collapsed input/expected/actual for passed tests, expanded for failures */}
                        {tc.status === 'failed' && tc.actualOutput && (
                          <details style={{ fontSize: 10, color: '#71717a', marginTop: 4 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 10, color: '#a1a1aa' }}>Show test I/O details</summary>
                            <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {tc.input && <div><strong>Input:</strong> <code style={{ fontSize: 9, background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{JSON.stringify(tc.input)}</code></div>}
                              {tc.expectedOutput && <div><strong>Expected:</strong> <code style={{ fontSize: 9, background: '#fff', padding: '1px 4px', borderRadius: 3 }}>{JSON.stringify(tc.expectedOutput)}</code></div>}
                              <div><strong>Actual:</strong> <code style={{ fontSize: 9, background: '#fee2e2', padding: '1px 4px', borderRadius: 3 }}>{JSON.stringify(tc.actualOutput)}</code></div>
                            </div>
                          </details>
                        )}
                        {tc.status === 'passed' && (
                          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                            {actual.reason || 'Test passed'}
                            {evalField && <span style={{ marginLeft: 6, color: '#a1a1aa' }}>({evalField})</span>}
                          </div>
                        )}
                        {tc.notes && <div style={{ fontSize: 10, color: '#71717a', fontStyle: 'italic', marginTop: 2 }}>Note: {tc.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Summary: Re-run prompt + open full skill editor link */}
            {(() => {
              const failedCount = (showEvalDetails.testCases || []).filter((tc) => tc.status === 'failed').length;
              if (failedCount === 0) return null;
              const skill = currentAgent?.skills?.find((s) => s.id === showEvalDetails.skillId);
              return (
                <div style={{ padding: '12px 14px', borderRadius: 8, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0c4a6e', marginBottom: 6 }}>
                    {failedCount} test{failedCount > 1 ? 's' : ''} failed — fix using the Quick Fix panels above, then re-run.
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleRunEval(showEvalDetails.skillId, showEvalDetails.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: '#0d9488', color: '#fff', border: 'none', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <Play size={12} /> Re-run Eval
                    </button>
                    {skill && (
                      <button
                        onClick={() => {
                          setShowEvalDetails(null);
                          openEditSkill(skill);
                        }}
                        style={{
                          padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: '#fff', color: '#0369a1', border: '1px solid #bae6fd', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Edit3 size={12} /> Open Full Skill Editor
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* LLM Recommendations (collapsed summary — detail is now on each test case) */}
            {(() => {
              const latestRun = (showEvalDetails.runs || []).find((r) => r.recommendations);
              const recs = latestRun?.recommendations;
              if (!recs || !(recs.items || []).length) return null;
              const priorityColors = { critical: '#dc2626', high: '#d97706', medium: '#78716c', info: '#a8a29e' };
              return (
                <details style={{ marginTop: 4 }}>
                  <summary style={{ fontSize: 13, fontWeight: 600, color: '#18181b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Zap size={14} /> AI Recommendations ({(recs.items || []).length})
                  </summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {(recs.items || []).map((rec, i) => (
                      <div key={i} style={{ padding: '8px 10px', borderRadius: 6, background: '#f9fafb', border: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, color: '#fff', background: priorityColors[rec.priority] || '#71717a' }}>{rec.priority}</span>
                          <span style={{ fontWeight: 600, fontSize: 11, color: '#18181b' }}>{rec.title}</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>{rec.detail}</div>
                      </div>
                    ))}
                  </div>
                </details>
              );
            })()}

            {/* Run History */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#18181b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <RotateCw size={14} /> Run History ({showEvalDetails.runs?.length || 0})
              </div>
              {(showEvalDetails.runs || []).length === 0 ? (
                <div style={{ fontSize: 12, color: '#a1a1aa', fontStyle: 'italic' }}>No runs yet. Click "Run Eval" to execute all test cases.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(showEvalDetails.runs || []).map((run, idx) => (
                    <div key={run.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                      borderRadius: 8, background: '#f9fafb', border: '1px solid #f0f0f0',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: run.score >= 0.9 ? '#dcfce7' : run.score >= 0.7 ? '#fef3c7' : '#fee2e2',
                        fontWeight: 700, fontSize: 12,
                        color: run.score >= 0.9 ? '#166534' : run.score >= 0.7 ? '#92400e' : '#991b1b',
                      }}>
                        {run.score != null ? `${(run.score * 100).toFixed(0)}` : '—'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b' }}>
                          Run #{(showEvalDetails.runs || []).length - idx}
                        </div>
                        <div style={{ fontSize: 10, color: '#71717a' }}>
                          {run.passedTests}/{run.totalTests} passed
                          {run.durationMs ? ` · ${run.durationMs}ms` : ''}
                          {run.startedAt ? ` · ${new Date(run.startedAt).toLocaleString()}` : ''}
                        </div>
                      </div>
                      <Badge status={run.status}>{run.status}</Badge>
                      {run.recommendations && (run.recommendations.items || []).length > 0 && (
                        <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                          {(run.recommendations.items || []).length} recs
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Meta info */}
            <div style={{ fontSize: 10, color: '#a1a1aa', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>Created: {showEvalDetails.createdAt ? new Date(showEvalDetails.createdAt).toLocaleString() : 'N/A'}</span>
              <span>Updated: {showEvalDetails.updatedAt ? new Date(showEvalDetails.updatedAt).toLocaleString() : 'N/A'}</span>
              <span>ID: {showEvalDetails.id}</span>
            </div>
          </div>
        )}
      </Modal>

      {/* Gate Result Modal */}
      <Modal isOpen={!!showGateResult} title="Phase Gate Result" onClose={() => setShowGateResult(null)}
        actions={<button onClick={() => setShowGateResult(null)} style={btnBase}>Close</button>}
      >
        {showGateResult && (
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
              padding: '10px 14px', borderRadius: 8,
              background: showGateResult.status === 'passed' ? '#e3f4f1' : '#fef2f2',
              border: `1px solid ${showGateResult.status === 'passed' ? '#a5d8d0' : '#fecaca'}`,
            }}>
              {showGateResult.status === 'passed'
                ? <CheckCircle size={18} color="#0d9488" />
                : <XCircle size={18} color="#ef4444" />}
              <span style={{ fontWeight: 600, color: showGateResult.status === 'passed' ? '#065f46' : '#991b1b' }}>
                {showGateResult.status === 'passed' ? 'Gate Passed' : 'Gate Failed'}
              </span>
              <span style={{ fontSize: 12, color: '#71717a', marginLeft: 'auto' }}>
                {PHASE_MAP[showGateResult.fromPhase]} → {PHASE_MAP[showGateResult.toPhase]}
              </span>
            </div>
            {renderGateChecks(showGateResult.checks)}
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

// PhaseProgressBar removed in UX overhaul — merged into unified Phase Navigation tabs above

// =========================================================================
// BEGIN v5.3.0 — LINKED WORKFLOWS SECTION (rollback: remove entire function)
// =========================================================================
function LinkedWorkflowsSection({ agent, agents, orgId, tenantId, canWrite, addToast }) {
  const [linkedWorkflows, setLinkedWorkflows] = React.useState([]);
  const [allWorkflows, setAllWorkflows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [expanded, setExpanded] = React.useState(true);
  // workflowId → highest skillPhase among workflow_skill rows on THIS agent
  // that point to this workflow. 'act' > 'decide' > 'build' > undefined.
  // Drives the disabled-state on the × unlink button (Rule A blocks
  // decide/act, Rule B cascades for build).
  const [skillPhaseByWorkflow, setSkillPhaseByWorkflow] = React.useState({});
  // {workflowId, workflowName, skillName, derivedPhase} or null — drives
  // the destructive-confirm modal for build-phase unlinks (Rule B).
  const [unlinkConfirm, setUnlinkConfirm] = React.useState(null);

  // Load workflows linked to this agent + derived skill phase per workflow.
  // Two parallel calls so we don't double the latency:
  //   1) getWorkflows({agentId})            — the cards
  //   2) getAgentSkills(agentId)            — the skill rows whose
  //                                            linkedResourceId === wf.id
  const loadLinked = React.useCallback(async () => {
    if (!agent?.id) return;
    setLoading(true);
    try {
      const { getWorkflows, getAgentSkills } = await import('../services/api');
      const [wfRes, skillRes] = await Promise.all([
        getWorkflows({ agentId: agent.id }),
        getAgentSkills(agent.id),
      ]);
      setLinkedWorkflows(wfRes?.ok ? (wfRes.data || []) : []);

      // Build the workflowId → phase map. A workflow CAN have multiple
      // workflow_skill rows on the same agent (rare, but possible during
      // re-publish races); collapse to the highest phase so the UI guards
      // the most restrictive case.
      const phaseRank = { act: 3, decide: 2, build: 1 };
      const map = {};
      const skills = skillRes?.ok ? (skillRes.data || []) : [];
      for (const s of skills) {
        if (s.skillType !== 'workflow_skill' || !s.linkedResourceId) continue;
        const cur = map[s.linkedResourceId];
        if (!cur || (phaseRank[s.skillPhase] || 0) > (phaseRank[cur.phase] || 0)) {
          map[s.linkedResourceId] = {
            phase: s.skillPhase || 'build',
            skillId: s.id,
            skillName: s.name,
          };
        }
      }
      setSkillPhaseByWorkflow(map);
    } catch {
      setLinkedWorkflows([]);
      setSkillPhaseByWorkflow({});
    }
    setLoading(false);
  }, [agent?.id]);

  // Load all workflows (for the link modal)
  const loadAll = React.useCallback(async () => {
    if (!orgId || !tenantId) return;
    try {
      const { getWorkflows } = await import('../services/api');
      const result = await getWorkflows({ orgId, tenantId });
      setAllWorkflows(result?.ok ? (result.data || []) : []);
    } catch { setAllWorkflows([]); }
  }, [orgId, tenantId]);

  React.useEffect(() => { loadLinked(); }, [loadLinked]);

  const handleLink = async (workflowId) => {
    try {
      const { updateWorkflow } = await import('../services/api');
      const result = await updateWorkflow(workflowId, { agentId: agent.id });
      if (result?.ok) {
        addToast('Workflow linked to agent', 'success');
        loadLinked();
      } else {
        addToast('Failed to link workflow', 'error');
      }
    } catch (err) { addToast('Error: ' + err.message, 'error'); }
  };

  // Unlink-click router. Branches based on (a) derived workflow_skill
  // phase and (b) the AGENT's lifecycle phase. The agent-level branch
  // exists because a user reasonably expects "don't let me casually
  // detach a workflow while the agent is published, even if THIS
  // particular workflow has no derived skill" — there might be
  // operational reasons the workflow is still relevant (test fixture
  // for the published canvas, scheduled trigger that touches the agent's
  // domain, etc). Confirm modal makes the intent explicit. The four
  // cases are mutually exclusive and ordered by severity:
  //
  //   1. derived skill in decide/act        → block (Rule A; backend 409)
  //   2. derived skill in build              → cascade-confirm (Rule B)
  //   3. agent in act, no derived skill     → "agent-published" confirm
  //   4. otherwise                            → plain unlink
  const handleUnlinkClick = (workflowId, workflowName) => {
    const derived = skillPhaseByWorkflow[workflowId];
    if (derived && (derived.phase === 'decide' || derived.phase === 'act')) {
      addToast(
        `Skill '${derived.skillName}' is in ${derived.phase} phase — regress to Build first via Skill Center`,
        'error',
      );
      return;
    }
    if (derived && derived.phase === 'build') {
      setUnlinkConfirm({
        workflowId,
        workflowName,
        skillName: derived.skillName,
        derivedPhase: derived.phase,
        reason: 'cascade-build',
      });
      return;
    }
    // Agent-level safeguard: even though THIS workflow has no derived
    // skill, the agent itself is in Act phase (i.e. published). Surface
    // a confirm dialog that names the situation rather than silently
    // detaching a workflow the user might still consider "in scope" of
    // the published agent. Cleared with a single click — same modal
    // chrome as the cascade case for visual consistency.
    if ((agent?.phase || '').toLowerCase() === 'act') {
      setUnlinkConfirm({
        workflowId,
        workflowName,
        skillName: null,
        derivedPhase: null,
        reason: 'agent-published',
      });
      return;
    }
    // No workflow_skill, agent not in act — clean unlink, no confirm dialog
    void performUnlink(workflowId);
  };

  const performUnlink = async (workflowId) => {
    try {
      const { updateWorkflow } = await import('../services/api');
      // Send agentId as empty string to clear — backend handles null conversion
      const result = await updateWorkflow(workflowId, { agentId: '' });
      if (result?.ok) {
        addToast('Workflow unlinked', 'success');
        setUnlinkConfirm(null);
        loadLinked();
        return;
      }
      // Backend rejected — surface the structured 409 if present so the
      // user can see WHICH skills are blocking and what to do about it.
      const code = result?.data?.code;
      if (code === 'workflow_unlink_blocked') {
        const blocking = result?.data?.blockingSkills || [];
        const detail = blocking
          .map(b => `${b.name} (${b.phase})`)
          .join(', ');
        addToast(
          `Unlink blocked — regress these skills first: ${detail || 'see backend logs'}`,
          'error',
        );
      } else if (code === 'workflow_ownership_conflict') {
        addToast(result?.data?.error || 'Workflow already owned by another agent', 'error');
      } else {
        addToast(result?.data?.error || 'Failed to unlink workflow', 'error');
      }
      setUnlinkConfirm(null);
    } catch (err) {
      addToast('Error: ' + err.message, 'error');
      setUnlinkConfirm(null);
    }
  };

  const unlinkableWorkflows = allWorkflows.filter(
    (w) => !linkedWorkflows.some((lw) => lw.id === w.id) && (!w.agentId || w.agentId === agent.id)
  );

  /* P0-2: sourced from the canonical SKILL_TYPE_COLORS_REGISTRY at
   * top of file. Local SKILL_TYPE_COLORS alias kept so the dozens of
   * existing rendering sites in this file don't all need to change. */
  const SKILL_TYPE_COLORS = SKILL_TYPE_COLORS_REGISTRY;

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 0', cursor: 'pointer', userSelect: 'none',
          borderBottom: expanded ? '1px solid #e4e4e7' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={16} style={{ color: '#0d9488' }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#18181b' }}>
            Linked Workflows ({linkedWorkflows.length})
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canWrite && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                loadAll();
                setShowLinkModal(true);
              }}
              style={{ ...btnBase, fontSize: 11, padding: '4px 10px' }}
            >
              <Plus size={13} /> Link
            </button>
          )}
          {expanded ? <ChevronRight size={14} style={{ transform: 'rotate(90deg)', color: '#71717a' }} />
            : <ChevronRight size={14} style={{ color: '#71717a' }} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '12px 0' }}>
          {loading ? (
            <div style={{ fontSize: 12, color: '#a1a1aa', fontStyle: 'italic' }}>Loading...</div>
          ) : linkedWorkflows.length === 0 ? (
            <div style={{
              padding: '16px', borderRadius: 8, background: '#fafaf9', border: '1px dashed #d4d4d8',
              textAlign: 'center', color: '#71717a', fontSize: 12,
            }}>
              No workflows linked to this agent. Workflows linked here become discoverable as
              <strong> Workflow Skills</strong> in the agent's execution context.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
              {linkedWorkflows.map((wf) => {
                // Phase-aware unlink affordance. The × button shows three
                // distinct states so the user understands WHY a click is
                // (or isn't) destructive:
                //   • no derived skill        → red × (clean unlink)
                //   • build-phase skill        → red × that opens confirm
                //   • decide/act-phase skill   → greyed × with tooltip
                //                                pointing at Skill Center
                const derived = skillPhaseByWorkflow[wf.id];
                const isPromoted = derived
                  && (derived.phase === 'decide' || derived.phase === 'act');
                const agentInAct = (agent?.phase || '').toLowerCase() === 'act';
                // Tooltip mirrors the four branches of handleUnlinkClick so
                // the user knows what's about to happen before they click:
                //   • promoted skill   → button is disabled; tooltip points
                //                        them at Skill Center (Rule A)
                //   • build skill      → click → cascade-confirm modal
                //   • agent in act     → click → agent-published confirm
                //   • otherwise         → plain unlink, no confirm
                const tooltip = isPromoted
                  ? `Skill '${derived.skillName}' is in ${derived.phase} phase — `
                    + `regress to Build via Skill Center first`
                  : derived
                    ? `Unlink workflow (will also delete build-phase skill '${derived.skillName}')`
                    : agentInAct
                      ? 'Unlink workflow — agent is in Act phase, will require confirmation'
                      : 'Unlink workflow';
                return (
                  <div key={wf.id} style={{
                    ...cardStyle, padding: 12, display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'box-shadow 0.15s',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#18181b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {wf.name}
                        </span>
                        <span style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 8,
                          background: wf.status === 'active' ? '#dcfce7' : '#f4f4f5',
                          color: wf.status === 'active' ? '#166534' : '#52525b',
                          fontWeight: 600, textTransform: 'uppercase',
                        }}>
                          {wf.status}
                        </span>
                        {derived && (
                          <span style={{
                            fontSize: 9, padding: '1px 6px', borderRadius: 8,
                            background: derived.phase === 'act' ? '#e3f4f1'
                                      : derived.phase === 'decide' ? '#fef9c3'
                                      : '#f0f9ff',
                            color: derived.phase === 'act' ? '#991b1b'
                                  : derived.phase === 'decide' ? '#854d0e'
                                  : '#0369a1',
                            fontWeight: 600, textTransform: 'uppercase',
                          }} title={`Workflow_skill phase: ${derived.phase}`}>
                            skill: {derived.phase}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#71717a' }}>
                        {wf.description || 'No description'}
                        <span style={{ marginLeft: 8, color: '#a1a1aa' }}>v{wf.version || 1}</span>
                      </div>
                    </div>
                    {canWrite && (
                      <button
                        onClick={() => !isPromoted && handleUnlinkClick(wf.id, wf.name)}
                        disabled={isPromoted}
                        title={tooltip}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: isPromoted ? 'not-allowed' : 'pointer',
                          padding: 4,
                          opacity: isPromoted ? 0.35 : 1,
                        }}
                      >
                        <X size={14} style={{ color: isPromoted ? '#a8a29e' : '#ef4444' }} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Confirm modal. Two reasons surface this dialog:
            • cascade-build      — Rule B: build-phase derived skill
                                   exists, will be deleted as part of
                                   unlink. Atomic, no production impact
                                   (act-phase skills are blocked at
                                   Rule A and never reach here).
            • agent-published     — agent itself is in Act, no derived
                                   skill. Soft confirm that names the
                                   situation so the user opts in.
          The CTA copy and the explanation paragraph differ per reason
          so the user always reads exactly what is about to happen. */}
      {unlinkConfirm && (
        <Modal
          isOpen={!!unlinkConfirm}
          title="Unlink workflow from agent"
          onClose={() => setUnlinkConfirm(null)}
        >
          <div style={{ padding: 4, fontSize: 13, color: '#3f3f46', lineHeight: 1.55 }}>
            {unlinkConfirm.reason === 'cascade-build' ? (
              <>
                <p style={{ margin: '0 0 12px 0' }}>
                  Unlinking <strong>{unlinkConfirm.workflowName}</strong> will also
                  delete the linked workflow_skill <strong>{unlinkConfirm.skillName}</strong>{' '}
                  from this agent. The skill is currently in{' '}
                  <strong>{unlinkConfirm.derivedPhase}</strong> phase, so it has
                  not been published to any agentlet — there is no production
                  impact.
                </p>
                <p style={{ margin: '0 0 16px 0', color: '#71717a', fontSize: 12 }}>
                  The workflow itself remains in the workspace, just no longer
                  owned by this agent. You can re-link it (or attach it to a
                  different agent) at any time.
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 12px 0' }}>
                  This agent is currently in <strong>Act phase</strong> with a
                  published bundle running on its agentlet. Unlinking{' '}
                  <strong>{unlinkConfirm.workflowName}</strong> won't affect any
                  published skill — this workflow has no derived workflow_skill
                  on the agent — but it will detach a workflow that lives in
                  this agent's workspace.
                </p>
                <p style={{ margin: '0 0 16px 0', color: '#71717a', fontSize: 12 }}>
                  Continue if you're sure this workflow is no longer needed
                  here. The workflow itself stays in the workspace and can be
                  re-linked or re-assigned at any time.
                </p>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setUnlinkConfirm(null)}
                style={{ ...btnBase, fontSize: 12, padding: '6px 12px' }}
              >
                Cancel
              </button>
              <button
                onClick={() => performUnlink(unlinkConfirm.workflowId)}
                style={{
                  ...btnBase, fontSize: 12, padding: '6px 12px',
                  background: '#0d9488', color: '#fff', border: '1px solid #0d9488',
                }}
              >
                {unlinkConfirm.reason === 'cascade-build'
                  ? 'Unlink & delete skill'
                  : 'Unlink workflow'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Link Workflow Modal */}
      {showLinkModal && (
        <Modal isOpen={showLinkModal} title="Link Workflow to Agent" onClose={() => setShowLinkModal(false)}>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {unlinkableWorkflows.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#71717a', fontSize: 13 }}>
                No available workflows to link. All workflows are either already linked to this agent or owned by another agent.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {unlinkableWorkflows.map((wf) => (
                  <div key={wf.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 8, border: '1px solid #e4e4e7',
                    background: '#ffffff',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{wf.name}</div>
                      <div style={{ fontSize: 11, color: '#71717a' }}>
                        {wf.description || 'No description'} · v{wf.version || 1} · {wf.status}
                        {wf.agentId && wf.agentId !== agent.id && (
                          <span style={{ color: '#d97706', marginLeft: 4 }}>
                            (owned by another agent)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => { handleLink(wf.id); setShowLinkModal(false); }}
                      style={{ ...btnPrimary, fontSize: 11, padding: '5px 12px' }}
                    >
                      Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
// =========================================================================
// END v5.3.0 — LINKED WORKFLOWS SECTION
// =========================================================================

// =========================================================================
// BUILD PHASE PANEL
// =========================================================================
function BuildPhasePanel({
  agent, evals, evalDetails, evalResults, loadingEvals, canWrite, canPromote, hasPermission,
  isCurrentPhase, agentPhase, onAddSkill, onImportFromConnector, onEditSkill, onDeleteSkill, onUnpublishSkill, onPushSkill,
  onGenerateEval, onRunEval, onDeleteEval, onViewEvalDetail,
  generatingEval, runningEval, onAdvancePhase, advancingPhase, onAdvanceSkill, onRegressSkill,
}) {
  // V169 / Wave 3 — AutoLet immutability: skill mutation permissions
  // gated on !isRunbookManaged so every downstream consumer auto-
  // inherits the lock. Backend 409s anyway (W1.4); UI just hides buttons.
  const isAutoLet = agent?.isRunbookManaged === true;
  const canCreateSkill = hasPermission('skill.create') && !isAutoLet;
  const canDeleteSkillPerm = hasPermission('skill.delete') && !isAutoLet;
  const canRunEval = hasPermission('eval.run');
  const skills = agent.skills || [];

  // v5.3.0 — Skill Type filter (rollback: remove skillTypeFilter + filteredSkills, revert to skills directly)
  const [skillTypeFilter, setSkillTypeFilter] = React.useState('all');
  const [confirmDelEvalId, setConfirmDelEvalId] = React.useState(null);
  const filteredSkills = skillTypeFilter === 'all'
    ? skills
    : skills.filter((s) => (s.skillType || 'standard') === skillTypeFilter);

  // Count by type for filter badges
  /* P0-2: typeCounts driven by the canonical SKILL_TYPES registry so
   * new kinds (wasm_skill, etc.) land in the counts dictionary
   * without an edit here. Anything not in the registry collapses
   * onto 'standard' to match historical behaviour. */
  const typeCounts = React.useMemo(() => {
    const counts = { all: skills.length };
    SKILL_TYPES.forEach((t) => { counts[t.id] = 0; });
    skills.forEach((s) => {
      const t = s.skillType || 'standard';
      if (counts[t] === undefined) counts.standard++;
      else counts[t]++;
    });
    return counts;
  }, [skills]);

  // Compute eval readiness summary — incremental model: at least 1 qualifying skill
  const buildSkills = skills.filter((s) => !s.skillPhase || s.skillPhase === 'build');
  const skillsWithEvals = buildSkills.filter((s) => evalDetails[s.id] && evalDetails[s.id].status === 'completed');
  const readySkills = skillsWithEvals.filter((s) => s.evalScore != null && s.evalScore > 0);
  const atLeastOneReady = readySkills.length > 0;
  const allHaveEvals = buildSkills.length > 0 && skillsWithEvals.length === buildSkills.length;
  const allPassing = skillsWithEvals.every((s) => s.evalScore != null && s.evalScore > 0);

  return (
    <div>
      {/* Phase Status Banner */}
      {!isCurrentPhase && (
        <div style={{
          padding: '14px 18px', borderRadius: 12, marginBottom: 22,
          background: 'linear-gradient(135deg, #f5f5f4 0%, #ffffff 100%)',
          border: '1px solid #d6d3d1',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 1px 4px rgba(226,74,58,0.08)',
        }}>
          <CheckCircle size={18} color="#0d9488" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e40af' }}>
              Agent is in {agentPhase.charAt(0).toUpperCase() + agentPhase.slice(1)} phase
            </div>
            <div style={{ fontSize: 11, color: '#78716c', marginTop: 3, lineHeight: 1.4 }}>
              You can still add, edit, and manage skills here. New skills advance through the pipeline independently.
            </div>
          </div>
        </div>
      )}

      {/*
        ═══ V168 — Canonical Execution Graph (CEG) banner ═══════════════════
        Visible only when this agent has an auto-emitted CEG, detected by:
          (a) provenance.autoCreatedFrom present (Auto Create lineage), OR
          (b) name starts with "AutoLets-" (covers older runs missing
              provenance — safe heuristic since the prefix is reserved).
        Links to /canvas?agentId={id} which auto-loads the linked CEG via
        the CanvasPageV3 useEffect §V168 patch (no manual workflow pick).
        Closes the UX gap users hit in Build phase: "I see skills, but
        where is the actual workflow this agent runs?"
        Rollback: remove this entire block.
      */}
      {(() => {
        const isAutoLet =
          (agent.provenance && (agent.provenance.autoCreatedFrom
              || agent.provenance.source === 'runbook'
              || agent.provenance.autoCreateRunId))
          || (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'));
        if (!isAutoLet) return null;
        const cegHref = `/canvas?agentId=${encodeURIComponent(agent.id)}`;
        return (
          <div style={{
            marginBottom: 18,
            padding: '12px 14px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, #fff7ed 0%, #fff 100%)',
            border: '1px solid #fed7aa',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{
              flex: '0 0 auto', width: 32, height: 32, borderRadius: 8,
              background: '#fef3c7', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>◩</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 650, color: '#92400e', marginBottom: 2 }}>
                Canonical Execution Graph
              </div>
              <div style={{ fontSize: 11, color: '#78716c', lineHeight: 1.4 }}>
                The runnable workflow this agent executes — auto-derived from the runbook.
                Inspect, dry-run, or edit; the graph and the agent's skills stay in sync.
              </div>
            </div>
            <a
              href={cegHref}
              style={{
                ...btnPrimary,
                fontSize: 12,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              title="Open the agent's Canonical Execution Graph in Workflow Canvas"
            >
              <ExternalLink size={13} /> Open in Canvas →
            </a>
          </div>
        );
      })()}

      {/* ─── V169 / Wave 3 — AutoLet immutability banner ─────────────
          When the agent is runbook-managed (created via Auto Create
          from a runbook upload), its skills are immutable. Hide the
          Add Skill / Import controls and surface a stone-grey banner
          explaining the new-version path. See GOLDEN_CEG_RULES.md §1
          and §9.2 (Skills panel — every mutation control hidden when
          is_runbook_managed=true).
          The banner is rendered ABOVE the section header so the user
          sees the constraint before they look for buttons. */}
      {agent?.isRunbookManaged === true && (
        <div style={{
          background: '#f5f5f4',
          color: '#44403c',
          padding: '10px 14px',
          borderRadius: 8,
          border: '1px solid #e7e5e4',
          marginBottom: 14,
          fontSize: 12,
          lineHeight: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{
            width: 22, height: 22, borderRadius: '50%',
            background: '#ffffff', border: '1px solid #d6d3d1',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#44403c', flexShrink: 0,
          }} aria-hidden="true">🔒</span>
          <span style={{ flex: 1 }}>
            <strong style={{ color: '#292524' }}>This is an AutoLet</strong> — created from {agent.sourceWorkflowId ? 'workflow' : 'runbook'} v{(agent.sourceWorkflowId ? agent.sourceWorkflowVersion : agent.runbookVersion) ?? 1}.
            Skills are immutable; no edits, deletions, or additions are permitted.
            To make changes, {agent.sourceWorkflowId ? 'publish a revised version of the source workflow' : 'upload a revised runbook'} to create v{((agent.sourceWorkflowId ? agent.sourceWorkflowVersion : agent.runbookVersion) ?? 1) + 1}.
          </span>
        </div>
      )}

      {/* ─── Skills Section ─── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: 17, fontWeight: 650, color: '#1c1917', margin: 0, letterSpacing: '-0.01em' }}>
          Skills
          <span style={{
            marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#78716c',
            background: '#f5f5f4', padding: '2px 8px', borderRadius: 6,
          }}>
            {skills.length}
          </span>
        </h3>
        {canCreateSkill && agent?.isRunbookManaged !== true && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onImportFromConnector} style={{ ...btnBase, fontWeight: 600, fontSize: 12 }}>
              <Download size={14} /> Import
            </button>
            <button onClick={onAddSkill} style={{ ...btnPrimary, fontSize: 12 }}>
              <Plus size={15} /> Add Skill
            </button>
          </div>
        )}
      </div>

      {/* Skill Type Filter Pills */}
      {skills.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
          {[{ id: 'all', label: 'All', icon: null, color: '#57534e' }, ...SKILL_TYPES].map((st) => {
            const isActive = skillTypeFilter === st.id;
            const count = typeCounts[st.id] || 0;
            if (st.id !== 'all' && count === 0 && !isActive) return null;
            return (
              <button
                key={st.id}
                onClick={() => setSkillTypeFilter(st.id)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  border: isActive ? `1.5px solid ${st.color || '#1c1917'}` : '1px solid #e7e5e4',
                  background: isActive ? `${st.color || '#1c1917'}0d` : '#ffffff',
                  color: isActive ? (st.color || '#1c1917') : '#78716c',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                  transition: 'all 0.15s ease',
                  boxShadow: isActive ? `0 1px 4px ${st.color || '#1c1917'}15` : 'none',
                }}
              >
                {st.icon && <span style={{ fontSize: 12 }}>{st.icon}</span>}
                {st.label}
                <span style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 8,
                  background: isActive ? `${st.color || '#1c1917'}15` : '#f5f5f4',
                  color: isActive ? (st.color || '#1c1917') : '#a8a29e',
                  fontWeight: 700,
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {skills.length === 0 && (
        <EmptyState text="No skills yet. Add your first skill to begin building this agent." />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16, marginBottom: 24 }}>
        {filteredSkills.map((skill) => (
          <SkillCardBuild
            key={skill.id}
            skill={skill}
            evalDetail={evalDetails[skill.id]}
            evalResult={evalResults[skill.id]}
            canWrite={canWrite}
            canPromote={canPromote}
            hasPermission={hasPermission}
            isCurrentPhase={isCurrentPhase}
            onEdit={() => onEditSkill(skill)}
            onDelete={() => onDeleteSkill(skill)}
            onPush={() => onPushSkill(skill)}
            onAdvanceSkill={onAdvanceSkill ? () => onAdvanceSkill(skill) : null}
            onRegressSkill={onRegressSkill ? () => onRegressSkill(skill) : null}
            // Published-asset deletion lockdown — passed through so the
            // SkillCardBuild renders the Unpublish button next to a
            // disabled Delete when skill.wasPublished or skill.pushed.
            onUnpublishSkill={onUnpublishSkill}
            agentPhase={agentPhase}
            // V169 / Wave 3 — AutoLet immutability passes through here.
            // Card-level effect: hides Edit / Delete / Unpublish / Push.
            // Eval-related actions stay (Generate, Run, Re-run, View
            // Details) because they don't mutate skill metadata.
            isAgentRunbookManaged={agent?.isRunbookManaged === true}
            onGenerateEval={() => onGenerateEval(skill.id)}
            onRunEval={(evalSuiteId) => onRunEval(skill.id, evalSuiteId)}
            onDeleteEval={(evalSuiteId) => onDeleteEval(evalSuiteId)}
            onViewEvalDetail={(evalSuite) => onViewEvalDetail(evalSuite)}
            generatingEval={generatingEval === skill.id}
            runningEval={runningEval}
          />
        ))}
      </div>

      {/* Eval Readiness Banner — incremental model */}
      {buildSkills.length > 0 && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          background: atLeastOneReady ? '#e3f4f1' : '#e3f4f1',
          border: `1px solid ${atLeastOneReady ? '#a5d8d0' : '#a5d8d0'}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {atLeastOneReady
            ? <CheckCircle size={18} color="#57534e" />
            : <AlertTriangle size={18} color="#d97706" />}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: atLeastOneReady ? '#065f46' : '#92400e' }}>
              {allHaveEvals && allPassing
                ? 'All skills have passing evaluations — ready to advance'
                : atLeastOneReady
                ? `${readySkills.length} of ${buildSkills.length} skill(s) ready to advance`
                : `Eval Readiness: ${skillsWithEvals.length}/${buildSkills.length} skills evaluated`}
            </div>
            <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
              {!atLeastOneReady
                ? 'Generate and run evals for at least one skill to advance to Decide phase.'
                : !allPassing
                ? `${readySkills.length} qualifying skill(s) will advance. ${buildSkills.length - readySkills.length} will remain in Build for later.`
                : 'All gate checks will pass. You can advance to the Decide phase.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#52525b' }}>
            <span style={{
              padding: '3px 8px', borderRadius: 6, fontWeight: 600,
              background: atLeastOneReady ? '#dcfce7' : '#fef3c7',
              color: atLeastOneReady ? '#166534' : '#92400e',
            }}>
              {readySkills.length}/{buildSkills.length} ready
            </span>
          </div>
        </div>
      )}

      {/* Evals Summary — clickable cards with actions */}
      {evals.length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 24 }}>
          <h4 style={{ fontSize: 15, fontWeight: 600, color: '#18181b', margin: '0 0 12px 0' }}>
            Evaluation Suites ({evals.length})
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {evals.map((ev) => {
              const scoreColor = ev.score >= 0.9 ? '#57534e' : ev.score >= 0.7 ? '#d97706' : '#ef4444';
              return (
                <div key={ev.id} style={{ ...cardStyle, padding: 14, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                  onClick={() => onViewEvalDetail(ev)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{ev.name}</div>
                      <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
                        {ev.description || `Skill ID: ${ev.skillId || 'N/A'}`}
                      </div>
                    </div>
                    <Badge status={ev.status}>{ev.status}</Badge>
                  </div>
                  {ev.status === 'completed' && ev.score != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{
                        width: '100%', height: 6, borderRadius: 3, background: '#f0f0f0', overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${ev.score * 100}%`, height: '100%', borderRadius: 3,
                          background: scoreColor, transition: 'width 0.3s',
                        }} />
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 12, color: scoreColor, whiteSpace: 'nowrap' }}>
                        {(ev.score * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: '#a1a1aa' }}>
                      {ev.passedTests != null ? `${ev.passedTests}/${ev.totalTests} passed` : ''}
                      {ev.createdAt && ` · ${new Date(ev.createdAt).toLocaleDateString()}`}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={(e) => { e.stopPropagation(); onViewEvalDetail(ev); }}
                        style={{ ...btnBase, fontSize: 10, padding: '2px 8px' }}>
                        <Eye size={10} /> View
                      </button>
                      {canDeleteSkillPerm && (
                        confirmDelEvalId === ev.id ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <button onClick={(e) => { e.stopPropagation(); onDeleteEval(ev.id); setConfirmDelEvalId(null); }}
                              style={{ ...btnBase, fontSize: 10, padding: '2px 8px', color: '#ef4444', fontWeight: 600 }}>Delete</button>
                            <button onClick={(e) => { e.stopPropagation(); setConfirmDelEvalId(null); }}
                              style={{ ...btnBase, fontSize: 10, padding: '2px 8px' }}>Cancel</button>
                          </span>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setConfirmDelEvalId(ev.id); }}
                            style={{ ...btnBase, fontSize: 10, padding: '2px 8px', color: '#ef4444' }}>
                            <Trash2 size={10} />
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loadingEvals && (
        <div style={{ textAlign: 'center', padding: 24, color: '#a1a1a6', fontSize: 13 }}>Loading evals...</div>
      )}

      {/* Advance Phase Button — hidden for external agents (use GEL lifecycle instead) */}
      {isCurrentPhase && canPromote() && buildSkills.length > 0 && !agent.isExternal && (
        <AdvancePhaseButton
          label="Advance to Decide"
          sublabel={allHaveEvals && allPassing
            ? '✓ All gate checks passing — ready to advance'
            : atLeastOneReady
            ? `✓ ${readySkills.length} skill(s) ready — ${buildSkills.length - readySkills.length} will remain in Build`
            : `Gate: need at least 1 skill with passing evals (${readySkills.length}/${buildSkills.length})`}
          onClick={onAdvancePhase}
          loading={advancingPhase}
          color={PHASE_COLORS.decide}
        />
      )}

      {/* External agent banner — direct users to GEL lifecycle */}
      {isCurrentPhase && agent.isExternal && (
        <div style={{
          margin: '16px 0 0', padding: '12px 16px', borderRadius: 8,
          background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#a5b4fc',
        }}>
          <span style={{ fontSize: 16 }}>⇄</span>
          <span>
            External agents are advanced through the <strong>Protection → External Agents</strong> registry.
            Use the Build-Decide-Act lifecycle panel on that page.
          </span>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Build Phase Skill Card with Eval UI
// =========================================================================
// ─── Policy Document Panel (inline in skill card) ────────────────
function PolicyDocumentPanel({ skillId, canEdit }) {
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [enforcement, setEnforcement] = React.useState('advisory');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState(null);
  const [searching, setSearching] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const { addToast } = useToast();
  const [confirmDelDoc, setConfirmDelDoc] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const loadDocs = React.useCallback(async (retries = 2) => {
    try {
      const result = await listPolicyDocuments(skillId);
      // apiFetch wraps responses as { ok, data } — unwrap the array
      const docs = result?.data ?? result;
      setDocs(Array.isArray(docs) ? docs : []);
    } catch (e) {
      // Retry on 403 — auth token may not be ready on initial mount
      if (retries > 0 && e.message && e.message.includes('403')) {
        setTimeout(() => loadDocs(retries - 1), 800);
        return; // don't clear loading yet
      }
      console.error('Failed to load policy documents:', e);
    } finally { setLoading(false); }
  }, [skillId]);

  React.useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadPolicyDocument(skillId, file, enforcement);
      await loadDocs();
    } catch (err) {
      addToast('Upload failed: ' + err.message, 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deletePolicyDocument(skillId, docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (err) { addToast('Delete failed: ' + err.message, 'error'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const result = await searchPolicyDocuments(skillId, searchQuery);
      setSearchResults(result?.data ?? result);
    } catch (err) {
      addToast('Search failed: ' + err.message, 'error');
    } finally { setSearching(false); }
  };

  const statusColors = { parsed: '#166534', parsed_no_embeddings: '#92400e', parsing: '#0c4a6e', embedding: '#0c4a6e', chunking: '#0c4a6e', error: '#991b1b', uploading: '#92400e' };
  const statusBg = { parsed: '#dcfce7', parsed_no_embeddings: '#fef3c7', parsing: '#e0f2fe', embedding: '#e0f2fe', chunking: '#e0f2fe', error: '#fef2f2', uploading: '#fef3c7' };
  const enforcementColors = { advisory: '#0c4a6e', mandatory: '#92400e', blocking: '#991b1b' };

  return (
    <div style={{
      background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 10,
      padding: 14, marginBottom: 14,
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontWeight: 600, fontSize: 12, color: '#92400e', flex: 1 }}>
          Policy Documents ({loading ? '...' : docs.length})
        </span>
        <ChevronRight size={14} color="#92400e" style={{
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
        }} />
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {/* Upload area */}
          {canEdit && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <select
                  value={enforcement}
                  onChange={(e) => setEnforcement(e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: 6, fontSize: 11, border: '1px solid #d6d3d1',
                    background: '#fff', color: '#18181b',
                  }}
                >
                  <option value="advisory">Advisory</option>
                  <option value="mandatory">Mandatory</option>
                  <option value="blocking">Blocking</option>
                </select>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  onChange={handleUpload}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    border: '1px solid #d6d3d1', background: '#fff', color: '#18181b',
                    cursor: uploading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <Upload size={11} /> {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
              <div style={{ fontSize: 10, color: '#a8a29e' }}>
                Supported: PDF, DOCX, TXT, MD (max 50MB)
              </div>
            </div>
          )}

          {/* Document list */}
          {docs.length === 0 && !loading && (
            <div style={{ fontSize: 11, color: '#a8a29e', fontStyle: 'italic', padding: '8px 0' }}>
              No policy documents uploaded yet. Upload a document to enable RAG-based policy enforcement.
            </div>
          )}
          {docs.map((doc) => (
            <div key={doc.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
              background: '#fff', borderRadius: 8, marginBottom: 6,
              border: '1px solid #e7e5e4',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: '#18181b',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {doc.filename}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>
                  <span style={{
                    fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                    background: statusBg[doc.parseStatus] || '#f5f5f4',
                    color: statusColors[doc.parseStatus] || '#71717a',
                  }}>
                    {doc.parseStatus}
                  </span>
                  <span style={{
                    fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                    background: '#f5f5f4', color: enforcementColors[doc.enforcement] || '#71717a',
                  }}>
                    {doc.enforcement}
                  </span>
                  {doc.chunkCount > 0 && (
                    <span style={{ fontSize: 9, color: '#a8a29e' }}>{doc.chunkCount} chunks</span>
                  )}
                </div>
              </div>
              {canEdit && (
                confirmDelDoc === doc.id ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: '#78716c' }}>Delete doc &amp; chunks?</span>
                    <button
                      onClick={() => { handleDelete(doc.id); setConfirmDelDoc(null); }}
                      style={{
                        padding: '3px 8px', borderRadius: 4, border: '1px solid #fecaca',
                        background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                      }}
                    >Delete</button>
                    <button
                      onClick={() => setConfirmDelDoc(null)}
                      style={{
                        padding: '3px 8px', borderRadius: 4, border: '1px solid #e7e5e4',
                        background: '#ffffff', color: '#57534e', cursor: 'pointer', fontSize: 10,
                      }}
                    >Cancel</button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmDelDoc(doc.id)}
                    style={{
                      padding: '3px 6px', borderRadius: 4, border: '1px solid #fecaca',
                      background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontSize: 10,
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <Trash2 size={10} />
                  </button>
                )
              )}
            </div>
          ))}

          {/* Search */}
          {docs.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search policy documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  style={{
                    flex: 1, padding: '5px 10px', borderRadius: 6, fontSize: 11,
                    border: '1px solid #d6d3d1', background: '#fff',
                  }}
                />
                <button
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  style={{
                    padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    border: '1px solid #d6d3d1', background: '#fff', cursor: 'pointer',
                  }}
                >
                  {searching ? '...' : 'Search'}
                </button>
              </div>

              {searchResults && searchResults.results?.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
                  {searchResults.results.map((r, i) => (
                    <div key={i} style={{
                      padding: '8px 10px', background: '#fff', borderRadius: 6,
                      marginBottom: 4, border: '1px solid #e7e5e4', fontSize: 11,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, color: '#18181b' }}>{r.filename}</span>
                        <span style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                          background: r.score >= 0.7 ? '#dcfce7' : r.score >= 0.5 ? '#fef3c7' : '#f5f5f4',
                          color: r.score >= 0.7 ? '#166534' : r.score >= 0.5 ? '#92400e' : '#71717a',
                        }}>
                          {(r.score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div style={{ color: '#52525b', lineHeight: 1.4 }}>
                        {r.content?.substring(0, 200)}{r.content?.length > 200 ? '...' : ''}
                      </div>
                      {r.sectionHeader && (
                        <div style={{ fontSize: 9, color: '#a8a29e', marginTop: 3 }}>
                          Section: {r.sectionHeader}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {searchResults && searchResults.results?.length === 0 && (
                <div style={{ fontSize: 11, color: '#a8a29e', fontStyle: 'italic', marginTop: 6 }}>
                  No matching policy passages found.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SkillCardBuild({
  skill, evalDetail, evalResult, canWrite, canPromote, hasPermission, isCurrentPhase,
  onEdit, onDelete, onPush, onAdvanceSkill, onRegressSkill, onUnpublishSkill,
  onGenerateEval, onRunEval, onDeleteEval, onViewEvalDetail,
  generatingEval, runningEval, agentPhase,
  // V169 / Wave 3 — when the parent agent is runbook-managed (AutoLet),
  // every skill mutation is forbidden per GOLDEN_CEG_RULES.md §1.
  // Edit, Delete, Unpublish, and Re-push are all hidden in that case.
  // Eval-related actions (Generate, Run, Re-run, View Details) stay
  // because they don't mutate skill metadata. Phase advance / regress
  // also stay because phase transitions are governance, not metadata
  // edits.
  isAgentRunbookManaged = false,
}) {
  const skillPhase = skill.skillPhase || 'build';
  const isLocked = skillPhase !== 'build'; // Skills in Decide/Act are locked
  const [confirmDelEval, setConfirmDelEval] = React.useState(false);
  // AutoLet immutability is a stronger lock than the phase lock: it
  // applies in EVERY phase, and removes the regress-and-edit escape
  // hatch entirely. Both gates are AND'd together so neither can grant
  // edit access alone.
  const canEditSkill = hasPermission('skill.edit') && !isLocked && !isAgentRunbookManaged;
  const canDeleteSkill = hasPermission('skill.delete') && !isLocked && !isAgentRunbookManaged;
  const canPushSkill = hasPermission('skill.push') && !isAgentRunbookManaged;
  const canRunEval = hasPermission('eval.run');
  const canDeleteEval = canDeleteSkill; // eval delete follows skill.delete permission
  const evalScore = skill.evalScore != null ? skill.evalScore * 100 : null;
  const evalScoreColor = evalScore == null ? '#a1a1aa'
    : evalScore >= 90 ? '#57534e'
    : evalScore >= 70 ? '#d97706'
    : '#ef4444';

  return (
    <div style={{
      ...cardStyle,
      position: 'relative',
      overflow: 'hidden',
      minWidth: 0,
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = '#d6d3d1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
        e.currentTarget.style.borderColor = '#e7e5e4';
      }}
    >
      {/* Accent top bar based on skill type */}
      {skill.skillType && skill.skillType !== 'standard' && (() => {
        const st = SKILL_TYPES.find((t) => t.id === skill.skillType);
        return st ? (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, ${st.color} 0%, ${st.color}66 100%)`,
          }} />
        ) : null;
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <h4 style={{ fontSize: 14, fontWeight: 650, color: '#1c1917', margin: 0, letterSpacing: '-0.01em', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{skill.name}</h4>
            {/* Skill Type Badge — inline with name */}
            {skill.skillType && skill.skillType !== 'standard' && (() => {
              const st = SKILL_TYPES.find((t) => t.id === skill.skillType);
              return st ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: `${st.color}10`, color: st.color,
                  border: `1px solid ${st.color}25`,
                  whiteSpace: 'nowrap',
                }}>
                  <span style={{ fontSize: 10 }}>{st.icon}</span> {st.label}
                </span>
              ) : null;
            })()}
            {/* Phase 7 — Auto-Generated badge.
                Indicates this skill was auto-created by the unified policy
                assignment wizard (auto_generated=true). Operators should
                avoid editing the policy_config here — re-run the wizard
                instead so the framework binding stays in sync. */}
            {skill.autoGenerated && (
              <span
                title={`Wizard-managed skill (kind: ${skill.skillKind || 'AUTO_FRAMEWORK_BINDING'}). Re-run the policy wizard to update framework binding.`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: '#eff6ff', color: '#1e40af',
                  border: '1px solid #bfdbfe',
                  whiteSpace: 'nowrap',
                }}
              >
                Auto
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#78716c', margin: 0, lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{skill.description}</p>
        </div>
        <Badge status={skill.riskLevel}>{skill.riskLevel}</Badge>
      </div>

      {/* Metadata Row */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 10, fontSize: 11, color: '#a8a29e',
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <span style={{
          padding: '2px 8px', borderRadius: 6, background: '#f5f5f4',
          color: '#57534e', fontWeight: 600, fontSize: 10,
        }}>
          {skill.autonomy?.replace(/_/g, ' ')}
        </span>
        {skill.inferenceMode && (
          <span style={{
            padding: '2px 8px', borderRadius: 6, background: '#fafaf9',
            color: '#78716c', fontSize: 10,
          }}>
            {skill.inferenceMode?.replace(/_/g, ' ')}
          </span>
        )}
        {evalScore != null && (
          <span style={{
            fontWeight: 700, color: evalScoreColor,
            padding: '2px 8px', borderRadius: 6,
            background: evalScore >= 90 ? '#e3f4f1' : evalScore >= 70 ? '#e3f4f1' : '#fef2f2',
          }}>
            {evalScore.toFixed(0)}%
          </span>
        )}
        {skill.pushed && <Badge status="deployed" variant="outline">Pushed</Badge>}
        {skill.reviewStatus && skill.reviewStatus !== 'pending_review' && (
          <ReviewStatusBadge status={skill.reviewStatus} />
        )}
      </div>

      {/* Guardrails */}
      {(() => {
        const guardrailLabels = {
          rate_limit: 'Rate Limit', audit_log: 'Audit Log', scope_limit: 'Scope Limit',
          approval_required: 'Approval Required', dual_approval: 'Dual Approval', rbac_check: 'RBAC Check',
        };
        const gList = Array.isArray(skill.guardrails) ? skill.guardrails
          : (skill.guardrails && typeof skill.guardrails === 'object')
            ? Object.keys(skill.guardrails).filter((k) => skill.guardrails[k])
            : [];
        return gList.length > 0 ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: 10, color: '#a8a29e', marginBottom: 5, textTransform: 'uppercase',
              letterSpacing: '0.06em', fontWeight: 600,
            }}>Guardrails</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {gList.map((g) => (
                <span key={g} style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: '#f5f3ff', color: '#6d28d9', border: '1px solid #e9d5ff',
                }}>
                  {guardrailLabels[g] || g}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: '#d6d3d1', fontStyle: 'italic' }}>No guardrails configured</div>
          </div>
        );
      })()}

      {/* Policy Documents — only for policy_skill type */}
      {skill.skillType === 'policy_skill' && (
        <PolicyDocumentPanel skillId={skill.id} canEdit={canEditSkill} />
      )}

      {/* Eval UI */}
      <div style={{
        background: '#fafaf9', border: '1px solid #f5f5f4', borderRadius: 10,
        padding: 14, marginBottom: 14,
      }}>
        {!evalDetail ? (
          <div>
            <div style={{ fontSize: 12, color: '#52525b', marginBottom: 8 }}>No eval suite yet</div>
            {canRunEval && (
              <button
                onClick={onGenerateEval}
                disabled={generatingEval}
                style={{ ...btnBase, fontSize: 11, fontWeight: 600, width: '100%', justifyContent: 'center' }}
              >
                <Beaker size={12} /> {generatingEval ? 'Generating...' : 'Generate Eval'}
              </button>
            )}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Beaker size={12} color="#0d9488" />
              <div style={{ fontWeight: 600, fontSize: 12, color: '#0c4a6e', flex: 1 }}>
                {evalDetail.name || 'Eval Suite'}
              </div>
              <Badge status={evalDetail.status}>{evalDetail.status}</Badge>
            </div>

            {/* Score bar */}
            {evalDetail.score != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1, height: 5, borderRadius: 3, background: '#e4e4e7', overflow: 'hidden' }}>
                  <div style={{
                    width: `${evalDetail.score * 100}%`, height: '100%', borderRadius: 3,
                    background: evalDetail.score >= 0.9 ? '#57534e' : evalDetail.score >= 0.7 ? '#d97706' : '#ef4444',
                  }} />
                </div>
                <span style={{
                  fontWeight: 700, fontSize: 11,
                  color: evalDetail.score >= 0.9 ? '#57534e' : evalDetail.score >= 0.7 ? '#d97706' : '#ef4444',
                }}>
                  {(evalDetail.score * 100).toFixed(0)}%
                </span>
              </div>
            )}

            {evalDetail.passedTests != null && (
              <div style={{ fontSize: 11, color: '#52525b', marginBottom: 8 }}>
                {evalDetail.passedTests}/{evalDetail.totalTests} tests passed
              </div>
            )}

            {/* Eval action buttons — run-once enforcement */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <button
                onClick={() => onViewEvalDetail(evalDetail)}
                style={{ ...btnBase, fontSize: 10, padding: '3px 8px' }}
              >
                <Eye size={10} /> View Details
              </button>
              {canRunEval && evalDetail.status !== 'archived' && (
                <button
                  onClick={() => onRunEval(evalDetail.id)}
                  disabled={runningEval === evalDetail.id}
                  style={{ ...btnBase, fontSize: 10, padding: '3px 8px', color: '#57534e' }}
                >
                  <Play size={10} /> {runningEval === evalDetail.id ? 'Running...' : evalDetail.status === 'completed' ? 'Re-run' : 'Run'}
                </button>
              )}
              {evalDetail.status === 'completed' && (
                <span style={{ fontSize: 10, padding: '3px 8px', color: '#57534e', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <CheckCircle size={10} /> Executed
                </span>
              )}
              {canRunEval && (
                <button
                  onClick={onGenerateEval}
                  disabled={generatingEval}
                  style={{ ...btnBase, fontSize: 10, padding: '3px 8px', color: '#6366f1' }}
                >
                  <Beaker size={10} /> {generatingEval ? 'Regenerating...' : 'Regenerate'}
                </button>
              )}
              {canDeleteEval && (
                confirmDelEval ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <button onClick={() => { onDeleteEval(evalDetail.id); setConfirmDelEval(false); }}
                      style={{ ...btnBase, fontSize: 10, padding: '3px 8px', color: '#ef4444', fontWeight: 600 }}>Delete</button>
                    <button onClick={() => setConfirmDelEval(false)}
                      style={{ ...btnBase, fontSize: 10, padding: '3px 8px' }}>Cancel</button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmDelEval(true)}
                    style={{ ...btnBase, fontSize: 10, padding: '3px 8px', color: '#ef4444' }}
                  >
                    <Trash2 size={10} /> Delete
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons — lifecycle-locked for non-Build skills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {isLocked ? (
          <>
            {/* Locked indicator + regress button */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, padding: '3px 8px', borderRadius: 6, fontWeight: 600,
              background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
            }}>
              <Lock size={10} /> Locked ({skillPhase.toUpperCase()})
            </span>
            {onRegressSkill && hasPermission('phase.advance') && (
              <button onClick={onRegressSkill} style={{
                ...btnBase, background: '#e3f4f1', color: '#0a766b', border: '1px solid #a5d8d0',
              }}>
                <RotateCcw size={12} /> Regress to Build
              </button>
            )}
            {/* Per-skill advance: only show when skill can still advance */}
            {agentPhase && agentPhase !== 'build' && skillPhase !== 'act' && onAdvanceSkill && (
              <button onClick={onAdvanceSkill} style={{
                ...btnBase, background: '#f5f5f4', color: '#0a766b', border: '1px solid #d6d3d1',
              }}>
                <ArrowRight size={12} /> Advance
              </button>
            )}
          </>
        ) : (
          <>
            {canEditSkill && <button onClick={onEdit} style={btnBase}><Edit2 size={12} /> Edit</button>}
            {/* Published-asset deletion lockdown (2026-05-04). When a
                skill carries wasPublished=true (regressed-from-Act, the
                ghost-publish state) Delete is disabled with a tooltip
                pointing the user at Unpublish. The Unpublish button
                clears wasPublished AND re-syncs the agentlet bundle so
                the skill is genuinely gone from the gateway, after
                which Delete enables. Without this gate, deleting a
                regressed skill would orphan the still-callable agentlet
                copy. The backend enforces the same rule (returns 409 +
                published_asset_deletion code) so a programmatic caller
                also can't bypass — the UI gate is purely UX. */}
            {canDeleteSkill && (() => {
              const isGhostPublished = skill.wasPublished === true || skill.pushed === true;
              if (isGhostPublished) {
                return (
                  <>
                    <button
                      disabled
                      title={"Skill has been published — Unpublish first to drop it from "
                           + "the agentlet's gateway bundle, then delete."}
                      style={{
                        ...btnDanger, opacity: 0.4, cursor: 'not-allowed',
                      }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                    {onUnpublishSkill && (
                      <button
                        onClick={() => onUnpublishSkill(skill)}
                        title={"Clears the published flags and re-syncs the agentlet "
                             + "so the skill is dropped from the gateway. Required "
                             + "before deletion."}
                        style={{
                          ...btnBase, background: '#e3f4f1', color: '#0a766b',
                          border: '1px solid #a5d8d0',
                        }}
                      >
                        <Upload size={12} style={{ transform: 'rotate(180deg)' }} /> Unpublish
                      </button>
                    )}
                  </>
                );
              }
              return <button onClick={onDelete} style={btnDanger}><Trash2 size={12} /> Delete</button>;
            })()}
            {canPushSkill && !skill.pushed && <button onClick={onPush} style={btnBase}><Upload size={12} /> Push</button>}
            {/* Per-skill advance: only show when agent is already past build and skill is still in build/decide */}
            {agentPhase && agentPhase !== 'build' && skillPhase !== 'act' && onAdvanceSkill && (
              <button onClick={onAdvanceSkill} style={{
                ...btnBase, background: '#f5f5f4', color: '#0a766b', border: '1px solid #d6d3d1',
              }}>
                <ArrowRight size={12} /> Advance
              </button>
            )}
          </>
        )}
        {/* Regression indicator */}
        {skill.regressionCount > 0 && (
          <span style={{
            fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
            background: '#e3f4f1', color: '#0a766b', border: '1px solid #fecdd3',
          }}>
            regressed {skill.regressionCount}x
          </span>
        )}
        {skill.skillPhase && skill.skillPhase !== 'build' && (
          <span style={{
            fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, marginLeft: 4,
            background: skill.skillPhase === 'act' ? '#dcfce7' : '#e3f4f1',
            color: skill.skillPhase === 'act' ? '#166534' : '#92400e',
          }}>
            {skill.skillPhase.toUpperCase()}
          </span>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// DECIDE PHASE PANEL
// =========================================================================
function DecidePhasePanel({
  agent, evals, skillReviews, loadingEvals, canWrite, canPromote, hasPermission,
  isCurrentPhase, agentPhase, onReviewSkill, onShowReviewModal, onAdvancePhase, advancingPhase,
}) {
  const canApprove = hasPermission('execution.approve');
  const skills = agent.skills || [];
  const agentPastDecide = agentPhase === 'act';

  // Build a map of latest review per skill
  const reviewMap = {};
  (skillReviews || []).forEach((r) => {
    if (!reviewMap[r.skillId] || new Date(r.createdAt) > new Date(reviewMap[r.skillId].createdAt)) {
      reviewMap[r.skillId] = r;
    }
  });

  const reviewedCount = Object.keys(reviewMap).length;
  const unreviewedSkills = skills.filter((s) => !reviewMap[s.id]);

  return (
    <div>
      {/* Phase Completed Banner — Decide is done, agent is in Act */}
      {agentPastDecide && unreviewedSkills.length === 0 && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          background: '#e3f4f1', border: '1px solid #a5d8d0',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <CheckCircle size={18} color="#0d9488" />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#065f46' }}>
              Decide phase completed
            </div>
            <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
              All skills have been reviewed. This agent is now in the Act phase.
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 17, fontWeight: 650, color: '#1c1917', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
          Skill Review Matrix
          <span style={{
            marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#78716c',
            background: '#f5f5f4', padding: '2px 8px', borderRadius: 6,
          }}>
            {reviewedCount}/{skills.length}
          </span>
        </h3>
        <p style={{ fontSize: 13, color: '#78716c', margin: 0, lineHeight: 1.5 }}>
          Review each skill's eval score and set autonomy level.
        </p>
      </div>

      {/* Banner: agent is already in Act phase but has unreviewed skills */}
      {agentPastDecide && unreviewedSkills.length > 0 && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: '#e3f4f1', border: '1px solid #a5d8d0',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <AlertTriangle size={16} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
              {unreviewedSkills.length} skill{unreviewedSkills.length > 1 ? 's need' : ' needs'} review
            </div>
            <div style={{ fontSize: 12, color: '#a16207', marginTop: 2 }}>
              This agent is already in the <strong>Act</strong> phase. Review and approve each new skill individually
              to activate it alongside your existing skills.
            </div>
          </div>
        </div>
      )}

      {skills.length === 0 && (
        <EmptyState text="No skills to review. Go back to Build phase to add skills." />
      )}

      {/* Review Cards */}
      {skills.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16, marginBottom: 24 }}>
          {skills.map((skill) => {
            const review = reviewMap[skill.id];
            const score = skill.evalScore != null ? (skill.evalScore * 100).toFixed(0) : 'N/A';
            const isUnreviewed = !review;
            // Allow actions if: (a) Decide is current phase, OR (b) agent is past Decide and this skill still needs review
            const canAct = isCurrentPhase || (agentPastDecide && isUnreviewed);
            // When agent is past Decide, reviewed skills are already active
            const isActiveInAct = agentPastDecide && review;

            return (
              <div key={skill.id} style={{
                ...cardStyle,
                ...(agentPastDecide && isUnreviewed ? {
                  border: '2px solid #fbbf24',
                  boxShadow: '0 0 0 1px rgba(251,191,36,0.2)',
                } : {}),
                ...(isActiveInAct ? { opacity: 0.7 } : {}),
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#18181b', margin: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {skill.name}
                      {isActiveInAct && (
                        <span style={{
                          marginLeft: 8, fontSize: 9, padding: '2px 6px', borderRadius: 4,
                          background: '#dcfce7', color: '#166534', fontWeight: 700, verticalAlign: 'middle',
                        }}>
                          ACTIVE
                        </span>
                      )}
                      {agentPastDecide && isUnreviewed && (
                        <span style={{
                          marginLeft: 8, fontSize: 9, padding: '2px 6px', borderRadius: 4,
                          background: '#fef3c7', color: '#92400e', fontWeight: 700, verticalAlign: 'middle',
                        }}>
                          NEEDS REVIEW
                        </span>
                      )}
                    </h4>
                    <p style={{ fontSize: 12, color: '#71717a', margin: '4px 0 0 0', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{skill.description}</p>
                  </div>
                  <Badge status={skill.riskLevel}>{skill.riskLevel}</Badge>
                </div>

                {/* Eval Score Prominently Displayed */}
                <div style={{
                  padding: 12, borderRadius: 6, background: '#f9fafb',
                  border: `2px solid ${score === 'N/A' ? '#e4e4e7'
                    : parseFloat(score) >= 90 ? '#d6d3d1'
                    : parseFloat(score) >= 70 ? '#a5d8d0'
                    : '#fecaca'}`,
                  marginBottom: 12,
                }}>
                  <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Eval Score</div>
                  <div style={{
                    fontSize: 18, fontWeight: 700,
                    color: score === 'N/A' ? '#a1a1aa'
                      : parseFloat(score) >= 90 ? '#57534e'
                      : parseFloat(score) >= 70 ? '#d97706'
                      : '#ef4444',
                  }}>
                    {score}{score !== 'N/A' ? '%' : ''}
                  </div>
                </div>

                {/* Review Status */}
                <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#f5f5f4' }}>
                  <div style={{ fontSize: 11, color: '#71717a', marginBottom: 4 }}>Review Status</div>
                  {review ? (
                    <div style={{
                      padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: review.decision === 'autonomous' ? '#e3f4f1'
                        : review.decision === 'blocked' ? '#fef2f2' : '#f5f5f4',
                      color: review.decision === 'autonomous' ? '#065f46'
                        : review.decision === 'blocked' ? '#991b1b' : '#0c4a6e',
                      display: 'inline-block',
                    }}>
                      {review.decision}
                    </div>
                  ) : (
                    <div style={{ color: '#a1a1aa', fontSize: 12 }}>Pending review</div>
                  )}
                </div>

                {/* Review Actions — shown when Decide is current OR agent is past Decide with unreviewed skill */}
                {(canApprove || (agentPastDecide && canWrite)) && canAct && (
                  <button
                    onClick={() => onShowReviewModal(skill)}
                    style={{
                      ...btnPrimary, fontSize: 11, width: '100%', justifyContent: 'center',
                      ...(agentPastDecide && isUnreviewed ? {
                        background: 'linear-gradient(135deg, #d97706 0%, #0a766b 100%)',
                        color: '#fff',
                      } : {}),
                    }}
                  >
                    <ShieldCheck size={12} />
                    {agentPastDecide && isUnreviewed
                      ? 'Review & Activate Skill'
                      : review ? 'Update Review' : 'Start Review'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Review History */}
      {skillReviews && skillReviews.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#52525b', margin: '0 0 10px 0' }}>
            Review History ({skillReviews.length})
          </h4>
          <div style={{ ...cardStyle, maxHeight: 240, overflowY: 'auto' }}>
            {skillReviews.map((r) => (
              <div key={r.id} style={{ padding: '10px 12px', borderBottom: '1px solid #f5f5f4', fontSize: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#18181b' }}>{r.decision}</span>
                  <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
                    Score: {r.evalScoreAtReview != null ? (r.evalScoreAtReview * 100).toFixed(0) + '%' : 'N/A'}
                  </div>
                  {r.justification && <div style={{ fontSize: 11, color: '#a1a1aa', fontStyle: 'italic', marginTop: 2 }}>{r.justification}</div>}
                </div>
                <div style={{ fontSize: 11, color: '#d4d4d8', whiteSpace: 'nowrap' }}>
                  {r.createdAt && new Date(r.createdAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Advance Phase Button — only when Decide is the current phase; hidden for external agents */}
      {isCurrentPhase && canPromote() && !agent.isExternal && (
        <AdvancePhaseButton
          label="Advance to Act"
          sublabel={`Reviewed skills advance to Act. ${unreviewedSkills.length > 0 ? unreviewedSkills.length + ' unreviewed skill(s) will remain in Decide for later review.' : 'All skills reviewed — ready to advance.'}`}
          onClick={onAdvancePhase}
          loading={advancingPhase}
          color={PHASE_COLORS.act}
        />
      )}

      {/* External agent banner — direct users to GEL lifecycle */}
      {isCurrentPhase && agent.isExternal && (
        <div style={{
          margin: '16px 0 0', padding: '12px 16px', borderRadius: 8,
          background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#a5b4fc',
        }}>
          <span style={{ fontSize: 16 }}>⇄</span>
          <span>
            External agents are advanced through the <strong>Protection → External Agents</strong> registry.
            Use the Build-Decide-Act lifecycle panel on that page.
          </span>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// HELPER: Relative Time Formatting
// =========================================================================
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'never';
  const now = new Date();
  const then = new Date(timestamp);
  const secondsAgo = Math.floor((now - then) / 1000);

  if (secondsAgo < 0) return 'in the future';
  if (secondsAgo < 60) return 'just now';
  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo} minute${minutesAgo > 1 ? 's' : ''} ago`;
  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
  const daysAgo = Math.floor(hoursAgo / 24);
  return `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
};

// =========================================================================
// HELPER: Heartbeat Status Color
// =========================================================================
const getHeartbeatStatus = (lastHeartbeatTime) => {
  if (!lastHeartbeatTime) return { color: '#ef4444', label: 'Red', bgColor: '#fee2e2' };
  const secondsAgo = Math.floor((new Date() - new Date(lastHeartbeatTime)) / 1000);
  if (secondsAgo < 60) return { color: '#57534e', label: 'Green', bgColor: '#e3f4f1' };
  if (secondsAgo <= 300) return { color: '#d97706', label: 'Yellow', bgColor: '#e3f4f1' };
  return { color: '#ef4444', label: 'Red', bgColor: '#fee2e2' };
};

// =========================================================================
// TEST RUN SIDE PANEL — Fixed right-side panel (Template AI Builder style)
// =========================================================================
const RBAC_ROLES = ['Super Admin', 'Org Admin', 'Tenant Admin', 'Developer', 'Viewer'];
const TR_GRADIENT = 'linear-gradient(135deg, #0d9488, #d4380d, #cf1322, #0d9488)';
const TR_GRADIENT_SOFT = 'linear-gradient(135deg, #0d948808, #d4380d05)';
const TR_PRIMARY = '#0d9488';  // Agentlets Red-Orange
const TR_SEND_BG = 'linear-gradient(135deg, #0d9488, #d4380d)';

function TestRunSidePanel({ agent, onClose, onTestPassed }) {
  const [messages, setMessages] = useState([]);  // { role: 'user'|'assistant'|'system', content, toolResults?, policyAction?, timestamp }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [simulateRole, setSimulateRole] = useState('');
  const [showRbac, setShowRbac] = useState(false);
  const [testPassed, setTestPassed] = useState(false);
  const chatEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Reset when agent changes
  React.useEffect(() => {
    setMessages([]);
    setInput('');
    setSending(false);
    setTestPassed(false);
    setSimulateRole('');
  }, [agent?.id]); // eslint-disable-line

  // Auto-scroll to bottom on new messages
  React.useEffect(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  }, [messages]);

  // Focus input on open
  React.useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const userMsg = { role: 'user', content: trimmed, timestamp: new Date().toISOString() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setSending(true);

    // Build history for embedded agentlet (exclude system messages)
    const history = newMessages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const raw = await testRunChat(agent.id, trimmed, history.slice(0, -1));
      // apiFetch wraps responses as { ok, data } — unwrap to get the actual agentlet response
      const res = raw?.data || raw || {};

      // Parse response — embedded agentlet returns { content, stop_reason, _toolResults, _policyResult }
      // Check if the response indicates an error
      if (res.ok === false || res.error) {
        setMessages(prev => [...prev, {
          role: 'system',
          content: res.error || 'Embedded agentlet returned an error.',
          isError: true,
          timestamp: new Date().toISOString(),
        }]);
        return;
      }

      const responseText = (res.content || [])
        .filter(c => c.type === 'text')
        .map(c => c.text)
        .join('\n') || res.response || res.text || JSON.stringify(res);

      // Detect policy enforcement from structured _policyResult or stop_reason
      // Three levels: L1 BLOCKED, LLM ENFORCED (agent refused via system prompt policy), L1 ALLOWED
      let policyAction = null;
      if (res.stop_reason === 'policy_blocked') {
        policyAction = 'BLOCKED';
      } else if (res._policyResult) {
        if (res._policyResult.blocked) {
          policyAction = 'BLOCKED';
        } else if (res._policyResult.llmEnforced) {
          policyAction = 'ENFORCED';
        } else {
          policyAction = 'ALLOWED';
        }
      }

      const assistantMsg = {
        role: 'assistant',
        content: responseText,
        toolResults: res._toolResults || null,
        policyAction,
        stopReason: res.stop_reason || null,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Mark test as passed only on genuine successful round trip
      if (!testPassed) {
        setTestPassed(true);
        if (onTestPassed) onTestPassed();
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: err?.message || 'Test run failed. Check that the platform inference (ANTHROPIC_API_KEY) is configured.',
        isError: true,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const panelWidth = expanded ? '48%' : 420;

  // Policy badge color helper
  const policyBadge = (action) => {
    const map = {
      BLOCKED: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', icon: <ShieldOff size={10} />, label: 'BLOCKED' },
      ENFORCED: { bg: '#e3f4f1', color: '#0a766b', border: '#f5b0a8', icon: <Shield size={10} />, label: 'POLICY ENFORCED' },
      ALLOWED: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', icon: <ShieldCheck size={10} />, label: 'ALLOWED' },
      FLAGGED: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', icon: <AlertTriangle size={10} />, label: 'FLAGGED' },
    };
    const s = map[action] || map.ALLOWED;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        marginBottom: 4,
      }}>
        {s.icon} {s.label || action}
      </span>
    );
  };

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: panelWidth,
      background: '#fafaf9', borderLeft: '1px solid #e7e5e4',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.10)', zIndex: 900,
      display: 'flex', flexDirection: 'column', transition: 'width 0.2s ease',
    }}>
      {/* ── Gradient Header ── */}
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)',
        background: TR_GRADIENT,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}>
            <FlaskConical size={17} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
              Test Run Center
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
              {agent?.name} — Embedded test with policy enforcement
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {testPassed && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'inline-flex', alignItems: 'center', gap: 3,
            }}>
              <CheckCircle size={10} /> PASSED
            </span>
          )}
          {/* RBAC toggle */}
          <button onClick={() => setShowRbac(!showRbac)}
            title="Simulate RBAC Role"
            style={{
              border: 'none', padding: 6, background: showRbac ? 'rgba(255,255,255,0.25)' : 'transparent',
              color: '#fff', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s',
            }}>
            <Shield size={14} />
          </button>
          <button onClick={() => setExpanded(!expanded)}
            style={{ border: 'none', padding: 6, background: 'transparent', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', borderRadius: 6 }}>
            {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={onClose}
            style={{ border: 'none', padding: 6, background: 'transparent', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', borderRadius: 6 }}>
            <X size={14} />
          </button>
        </div>
      </div>

      {/* RBAC Role Selector (collapsible) */}
      {showRbac && (
        <div style={{
          padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e7e5e4',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Shield size={12} style={{ color: TR_PRIMARY, flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: '#44403c', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            Simulate Role:
          </span>
          <select value={simulateRole} onChange={(e) => setSimulateRole(e.target.value)} style={{
            flex: 1, padding: '5px 8px', borderRadius: 6,
            border: '1px solid #d6d3d1', fontSize: 11, background: '#fff', color: '#1c1917',
          }}>
            <option value="">(current user role)</option>
            {RBAC_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      )}

      {/* ── Chat Messages Area ── */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '16px 14px',
        display: 'flex', flexDirection: 'column', gap: 12,
        background: TR_GRADIENT_SOFT,
      }}>
        {/* Welcome message when empty */}
        {messages.length === 0 && !sending && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', textAlign: 'center', padding: 24,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: TR_GRADIENT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, boxShadow: '0 4px 16px rgba(226,74,58,0.2)',
            }}>
              <MessageSquare size={26} color="#fff" />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1c1917', marginBottom: 6 }}>
              Chat with {agent?.name || 'your Agent'}
            </div>
            <div style={{ fontSize: 12, color: '#78716c', lineHeight: 1.6, maxWidth: 300 }}>
              Type a message below to interact with your agentlet exactly as end users would.
              Policy enforcement, guardrails, and tool use are all active.
            </div>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 16, justifyContent: 'center',
            }}>
              {['What can you do?', 'Show me patient records', 'Run a compliance check'].map((q, i) => (
                <button key={i} onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                  style={{
                    padding: '6px 12px', borderRadius: 20, border: '1px solid #d6d3d1',
                    background: '#fff', fontSize: 11, color: '#44403c', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.target.style.borderColor = '#0d9488'; e.target.style.color = '#0d9488'; }}
                  onMouseLeave={(e) => { e.target.style.borderColor = '#d6d3d1'; e.target.style.color = '#44403c'; }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((msg, i) => {
          if (msg.role === 'user') {
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  maxWidth: '80%', padding: '10px 14px', borderRadius: '16px 16px 4px 16px',
                  background: TR_SEND_BG, color: '#fff',
                  fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word',
                  boxShadow: '0 2px 8px rgba(226,74,58,0.15)',
                }}>
                  {msg.content}
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 4, textAlign: 'right' }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          }

          if (msg.role === 'system') {
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  maxWidth: '90%', padding: '8px 12px', borderRadius: 10,
                  background: msg.isError ? '#fef2f2' : '#f5f5f4',
                  border: `1px solid ${msg.isError ? '#fecaca' : '#e7e5e4'}`,
                  color: msg.isError ? '#dc2626' : '#78716c',
                  fontSize: 11, textAlign: 'center', lineHeight: 1.5,
                }}>
                  {msg.isError && <XCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                  {msg.content}
                </div>
              </div>
            );
          }

          // Assistant message
          return (
            <div key={i} style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: TR_GRADIENT,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginTop: 2,
              }}>
                <Bot size={14} color="#fff" />
              </div>
              <div style={{ maxWidth: '82%', display: 'flex', flexDirection: 'column' }}>
                {/* Policy badge */}
                {msg.policyAction && policyBadge(msg.policyAction)}

                {/* Tool results (if any) */}
                {msg.toolResults && msg.toolResults.length > 0 && (
                  <div style={{
                    marginBottom: 6, padding: '6px 10px', borderRadius: 8,
                    background: '#f5f5f4', border: '1px solid #e7e5e4',
                    fontSize: 10, color: '#78716c', fontFamily: 'monospace',
                  }}>
                    <Zap size={10} style={{ verticalAlign: 'middle', marginRight: 4, color: '#d97706' }} />
                    {msg.toolResults.length} tool{msg.toolResults.length !== 1 ? 's' : ''} executed
                    {msg.toolResults.map((tr, ti) => (
                      <div key={ti} style={{ marginTop: 3, paddingLeft: 14, borderLeft: '2px solid #e7e5e4' }}>
                        <span style={{ color: '#44403c', fontWeight: 600 }}>{tr.name || tr.tool || `Tool ${ti + 1}`}</span>
                        {tr.status && <span style={{ marginLeft: 6, color: tr.status === 'success' ? '#16a34a' : '#dc2626' }}>({tr.status})</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Message content */}
                <div style={{
                  padding: '10px 14px', borderRadius: '16px 16px 16px 4px',
                  background: '#fff', border: '1px solid #e7e5e4',
                  fontSize: 13, lineHeight: 1.6, color: '#1c1917',
                  wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                  {msg.content}
                </div>
                <div style={{ fontSize: 9, color: '#a8a29e', marginTop: 3, paddingLeft: 4 }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {msg.stopReason && msg.stopReason !== 'end_turn' && (
                    <span style={{ marginLeft: 8, fontFamily: 'monospace' }}>stop: {msg.stopReason}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing indicator */}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
              background: TR_GRADIENT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Bot size={14} color="#fff" />
            </div>
            <div style={{
              padding: '12px 16px', borderRadius: '16px 16px 16px 4px',
              background: '#fff', border: '1px solid #e7e5e4',
              display: 'flex', gap: 4, alignItems: 'center',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#a8a29e', animation: 'pulse 1.4s ease-in-out infinite' }} />
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#a8a29e', animation: 'pulse 1.4s ease-in-out 0.2s infinite' }} />
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#a8a29e', animation: 'pulse 1.4s ease-in-out 0.4s infinite' }} />
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* ── Input Area ── */}
      <div style={{
        padding: '12px 14px', borderTop: '1px solid #e7e5e4', background: '#fff',
        display: 'flex', gap: 8, alignItems: 'flex-end',
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Message ${agent?.name || 'agent'}...`}
          rows={1}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 12,
            border: '1px solid #d6d3d1', fontSize: 13, color: '#1c1917',
            background: '#fafaf9', resize: 'none', outline: 'none',
            lineHeight: 1.5, maxHeight: 120, overflow: 'auto',
            fontFamily: 'inherit', transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#0d9488'; }}
          onBlur={(e) => { e.target.style.borderColor = '#d6d3d1'; }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          style={{
            width: 40, height: 40, borderRadius: 12, border: 'none',
            background: (!input.trim() || sending) ? '#d6d3d1' : TR_SEND_BG,
            color: '#fff', cursor: (!input.trim() || sending) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.15s',
            opacity: (!input.trim() || sending) ? 0.5 : 1,
            boxShadow: (!input.trim() || sending) ? 'none' : '0 2px 8px rgba(226,74,58,0.2)',
          }}
        >
          <Send size={16} />
        </button>
      </div>

      {/* ── Footer Status ── */}
      <div style={{
        padding: '8px 16px', borderTop: '1px solid #f5f5f4', background: '#fafaf9',
        fontSize: 10, color: '#a8a29e', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#16a34a',
            display: 'inline-block',
          }} />
          Embedded agentlet ready
          {simulateRole && <span style={{ marginLeft: 8, color: TR_PRIMARY }}>Role: {simulateRole}</span>}
        </span>
        <span>
          {testPassed ? (
            <span style={{ color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <CheckCircle size={10} /> Test passed
            </span>
          ) : (
            <span>{messages.filter(m => m.role === 'user').length} message{messages.filter(m => m.role === 'user').length !== 1 ? 's' : ''} sent</span>
          )}
        </span>
      </div>

      {/* Inline animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// =========================================================================
// AUTO CREATE — Source picker + upload modals (v1 UI shell, 501 backend)
// =========================================================================
//
// Two-step modal sequence:
//
//   AutoCreateSourceModal
//      │  user picks one of: runbook | workflow | doc_library
//      ▼
//   AutoCreateUploadModal (sourceKind in props)
//      │  collect the source-specific input
//      ▼
//   POST /api/v1/auto-create  ──── 501 in v1 ────► render the
//                                                  "coming in 1.1"
//                                                  message in-place
//
// The submit returns 501 today (see AutoCreateController). The upload
// modal renders the structured response body's `message` and `eta`
// directly so the user sees an explanation, not an error toast.
//
// Reference: RUNBOOK_TO_AGENT_DESIGN.md — locks the UX shape for v1
// so the 1.1 implementation is a backend wiring change.
// =========================================================================

const AUTO_CREATE_SOURCES = [
  {
    kind: 'runbook',
    title: 'From a Runbook',
    description: 'Upload a Word, PDF, or HTML runbook. The pipeline extracts steps, links them to Skill Registry entries, and emits a fully protected agent.',
    icon: BookOpen,
    accent: '#0d9488',
    badge: 'Recommended',
  },
  {
    kind: 'workflow',
    title: 'From an Existing Workflow',
    description: 'Pick one of your tenant\'s active workflows. The pipeline re-derives skills against the current Skill Registry and attaches them to a fresh agent stub.',
    icon: GitBranch,
    accent: '#1e40af',
  },
  {
    kind: 'doc_library',
    title: 'From the Doc Library',
    description: 'Select a set of policy / SOP documents already in your tenant\'s policy library. RAG-ranks them for skill-relevant chunks and dedupes against the Registry.',
    icon: FileText,
    accent: '#085041',
  },
];

function AutoCreateSourceModal({ onClose, onPick }) {
  return (
    <Modal
      isOpen={true}
      title="Auto Create — Pick a source"
      onClose={onClose}
      wide
      actions={<button onClick={onClose} style={btnBase}>Cancel</button>}
    >
      <p style={{ fontSize: 13, color: '#52525b', margin: '0 0 16px 0', lineHeight: 1.55 }}>
        Auto Create takes you from a source artefact to a fully protected agent
        in one step — extracted skills land in the agent's Build phase pre-bound
        to Skill Registry entries, ready for review.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {AUTO_CREATE_SOURCES.map((src) => {
          const Icon = src.icon;
          return (
            <button
              key={src.kind}
              type="button"
              onClick={() => onPick(src.kind)}
              style={{
                width: '100%', textAlign: 'left',
                background: '#fff',
                border: src.kind === 'runbook' ? '1px solid #a5d8d0' : '1px solid #e7e5e4',
                borderLeft: `3px solid ${src.accent}`,
                borderRadius: 8, padding: '14px 16px',
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', gap: 14, alignItems: 'flex-start',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: '#fafaf9', border: '1px solid #e7e5e4',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icon size={18} style={{ color: src.accent }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 650, color: '#1c1917', margin: 0 }}>
                    {src.title}
                  </h4>
                  {src.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                      background: '#e3f4f1', color: '#9a2f23', border: '1px solid #a5d8d0',
                      textTransform: 'uppercase', letterSpacing: 0.6,
                    }}>{src.badge}</span>
                  )}
                </div>
                <p style={{ fontSize: 12, color: '#71717a', margin: 0, lineHeight: 1.5 }}>
                  {src.description}
                </p>
              </div>
              <ChevronRight size={14} style={{ color: '#a8a29e', marginTop: 14, flexShrink: 0 }} />
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function AutoCreateUploadModal({ sourceKind, onBack, onClose, onRunStarted }) {
  const src = AUTO_CREATE_SOURCES.find((s) => s.kind === sourceKind) || AUTO_CREATE_SOURCES[0];

  // Per-source-kind input state. Locked to a single string field for v1
  // so the request body shape is stable across all three kinds:
  //   { sourceKind, sourceRef, hints? }
  const [sourceRef, setSourceRef] = React.useState('');
  const [agentName, setAgentName] = React.useState('');

  // Mockup-aligned metadata fields. ALL optional except the file pick;
  // the pipeline's INTENT phase fills in sensible defaults if the user
  // leaves them blank. We pass selections through the request body's
  // hints object so the backend's StartRunRequest doesn't need a schema
  // bump for every new optional field.
  const [agentType, setAgentType] = React.useState('cognitive');
  const [agentCategory, setAgentCategory] = React.useState('');
  const [selectedFrameworkIds, setSelectedFrameworkIds] = React.useState([]);
  const [frameworkOptions, setFrameworkOptions] = React.useState([]);
  const [loadingFrameworks, setLoadingFrameworks] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingFrameworks(true);
      try {
        const res = await ccListFrameworks({});
        if (cancelled) return;
        const list = res?.ok && Array.isArray(res.data) ? res.data
                    : Array.isArray(res?.data?.frameworks) ? res.data.frameworks
                    : (Array.isArray(res) ? res : []);
        // Keep only the lightweight identity each chip needs to render.
        setFrameworkOptions(list.map((f) => ({
          id:        f.id,
          shortName: f.shortName || f.code || f.name,
          name:      f.name || f.shortName,
          version:   f.version,
        })));
      } catch (_) {
        // Frameworks failure is non-fatal — the chips just don't render.
      } finally {
        if (!cancelled) setLoadingFrameworks(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleFramework = (id) => {
    setSelectedFrameworkIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  // Workflow picker source: only fetched when sourceKind === 'workflow'.
  const [workflowOptions, setWorkflowOptions] = React.useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = React.useState(false);

  React.useEffect(() => {
    if (sourceKind !== 'workflow') return;
    let cancelled = false;
    (async () => {
      setLoadingWorkflows(true);
      try {
        // Empty scope intentional — backend fills from session JWT for
        // tenant users. Outer error handling below silences a 400 if the
        // controller rejects empty scope; the picker just shows empty.
        const res = await getWorkflows({});
        if (cancelled) return;
        const list = res?.ok && Array.isArray(res.data) ? res.data
                    : (Array.isArray(res) ? res : []);
        setWorkflowOptions(list);
      } catch (_) {
        // swallow
      } finally {
        if (!cancelled) setLoadingWorkflows(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceKind]);

  // Runbook upload state — only used when sourceKind === 'runbook'.
  // The flow is: pick/drop file → POST to /api/v1/auto-create/upload →
  // backend validates MIME + size, returns a synthetic staging id (no
  // persistence in v1) → we store the id in sourceRef and call autoCreate.
  // Errors from the upload (oversized, wrong MIME) surface here, not in
  // the main submitInfo, because they're fixable by re-picking a file.
  const fileInputRef = React.useRef(null);
  const [pickedFile, setPickedFile] = React.useState(null);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [dragActive, setDragActive] = React.useState(false);

  const onPickFile = async (file) => {
    if (!file) return;
    setUploadError(null);
    setPickedFile(file);
    setUploadingFile(true);
    try {
      const res = await stageAutoCreateRunbook(file);
      // Backend returns { stagingId: 'upload://staging/<uuid>', ... }
      // — that's exactly the shape sourceRef expects.
      if (!res || !res.stagingId) {
        throw new Error('Upload acknowledged but server returned no stagingId.');
      }
      setSourceRef(res.stagingId);
    } catch (e) {
      setUploadError(e?.message || 'Upload failed.');
      setPickedFile(null);
      setSourceRef('');
    } finally {
      setUploadingFile(false);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onPickFile(file);
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // ── Submit state machine ──────────────────────────────────────────
  // phase transitions:
  //   idle              ─── click Run ──────►  submitting
  //   submitting        ─── runId returned ─►  hands off to AutoCreateWizard (modal closes)
  //   submitting        ─── legacy { agentId } ──► done_success  (terminal — older sync backends)
  //   submitting        ─── error  ─────────►  idle  (with submitError)
  //
  // The `done_success` terminal state remains for legacy synchronous
  // backends that return { agentId, agentName } directly. Today's
  // pipeline returns { runId } and the wizard handles the live stream.
  // If neither is present we fall through to a generic error — never
  // a silent dead-end.
  const [phase, setPhase] = React.useState('idle');
  const [submitError, setSubmitError] = React.useState(null);
  // Holds the structured response on the legacy success path:
  // { agentId, agentName, status } drives the View-Agent deep-link.
  const [submitInfo, setSubmitInfo] = React.useState(null);

  const isSubmitting = phase === 'submitting';
  const isTerminal   = phase === 'done_success';
  // Lock every interactive input the moment we start submitting AND
  // keep them locked through terminal states. Re-editing after success
  // would only enable a re-submission, which we deliberately disallow.
  const isLocked     = isSubmitting || isTerminal;
  const canSubmit    = phase === 'idle' && sourceRef.trim().length > 0;

  const handleSubmit = async () => {
    setPhase('submitting');
    setSubmitError(null);
    setSubmitInfo(null);
    try {
      // Hints carry every optional metadata field the user filled in;
      // the backend pipeline reads them in INTENT (proposedAgentName,
      // type, category) and the post-commit hook (framework binding).
      const hints = {};
      if (agentName)        hints.proposedAgentName = agentName.trim();
      if (agentType)        hints.type              = agentType;
      if (agentCategory)    hints.category          = agentCategory;
      if (selectedFrameworkIds.length > 0) hints.frameworkIds = selectedFrameworkIds;

      const res = await autoCreateAgent({
        sourceKind,
        sourceRef: sourceRef.trim(),
        proposedAgentName: agentName ? agentName.trim() : undefined,
        hints: Object.keys(hints).length > 0 ? hints : undefined,
      });
      // apiFetch envelope: { ok, data: <body> }. Unwrap to the actual body.
      const payload = res?.ok ? res.data : res;

      // 1.1 LIVE PIPELINE PATH — backend returns { runId, status, eventsUrl }.
      // Hand off to AutoCreateWizard via onRunStarted; this modal closes
      // and the wizard takes over with the live SSE stream + screens.
      if (payload && payload.runId) {
        onRunStarted?.(payload.runId);
        return;
      }

      // Fallback: legacy synchronous response carrying { agentId, agentName }.
      // Older backend deployments returned this shape directly. Today's
      // pipeline ALWAYS goes through the runId path; if neither is present
      // we treat it as a malformed response and surface a clear error.
      if (payload && payload.agentId) {
        setSubmitInfo({ kind: 'success', payload });
        setPhase('done_success');
        return;
      }
      setSubmitError('Server response had no runId or agentId — cannot proceed.');
      setPhase('idle');
    } catch (e) {
      // Real error — let the user fix and retry. Roll back to idle so
      // the form unlocks and the Run button comes back.
      setSubmitError(e?.message || 'Auto Create failed.');
      setPhase('idle');
    }
  };

  // Deep-link to the newly provisioned agent's Build phase. Only present
  // in the 1.1 success branch where the response carries an agentId.
  const provisionedAgent = phase === 'done_success' ? submitInfo?.payload : null;
  const provisionedAgentLink = provisionedAgent?.agentId
    ? `/agents?agentId=${encodeURIComponent(provisionedAgent.agentId)}`
    : null;

  const Icon = src.icon;

  return (
    <Modal
      isOpen={true}
      title={`Auto Create — ${src.title}`}
      onClose={onClose}
      wide
      actions={
        // Action-bar varies by phase:
        //   idle / submitting → Back · Close · Begin Auto Creation
        //   done_success      → Close · View {agentName} in Build → (deep-link)
        //   (the success runId path closes this modal entirely and the
        //    AutoCreateWizard takes over — so done_success here is only
        //    the legacy synchronous-backend path.)
        // Hiding Back + Run after a terminal state is the key fix: it
        // prevents the user from double-submitting and creating a duplicate
        // agent (1.1) or logging a duplicate 501 (v1).
        isTerminal ? (
          <>
            <button onClick={onClose} style={btnBase}>Close</button>
            {provisionedAgentLink && (
              <a
                href={provisionedAgentLink}
                style={{ ...btnPrimary, textDecoration: 'none' }}
              >
                View {provisionedAgent.agentName || 'agent'} in Build →
              </a>
            )}
          </>
        ) : (
          <>
            <button onClick={onBack} disabled={isSubmitting} style={btnBase}>Back</button>
            <button onClick={onClose} disabled={isSubmitting} style={btnBase}>Close</button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                ...btnPrimary,
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {isSubmitting ? (
                <>
                  <RotateCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />
                  Submitting…
                </>
              ) : 'Begin Auto Creation'}
            </button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Source identity strip */}
        <div style={{
          background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 6,
          padding: 12, display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <Icon size={18} style={{ color: src.accent }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 650, color: '#1c1917' }}>
              {src.title}
            </div>
            <div style={{ fontSize: 11, color: '#78716c', marginTop: 2 }}>
              {src.description}
            </div>
          </div>
        </div>

        {/* Source-specific input — runbook: drag-and-drop file upload.
             v1 backend validates MIME + size and returns a synthetic
             staging id; the file itself is NOT persisted. The 1.1
             pipeline wires real storage to the tenant's secure file-
             staging bucket — same endpoint contract, same UX. */}
        {sourceKind === 'runbook' && (
          <div>
            <label style={labelStyle}>Runbook file <span style={{ color: '#085041' }}>*</span></label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.html,.htm,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/html,text/plain"
              style={{ display: 'none' }}
              disabled={isLocked}
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
            {!pickedFile && !uploadingFile && (
              <div
                onClick={() => !isLocked && fileInputRef.current?.click()}
                onDragOver={isLocked ? undefined : onDragOver}
                onDragLeave={isLocked ? undefined : onDragLeave}
                onDrop={isLocked ? undefined : onDrop}
                style={{
                  border: dragActive
                    ? '2px dashed #0d9488'
                    : '2px dashed #d6d3d1',
                  borderRadius: 8,
                  padding: '28px 20px',
                  textAlign: 'center',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: dragActive ? '#fef6f4' : '#fafaf9',
                  opacity: isLocked ? 0.55 : 1,
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <Upload size={22} style={{ color: dragActive ? '#0d9488' : '#a8a29e', marginBottom: 8 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1917', marginBottom: 4 }}>
                  {dragActive ? 'Drop your runbook here' : 'Drop a runbook here, or click to browse'}
                </div>
                <div style={{ fontSize: 11, color: '#78716c' }}>
                  PDF, Word (.docx, .doc), HTML, or plain text · up to 50 MB
                </div>
              </div>
            )}

            {uploadingFile && (
              <div style={{
                border: '1px solid #e7e5e4', borderRadius: 8,
                padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
                background: '#fafaf9',
              }}>
                <RotateCw size={16} style={{ animation: 'spin 1s linear infinite', color: '#0d9488' }} />
                <div style={{ fontSize: 12, color: '#52525b' }}>
                  Validating & staging <strong>{pickedFile?.name}</strong>…
                </div>
              </div>
            )}

            {pickedFile && !uploadingFile && !uploadError && (
              <div style={{
                border: '1px solid #bbf7d0', borderRadius: 8,
                padding: '12px 14px', background: '#f0fdf4',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <FileText size={18} style={{ color: '#166534', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#14532d', marginBottom: 2,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {pickedFile.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#166534' }}>
                    {formatBytes(pickedFile.size)} · staged for Auto Create
                  </div>
                </div>
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => {
                    setPickedFile(null);
                    setSourceRef('');
                    setUploadError(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  style={{
                    background: 'transparent', border: 'none',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    color: '#166534', padding: 4, borderRadius: 4,
                    display: 'inline-flex', alignItems: 'center',
                    opacity: isLocked ? 0.4 : 1,
                  }}
                  title={isLocked ? 'Locked after submit' : 'Remove this file'}
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {uploadError && (
              <div style={{
                border: '1px solid #a5d8d0', borderRadius: 8,
                padding: '12px 14px', background: '#e3f4f1',
                display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8,
              }}>
                <AlertTriangle size={14} style={{ color: '#085041', flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 650, color: '#7a2820', marginBottom: 2 }}>
                    Couldn't stage the runbook
                  </div>
                  <div style={{ fontSize: 11, color: '#7a2820', lineHeight: 1.5 }}>
                    {uploadError}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadError(null);
                      setPickedFile(null);
                      setSourceRef('');
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    style={{
                      ...btnBase, marginTop: 8,
                      borderColor: '#a5d8d0', color: '#9a2f23', background: '#fff',
                    }}
                  >
                    Try a different file
                  </button>
                </div>
              </div>
            )}

            <p style={{ fontSize: 10, color: '#a8a29e', margin: '8px 2px 0 2px', lineHeight: 1.5, fontStyle: 'italic' }}>
              v1 validates the file shape and generates a staging id, but does not persist the bytes.
              Actual file storage lands with the 1.1 Auto Create pipeline.
            </p>
          </div>
        )}

        {sourceKind === 'workflow' && (
          <div>
            <label style={labelStyle}>Workflow <span style={{ color: '#085041' }}>*</span></label>
            {loadingWorkflows ? (
              <div style={{ fontSize: 12, color: '#78716c', padding: 8 }}>
                Loading workflows…
              </div>
            ) : workflowOptions.length === 0 ? (
              <div style={{
                background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 6,
                padding: 10, fontSize: 12, color: '#78716c',
              }}>
                No workflows visible to this scope. Build one in the Workflow Canvas first.
              </div>
            ) : (
              <select
                value={sourceRef}
                onChange={(e) => setSourceRef(e.target.value)}
                disabled={isLocked}
                style={{ ...inputStyle, padding: '8px 10px', opacity: isLocked ? 0.6 : 1 }}
              >
                <option value="">Select a workflow…</option>
                {workflowOptions.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}{w.versionLabel ? ` · ${w.versionLabel}` : (w.version ? ` · v${w.version}` : '')}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {sourceKind === 'doc_library' && (
          <div>
            <label style={labelStyle}>Policy doc IDs <span style={{ color: '#085041' }}>*</span></label>
            <p style={{ fontSize: 11, color: '#78716c', margin: '0 0 8px 0', lineHeight: 1.5 }}>
              Comma-separated list of policy_doc IDs from your tenant's library.
              In 1.1 this becomes a multi-select against the Doc Library page.
            </p>
            <input
              type="text"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              placeholder="doc_abc123, doc_def456, doc_ghi789"
              disabled={isLocked}
              style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1 }}
            />
          </div>
        )}

        {/* Optional agent name override.
             Auto-Created agents always get the AutoLets- prefix so they
             sort into the dedicated Overview bucket. We show a live
             preview of the final name so the user understands the
             convention. Backend normalises again defensively. */}
        <div>
          <label style={labelStyle}>
            Proposed agent name <span style={{ color: '#a8a29e', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            type="text"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder="The pipeline will derive a name if you leave this blank"
            disabled={isLocked}
            style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1 }}
          />
          <div style={{ fontSize: 11, color: '#78716c', marginTop: 6 }}>
            Final agent name:{' '}
            <code style={{ fontSize: 11, color: '#9a2f23',
              background: '#e3f4f1', border: '1px solid #a5d8d0',
              padding: '1px 6px', borderRadius: 4 }}>
              {agentName && agentName.trim()
                ? (agentName.trim().startsWith('AutoLets-')
                    ? agentName.trim()
                    : `AutoLets-${agentName.trim()}`)
                : 'AutoLets-<derived from runbook>'}
            </code>
          </div>
        </div>

        {/* Type + Category — two-column row mirroring the approved mockup.
             Both optional — the pipeline's INTENT phase backfills sensible
             defaults if left blank; we expose them here so power users can
             pin a category/type up-front without round-tripping. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Type</label>
            <select
              value={agentType}
              onChange={(e) => setAgentType(e.target.value)}
              disabled={isLocked}
              style={{ ...inputStyle, padding: '8px 10px', opacity: isLocked ? 0.6 : 1 }}
            >
              <option value="cognitive">Cognitive</option>
              <option value="reactive">Reactive</option>
              <option value="autonomous">Autonomous</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>
              Category <span style={{ color: '#a8a29e', fontWeight: 400 }}>(optional)</span>
            </label>
            <select
              value={agentCategory}
              onChange={(e) => setAgentCategory(e.target.value)}
              disabled={isLocked}
              style={{ ...inputStyle, padding: '8px 10px', opacity: isLocked ? 0.6 : 1 }}
            >
              <option value="">— Pipeline will infer —</option>
              <option value="incident response">Incident Response</option>
              <option value="email security">Email Security</option>
              <option value="vulnerability management">Vulnerability Management</option>
              <option value="cloud security">Cloud Security</option>
              <option value="identity">Identity & Access</option>
              <option value="insider threat">Insider Threat</option>
              <option value="compliance">Compliance / GRC</option>
              <option value="it operations">IT Operations</option>
              <option value="change management">Change Management</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Compliance frameworks — multi-select chips. Loaded from the
             Compliance Center; NOT pre-bound. Empty selection = no
             framework binding (the pipeline can still recommend one in
             the post-commit step the user reviews). */}
        <div>
          <label style={labelStyle}>
            Compliance frameworks to bind{' '}
            <span style={{ color: '#a8a29e', fontWeight: 400 }}>
              (optional, multi-select)
            </span>
          </label>
          <p style={{ fontSize: 11, color: '#78716c', margin: '0 0 8px 0', lineHeight: 1.5 }}>
            Toggle the frameworks this agent should bind to. Bindings drive the Compliance Center's
            evidence rows. Skip if you'd rather decide later — the agent's lifecycle owner can bind
            frameworks any time from the Compliance tab.
          </p>
          {loadingFrameworks ? (
            <div style={{ fontSize: 12, color: '#78716c', padding: 8 }}>Loading frameworks…</div>
          ) : frameworkOptions.length === 0 ? (
            <div style={{
              background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 6,
              padding: '8px 10px', fontSize: 11, color: '#78716c',
            }}>
              No frameworks available for binding in this tenant.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {frameworkOptions.map((f) => {
                const active = selectedFrameworkIds.includes(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => !isLocked && toggleFramework(f.id)}
                    disabled={isLocked}
                    title={f.name}
                    style={{
                      fontFamily: 'inherit',
                      padding: '6px 12px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      background: active ? '#e3f4f1' : '#fff',
                      border: active ? '1px solid #a5d8d0' : '1px solid #e7e5e4',
                      color: active ? '#9a2f23' : '#52525b',
                      opacity: isLocked ? 0.6 : 1,
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {f.shortName}
                    {active && <CheckCircle size={12} style={{ color: '#9a2f23' }} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Trust statement — the most important UI element on this screen
             per the mockup spec. Communicates exactly what happens to the
             agent post-commit and what the lifecycle gates require. */}
        <div style={{
          background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 75%)',
          border: '1px solid #bfdbfe', borderRadius: 8,
          padding: '12px 14px', fontSize: 12, color: '#1e3a8a', lineHeight: 1.6,
        }}>
          <strong style={{ color: '#1e40af' }}>This agent will be created in BUILD phase.</strong>{' '}
          All skills extracted will require <strong>evalScore ≥ 0.90</strong> to advance to DECIDE.
          You'll review every skill, guardrail, and framework binding before anything runs.
          Cryptographic provenance is recorded at every step.
        </div>

        {/* Provisioning spinner — shown while the request is in flight.
             v1's 501 returns instantly so this barely flashes; in 1.1 the
             pipeline can take 30–60s and the user needs to know the agent
             is being built, not that the modal froze. */}
        {isSubmitting && (
          <div style={{
            background: '#fafaf9', border: '1px solid #e7e5e4', borderRadius: 8,
            padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'center',
          }}>
            <RotateCw size={16} style={{ animation: 'spin 1s linear infinite', color: '#0d9488', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 650, color: '#1c1917' }}>
                Provisioning your agent…
              </div>
              <div style={{ fontSize: 11, color: '#78716c', marginTop: 2, lineHeight: 1.5 }}>
                Extracting steps, resolving against the Skill Registry, emitting an AgentDefinition,
                running the structural validator. Please don’t close this dialog.
              </div>
            </div>
          </div>
        )}

        {/* Real error — only shown when phase rolled back to idle.
             Terminal pending / success states render their own panels below. */}
        {phase === 'idle' && submitError && (
          <div style={{
            background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 8,
            padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <AlertTriangle size={14} style={{ color: '#085041', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 650, color: '#7a2820', marginBottom: 4 }}>
                Auto Create failed
              </div>
              <div style={{ fontSize: 11, color: '#7a2820', lineHeight: 1.5 }}>
                {submitError}
              </div>
            </div>
          </div>
        )}

        {/* Terminal: 1.1 success — green panel with deep-link rendered in
             the action bar (View {agentName} in Build →). */}
        {phase === 'done_success' && submitInfo?.kind === 'success' && (
          <div style={{
            background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 8,
            padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <CheckCircle size={16} style={{ color: '#166534', flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: '#14532d', marginBottom: 4 }}>
                  Auto Create accepted
                  {provisionedAgent?.agentName ? ` · ${provisionedAgent.agentName}` : ''}
                </div>
                <p style={{ fontSize: 12, color: '#166534', margin: 0, lineHeight: 1.55 }}>
                  Your agent is provisioning in the Build phase. Click{' '}
                  <strong>View in Build</strong> below to inspect the materialised skill graph,
                  validator findings, and proposed bindings before promoting through DECIDE.
                </p>
                {provisionedAgent?.status && provisionedAgent.status !== 'build' && (
                  <p style={{ fontSize: 11, color: '#166534', margin: '6px 0 0 0', fontStyle: 'italic' }}>
                    Current status: <code>{provisionedAgent.status}</code> — the link will
                    take you there as soon as the pipeline completes.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// =========================================================================
// OPEN-IN-CANVAS CTA — Agent → Canvas forward materialisation
// =========================================================================
//
// Renders a prominent banner at the top of the ACT-phase panel with a
// [Open in Canvas to Test & Verify] button. Click handler:
//
//   1. POST /api/v1/agents/{id}/canvas         → materialise (preview)
//   2. POST /api/v1/workflows                  → persist as draft workflow
//   3. window.location.assign('/canvas?agentId=…&workflowId=…')
//      so the Canvas page mounts with the AgentCanvasLinkBanner.
//
// Failure modes surfaced inline:
//   - 404 (agent or definition not found) → red banner
//   - usedFallback=true → amber hint that the canvas was derived from
//     legacy Skill rows and may have unresolved registryIds
//   - validation.blocking → red banner with the issue list
//   - workflow create failed → red banner with the API message
//
// Permission gate: agent.read covers the materialise; workflow.create
// covers the persist. We render disabled if either is missing — prevents
// the click → 403 → unhelpful generic error path.
// =========================================================================

function OpenInCanvasCTA({ agent, canWrite, hasPermission }) {
  const [busy, setBusy]       = React.useState(false);
  const [error, setError]     = React.useState(null);
  const [hint, setHint]       = React.useState(null);

  const canRead   = hasPermission('agent.read');
  const canCreate = hasPermission('workflow.create');
  const enabled   = !busy && canRead && canCreate && agent?.id;

  const handleOpen = async () => {
    if (!agent?.id) return;
    setBusy(true);
    setError(null);
    setHint(null);
    try {
      // Step 1 — materialise. apiFetch envelope is { ok, data }.
      const res = await materializeAgentCanvas(agent.id, { mode: 'preview' });
      const payload = res?.ok ? res.data : res;
      if (!payload || !payload.canvasDefinition) {
        throw new Error('Materialise returned no canvasDefinition.');
      }
      const validation = payload.validation;
      if (validation && validation.valid === false) {
        const first = (validation.blocking && validation.blocking[0]) || null;
        throw new Error(
          'Materialised canvas failed validation: '
          + (first ? first.message : `${validation.blocking?.length ?? '?'} blocking issue(s)`));
      }
      if (payload.usedFallback) {
        // Non-blocking — proceed but tell the user.
        setHint(
          'This agent had no AgentDefinition.skills, so the canvas was '
          + 'derived from legacy Skill rows. Some nodes may carry '
          + 'unresolvedRegistry: true until you bind them via '
          + 'PUT /agents/{id}/definition.');
      }

      // Step 2 — idempotent persist. Look for an existing sandbox
      // workflow tied to this agent FIRST and update it instead of
      // creating yet another row. Without this, every click of "Test
      // & Verify in Canvas" creates another "Test · {agent} · {ts}"
      // workflow, polluting the picker and confusing the agent's
      // CEG selection (the most-recent-by-updated-at sandbox ends up
      // outranking the canonical CEG).
      //
      // Sandbox detection mirrors CanvasPageV3's CEG selector: name
      // prefix "Test · " is the cheap, reliable signal we control on
      // both ends.
      const STABLE_NAME = `Test · ${agent.name || 'Agent'}`;
      const STABLE_DESC = `Materialised from agent '${agent.name}' AgentDefinition for sandbox testing.`;

      let existingId = null;
      try {
        const listRes = await getWorkflows({ agentId: agent.id });
        const list = listRes?.ok ? (listRes.data || []) : (Array.isArray(listRes) ? listRes : []);
        // Most recent sandbox wins (typically there's only one after
        // this fix lands; existing duplicates from before the fix
        // collapse onto the latest on next click).
        const sandbox = list
          .filter((w) => w && w.id && typeof w.name === 'string' && w.name.startsWith('Test · '))
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0];
        if (sandbox) existingId = sandbox.id;
      } catch (lookupErr) {
        // Lookup failure shouldn't block the flow — fall through to
        // create. The list endpoint is independent from the create
        // endpoint; if both are down, create will surface its own
        // error.
        // eslint-disable-next-line no-console
        console.warn('[OpenInCanvasCTA] sandbox lookup failed; falling back to create', lookupErr);
      }

      const wfBody = {
        name:        STABLE_NAME,
        description: STABLE_DESC,
        status:      'draft',
        triggerType: 'manual',
        agentId:     agent.id,
        orgId:       agent.orgId,
        tenantId:    agent.tenantId,
        definition:  payload.canvasDefinition,
      };

      let wfId;
      if (existingId) {
        // Refresh the existing sandbox in place — keeps a single
        // canonical Test workflow per agent that always reflects the
        // current AgentDefinition.
        const updRes = await updateWorkflow(existingId, wfBody);
        const upd = updRes?.ok ? updRes.data : updRes;
        wfId = upd?.id || upd?.workflow?.id || existingId;
        if (!wfId) {
          throw new Error('Workflow update returned no id.');
        }
      } else {
        const wfRes = await createWorkflow(wfBody);
        const wf = wfRes?.ok ? wfRes.data : wfRes;
        // Robust id parsing — backend has shipped two response shapes
        // historically (`Workflow` directly and `{ workflow: ... }`
        // envelope). Walk both before giving up.
        wfId = wf?.id || wf?.workflow?.id || wf?.data?.id;
        if (!wfId) {
          throw new Error('Workflow create returned no id.');
        }
      }

      // Step 3 — navigate. Using window.location so the Canvas page
      // does its own auth + load fresh, exactly as if the user had
      // followed a deep link.
      const url = `/canvas?workflowId=${encodeURIComponent(wfId)}&agentId=${encodeURIComponent(agent.id)}`;
      window.location.assign(url);
    } catch (e) {
      setError(e?.message || 'Open in Canvas failed.');
    } finally {
      setBusy(false);
    }
  };

  const tooltip = !canRead
    ? "Requires 'agent.read' permission"
    : !canCreate
      ? "Requires 'workflow.create' permission to persist the test workflow"
      : '';

  return (
    <div style={{
      marginBottom: 24,
      padding: '16px 18px',
      borderRadius: 10,
      border: '1px solid #a5d8d0',
      background: 'linear-gradient(180deg, #e3f4f1 0%, #fff 75%)',
      display: 'flex', alignItems: 'flex-start', gap: 14,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: '#fff', border: '1px solid #a5d8d0',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <GitBranch size={18} style={{ color: '#0d9488' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 650, color: '#1c1917', marginBottom: 3 }}>
          Test &amp; Verify in Canvas
        </div>
        <p style={{ fontSize: 12, color: '#52525b', margin: '0 0 10px 0', lineHeight: 1.5 }}>
          Materialise this agent's AgentDefinition into a runnable Canvas DAG and open it in the
          sandbox so you can dry-run the skill graph before bumping a publish.
        </p>
        {error && (
          <div style={{
            background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 6,
            padding: '8px 12px', fontSize: 12, color: '#7a2820', marginBottom: 10,
            display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <AlertTriangle size={14} style={{ color: '#085041', flexShrink: 0, marginTop: 2 }} />
            <div><strong>Couldn't open in Canvas.</strong> {error}</div>
          </div>
        )}
        {hint && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
            padding: '8px 12px', fontSize: 11, color: '#854d0e', marginBottom: 10, lineHeight: 1.5,
          }}>
            {hint}
          </div>
        )}
        <button
          type="button"
          onClick={handleOpen}
          disabled={!enabled}
          title={tooltip}
          style={{
            ...btnPrimary,
            background: enabled
              ? 'linear-gradient(135deg, #0d9488 0%, #c43d2e 100%)'
              : 'linear-gradient(135deg, #d4d4d8 0%, #a1a1aa 100%)',
            cursor: enabled ? 'pointer' : 'not-allowed',
            opacity: enabled ? 1 : 0.7,
          }}
        >
          {busy ? (
            <>
              <RotateCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
              Opening Canvas…
            </>
          ) : (
            <>
              <ExternalLink size={14} />
              Open in Canvas to Test &amp; Verify
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// ACT PHASE PANEL
// =========================================================================
function ActPhasePanel({
  agent, agents, users, groups, assignments, publications, executions,
  loadingExecs, canWrite, hasPermission, isCurrentPhase, onAddAssignment,
  onRemoveAssignment, onPublish, onRollback, onRefreshExecs, publishing,
  skillReviews,
  agentlets, loadingAgentlets, onRefreshAgentlets, onPingAgentlet,
  pingingAgentlet, agentletPage, onAgentletPageChange, agentletsPerPage,
  onAddSkill, onImportFromConnector, onEditSkill, onDeleteSkill, onUnpublishSkill, onAdvanceSkill, onRegressSkill, onPushSkill,
  pubPage = 0, pubTotalPages = 1, onPubPageChange,
}) {
  // Pagination state hooks
  const assignmentPagination = usePaginationState(10);
  const publicationPagination = usePaginationState(10);
  const executionPagination = usePaginationState(10);

  const canPublish = hasPermission('publish.create');
  const canRollback = hasPermission('publish.rollback');
  const canManageAssign = hasPermission('assignment.manage');
  // V169 / Wave 3 — AutoLet immutability: every skill mutation
  // permission is AND'd with !isRunbookManaged so the buttons disappear
  // on AutoLets regardless of the user's underlying RBAC. The backend
  // 409s on the same calls (per W1.4) so this is purely UX — but
  // surfacing buttons that always error is worse than hiding them.
  const isAutoLet = agent?.isRunbookManaged === true;
  const canCreateSkill = hasPermission('skill.create') && !isAutoLet;
  const canEditSkillPerm = hasPermission('skill.edit') && !isAutoLet;
  const canDeleteSkillPerm = hasPermission('skill.delete') && !isAutoLet;

  const resolveAssignmentName = (a) => {
    // Prefer enriched name from backend (userName / groupName)
    if (a.userId) {
      if (a.userName) return a.userName;
      const u = (users || []).find((u) => u.id === a.userId);
      return u ? (u.displayName || u.name || u.email) : a.userId;
    }
    if (a.groupId) {
      if (a.groupName) return a.groupName;
      const g = (groups || []).find((g) => g.id === a.groupId);
      return g ? g.name : a.groupId;
    }
    return '—';
  };

  const resolveUserName = (uid, enrichedName) => {
    if (!uid) return '—';
    // Prefer enriched name from backend if provided
    if (enrichedName) return enrichedName;
    const u = (users || []).find((u) => u.id === uid);
    return u ? (u.displayName || u.name || u.email) : uid?.substring(0, 8) + '...';
  };

  // v5.3.0 — Filter assignments to current agent only (rollback: remove agentAssignments, use assignments directly)
  const agentAssignments = (assignments || []).filter((a) => a.agentId === agent.id);
  const assignedCount = agentAssignments.length;
  const userCount = agentAssignments.filter((a) => a.userId).length;
  const groupCount = agentAssignments.filter((a) => a.groupId).length;

  const allSkills = agent.skills || [];
  const actReadySkills = allSkills.filter((s) => s.skillPhase === 'act');

  // Agentlet status colors
  const agentletStatusCfg = {
    connected: { label: 'Connected', color: '#0d9488', bg: '#e3f4f1', border: '#a5d8d0' },
    stale: { label: 'Stale', color: '#d97706', bg: '#e3f4f1', border: '#a5d8d0' },
    disconnected: { label: 'Disconnected', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  };

  // Publication status config
  const pubStatusConfig = {
    published: { label: 'Deployed', color: '#0d9488', bg: '#e3f4f1', border: '#a5d8d0', icon: CheckCircle },
    publishing: { label: 'Publishing', color: '#d97706', bg: '#e3f4f1', border: '#a5d8d0', icon: Activity },
    rolled_back: { label: 'Rolled Back', color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db', icon: RotateCw },
    failed: { label: 'Failed', color: '#ef4444', bg: '#fef2f2', border: '#fecaca', icon: XCircle },
    pending: { label: 'Pending', color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', icon: Clock },
  };

  // Pagination for agentlets
  const totalAgentlets = (agentlets || []).length;
  const totalPages = Math.ceil(totalAgentlets / agentletsPerPage);
  const paginatedAgentlets = (agentlets || []).slice(
    agentletPage * agentletsPerPage,
    (agentletPage + 1) * agentletsPerPage
  );

  return (
    <div>
      {/* ═══ Open in Canvas — forward Agent → Canvas materialisation ═══
          Materialises the agent's canonical AgentDefinition into a Canvas
          DAG, persists it as a draft workflow, and navigates to the Canvas
          page with both ?agentId= and ?workflowId= so the AgentCanvasLinkBanner
          can render the back-navigation crumb. v1 is preview-only on the
          backend; the controller orchestrates the persist step on the way
          to /canvas. (Reference: AGENT_DEFINITION_JSON_PREVIEW_SPEC.md §4.) */}
      <OpenInCanvasCTA agent={agent} canWrite={canWrite} hasPermission={hasPermission} />

      {/* ═══ Agentlets Live Section ═══ */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 650, color: '#1c1917', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
              <Server size={17} style={{ verticalAlign: 'middle', marginRight: 8, color: '#0d9488' }} />
              Agentlets Live
              <span style={{
                marginLeft: 8, fontSize: 12, fontWeight: 500, color: '#78716c',
                background: '#f5f5f4', padding: '2px 8px', borderRadius: 6,
              }}>
                {totalAgentlets}
              </span>
            </h3>
            <p style={{ fontSize: 12, color: '#78716c', margin: 0 }}>
              Auto-discovered agentlet containers registered to the platform.
            </p>
          </div>
          <button onClick={onRefreshAgentlets} style={btnBase} disabled={loadingAgentlets}>
            <RotateCw size={12} style={loadingAgentlets ? { animation: 'spin 1s linear infinite' } : {}} /> Refresh
          </button>
        </div>

        {loadingAgentlets && totalAgentlets === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: '#a1a1aa', fontSize: 14 }}>Loading agentlets...</div>
        )}

        {!loadingAgentlets && totalAgentlets === 0 && (
          <div style={{
            padding: '24px', borderRadius: 8, border: '1px dashed #d4d4d8',
            background: '#fafafa', textAlign: 'center',
          }}>
            <Server size={28} style={{ color: '#a1a1aa', marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#52525b', marginBottom: 4 }}>No agentlets connected</div>
            <div style={{ fontSize: 12, color: '#71717a', marginBottom: 12 }}>
              Start a standalone agentlet container and point it to this platform. It will auto-register.
            </div>
            <div style={{
              background: '#1f2937', color: '#e5e7eb', padding: '12px 14px', borderRadius: 6,
              fontFamily: 'monospace', fontSize: 11, lineHeight: '1.5', textAlign: 'left', display: 'inline-block',
            }}>
              <div>PLATFORM_URL={`${window.location.origin.replace(/:\d+$/, ':8443')}/api/v1`}</div>
              <div>PLATFORM_WS_URL={`${window.location.origin.replace(/^http/, 'ws').replace(/:\d+$/, ':8443')}/ws`}</div>
            </div>
          </div>
        )}

        {totalAgentlets > 0 && (
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#e3f4f1', borderBottom: '1px solid #a5d8d0' }}>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Agentlet ID</th>
                  <th style={thStyle}>Gateway URL</th>
                  <th style={thStyle}>Agents</th>
                  <th style={thStyle}>Skills</th>
                  <th style={thStyle}>Version</th>
                  <th style={thStyle}>Last Heartbeat</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAgentlets.map((ag) => {
                  const sCfg = agentletStatusCfg[ag.status] || agentletStatusCfg.disconnected;
                  return (
                    <tr key={ag.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                          background: sCfg.bg, color: sCfg.color, border: `1px solid ${sCfg.border}`,
                        }}>{sCfg.label}</span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                        {ag.displayName || ag.agentletId?.substring(0, 16) || '—'}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11 }}>
                        {ag.gatewayUrl ? (
                          <a href={ag.gatewayUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#0d9488', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                            {ag.gatewayUrl.length > 35 ? ag.gatewayUrl.substring(0, 35) + '...' : ag.gatewayUrl}
                            <ExternalLink size={10} />
                          </a>
                        ) : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{ag.agentCount || 0}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{ag.skillCount || 0}</td>
                      <td style={{ ...tdStyle, fontSize: 11 }}>{ag.version || '—'}</td>
                      <td style={{ ...tdStyle, fontSize: 11, color: '#71717a' }}>
                        {ag.lastHeartbeat ? new Date(ag.lastHeartbeat).toLocaleString() : '—'}
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => onPingAgentlet(ag.id)}
                          disabled={pingingAgentlet === ag.id}
                          style={{ ...btnBase, fontSize: 11, padding: '3px 8px' }}
                        >
                          {pingingAgentlet === ag.id ? (
                            <><RotateCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Pinging</>
                          ) : (
                            <><Activity size={10} /> Ping</>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 16px', borderTop: '1px solid #e4e4e7', fontSize: 12, color: '#71717a',
              }}>
                <span>Showing {agentletPage * agentletsPerPage + 1}–{Math.min((agentletPage + 1) * agentletsPerPage, totalAgentlets)} of {totalAgentlets}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => onAgentletPageChange(Math.max(0, agentletPage - 1))}
                    disabled={agentletPage === 0} style={{ ...btnBase, fontSize: 11, padding: '2px 8px' }}>← Prev</button>
                  <button onClick={() => onAgentletPageChange(Math.min(totalPages - 1, agentletPage + 1))}
                    disabled={agentletPage >= totalPages - 1} style={{ ...btnBase, fontSize: 11, padding: '2px 8px' }}>Next →</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div style={{
        padding: '10px 14px', marginBottom: 20, borderRadius: 8,
        background: '#f0f9ff', border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Users size={14} style={{ color: '#0369a1' }} />
        {/* v5.3.0 — Read-only assignments: all management goes through Teams page (rollback: restore editable version) */}
        <span style={{ fontSize: 12, color: '#0c4a6e' }}>
          Assignments are managed through <strong><a href="/team" style={{ color: '#0369a1', textDecoration: 'underline' }}>Teams Management</a></strong>.
          {assignedCount > 0 && ` • ${userCount} user${userCount !== 1 ? 's' : ''}, ${groupCount} group${groupCount !== 1 ? 's' : ''} assigned.`}
        </span>
      </div>

      {/* ═══ Skill Summary Section ═══ */}
      {(() => {
        const skills = agent.skills || [];
        if (skills.length === 0) return null;

        const reviewMap = {};
        (skillReviews || []).forEach((r) => {
          if (!reviewMap[r.skillId] || new Date(r.createdAt) > new Date(reviewMap[r.skillId].createdAt)) {
            reviewMap[r.skillId] = r;
          }
        });

        const readySkills = skills.filter((s) => s.skillPhase === 'act' || (reviewMap[s.id] && s.skillPhase !== 'build'));
        const pendingSkills = skills.filter((s) => s.skillPhase !== 'act' && !reviewMap[s.id]);

        const autonomyLabels = {
          HUMAN_CONTROLLED: { label: 'Human Controlled', color: '#6b7280', bg: '#f3f4f6' },
          SUPERVISED: { label: 'Supervised', color: '#78716c', bg: '#f5f5f4' },
          ASSISTED: { label: 'Assisted', color: '#8b5cf6', bg: '#f5f3ff' },
          AUTONOMOUS: { label: 'Autonomous', color: '#d97706', bg: '#e3f4f1' },
          FULL_AUTO: { label: 'Full Auto', color: '#0d9488', bg: '#e3f4f1' },
        };

        return (
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 650, color: '#1c1917', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
                  <Zap size={17} style={{ verticalAlign: 'middle', marginRight: 8, color: '#d97706' }} />
                  Skill Summary
                  <span style={{
                    marginLeft: 8, fontSize: 11, fontWeight: 500, color: '#57534e',
                    background: '#e3f4f1', padding: '2px 8px', borderRadius: 6,
                  }}>
                    {readySkills.length} active
                  </span>
                  {pendingSkills.length > 0 && (
                    <span style={{
                      marginLeft: 4, fontSize: 11, fontWeight: 500, color: '#d97706',
                      background: '#e3f4f1', padding: '2px 8px', borderRadius: 6,
                    }}>
                      {pendingSkills.length} pending
                    </span>
                  )}
                </h3>
                <p style={{ fontSize: 12, color: '#78716c', margin: 0 }}>
                  Skills included in the next publish. New skills start in Build phase.
                </p>
              </div>
              {canCreateSkill && agent?.isRunbookManaged !== true && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {onImportFromConnector && (
                    <button onClick={onImportFromConnector} style={{ ...btnBase, fontWeight: 600, fontSize: 12 }}>
                      <Download size={14} /> Import
                    </button>
                  )}
                  {onAddSkill && (
                    <button onClick={onAddSkill} style={{ ...btnPrimary, background: '#52525b' }}>
                      <Plus size={16} /> Add Skill
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginBottom: 8 }}>
              {skills.map((skill) => {
                const review = reviewMap[skill.id];
                const isActive = skill.skillPhase === 'act' || !!review;
                const score = skill.evalScore != null ? (skill.evalScore * 100).toFixed(0) : null;
                const autoConfig = autonomyLabels[skill.autonomy] || autonomyLabels.HUMAN_CONTROLLED;

                return (
                  <div key={skill.id} style={{
                    ...cardStyle, padding: '12px 16px',
                    borderLeft: `4px solid ${isActive ? '#57534e' : '#d97706'}`,
                    opacity: isActive ? 1 : 0.75,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: '#18181b', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{skill.name}</span>
                          {/* Skill-type chip — kept identical in shape & colour to
                              the SkillCardBuild rendering above so the visual
                              identity (Workflow / Connector / Policy) survives
                              promotion across phases. Reported regression: chip
                              was disappearing on Act-phase cards because this
                              renderer never had it; now mirrors line ~3786.
                              Hidden for `standard` — that's the implicit
                              default and showing a chip for it is noise. */}
                          {skill.skillType && skill.skillType !== 'standard' && (() => {
                            const st = SKILL_TYPES.find((t) => t.id === skill.skillType);
                            return st ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                background: `${st.color}10`, color: st.color,
                                border: `1px solid ${st.color}25`,
                                whiteSpace: 'nowrap',
                              }}>
                                <span style={{ fontSize: 10 }}>{st.icon}</span> {st.label}
                              </span>
                            ) : null;
                          })()}
                          {isActive ? (
                            <span style={{
                              fontSize: 9, padding: '2px 6px', borderRadius: 4,
                              background: '#e3f4f1', color: '#166534', fontWeight: 700,
                            }}>ACTIVE</span>
                          ) : (
                            <span style={{
                              fontSize: 9, padding: '2px 6px', borderRadius: 4,
                              background: '#e3f4f1', color: '#92400e', fontWeight: 700,
                            }}>PENDING REVIEW</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#71717a', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{skill.description}</div>
                      </div>
                      {score && (
                        <div style={{
                          padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                          color: parseFloat(score) >= 90 ? '#57534e' : parseFloat(score) >= 70 ? '#d97706' : '#ef4444',
                          background: parseFloat(score) >= 90 ? '#e3f4f1' : parseFloat(score) >= 70 ? '#e3f4f1' : '#fef2f2',
                        }}>
                          {score}%
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 11, marginBottom: 8 }}>
                      <div>
                        <span style={{ color: '#a1a1aa' }}>Autonomy: </span>
                        <span style={{
                          padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                          background: autoConfig.bg, color: autoConfig.color, fontSize: 10,
                        }}>{autoConfig.label}</span>
                      </div>
                      <div>
                        <span style={{ color: '#a1a1aa' }}>Inference: </span>
                        <span style={{ color: '#52525b', fontWeight: 500 }}>
                          {skill.inferenceMode?.replace(/_/g, ' ').toLowerCase() || '—'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#a1a1aa' }}>Risk: </span>
                        <span style={{
                          padding: '1px 6px', borderRadius: 3, fontWeight: 600, fontSize: 10,
                          background: skill.riskLevel === 'critical' ? '#fef2f2' : skill.riskLevel === 'high' ? '#e3f4f1' : '#f5f5f4',
                          color: skill.riskLevel === 'critical' ? '#dc2626' : skill.riskLevel === 'high' ? '#d97706' : '#57534e',
                        }}>{skill.riskLevel || 'low'}</span>
                      </div>
                      <div>
                        <span style={{ color: '#a1a1aa' }}>Review: </span>
                        <span style={{ color: isActive ? '#166534' : '#92400e', fontWeight: 500 }}>
                          {review ? review.decision : 'Pending'}
                        </span>
                      </div>
                    </div>

                    {/* Actions: lifecycle-locked for non-Build skills */}
                    <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #f0f0f0', paddingTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {(skill.skillPhase || 'build') !== 'build' ? (
                        <>
                          {/* Locked — show regress button instead of edit/delete */}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: 10, padding: '2px 7px', borderRadius: 5, fontWeight: 600,
                            background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                          }}>
                            <Lock size={9} /> Locked
                          </span>
                          {onRegressSkill && hasPermission('phase.advance') && (
                            <button onClick={() => onRegressSkill(skill)} style={{
                              ...btnBase, fontSize: 11, padding: '3px 8px',
                              background: '#e3f4f1', color: '#0a766b', border: '1px solid #a5d8d0',
                            }}>
                              <RotateCcw size={10} /> Regress to Build
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {canEditSkillPerm && onEditSkill && (
                            <button onClick={() => onEditSkill(skill)} style={{ ...btnBase, fontSize: 11, padding: '3px 8px' }}>
                              <Edit2 size={10} /> Edit
                            </button>
                          )}
                          {/* Published-asset deletion lockdown — same gate as
                              SkillCardBuild. Skills in Build with wasPublished=true
                              get a disabled Delete and an Unpublish action that
                              clears the published flags AND re-syncs the agentlet. */}
                          {canDeleteSkillPerm && onDeleteSkill && (() => {
                            const isGhostPublished = skill.wasPublished === true || skill.pushed === true;
                            if (isGhostPublished) {
                              return (
                                <>
                                  <button
                                    disabled
                                    title={"Skill has been published — Unpublish first to drop "
                                         + "it from the agentlet's gateway bundle, then delete."}
                                    style={{
                                      ...btnBase, fontSize: 11, padding: '3px 8px',
                                      color: '#ef4444', opacity: 0.4, cursor: 'not-allowed',
                                    }}
                                  >
                                    <Trash2 size={10} /> Delete
                                  </button>
                                  {onUnpublishSkill && (
                                    <button
                                      onClick={() => onUnpublishSkill(skill)}
                                      title={"Clears the published flags and re-syncs the agentlet "
                                           + "so the skill is dropped from the gateway."}
                                      style={{
                                        ...btnBase, fontSize: 11, padding: '3px 8px',
                                        background: '#e3f4f1', color: '#0a766b',
                                        border: '1px solid #a5d8d0',
                                      }}
                                    >
                                      <Upload size={10} style={{ transform: 'rotate(180deg)' }} /> Unpublish
                                    </button>
                                  )}
                                </>
                              );
                            }
                            return (
                              <button onClick={() => onDeleteSkill(skill)} style={{ ...btnBase, fontSize: 11, padding: '3px 8px', color: '#ef4444' }}>
                                <Trash2 size={10} /> Delete
                              </button>
                            );
                          })()}
                        </>
                      )}
                      {/* Advance button for skills not yet in Act */}
                      {skill.skillPhase && skill.skillPhase !== 'act' && onAdvanceSkill && (
                        <button onClick={() => onAdvanceSkill(skill)} style={{
                          ...btnBase, fontSize: 11, padding: '3px 8px',
                          background: '#f5f5f4', color: '#0a766b', border: '1px solid #d6d3d1',
                        }}>
                          <ArrowRight size={10} /> Advance ({skill.skillPhase === 'build' ? 'Build→Decide' : 'Decide→Act'})
                        </button>
                      )}
                      {/* Re-push button for Act skills that were edited (pushed=false) */}
                      {skill.skillPhase === 'act' && skill.pushed === false && onPushSkill && (
                        <button onClick={() => onPushSkill(skill)} style={{
                          ...btnBase, fontSize: 11, padding: '3px 8px',
                          background: '#e3f4f1', color: '#0a766b', border: '1px solid #a5d8d0',
                        }}>
                          <Upload size={10} /> Re-push
                        </button>
                      )}
                      {/* Regression count indicator */}
                      {skill.regressionCount > 0 && (
                        <span style={{
                          fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                          background: '#e3f4f1', color: '#0a766b', border: '1px solid #fecdd3',
                        }}>
                          regressed {skill.regressionCount}x
                        </span>
                      )}
                      {/* Phase badge for non-Act skills */}
                      {skill.skillPhase && skill.skillPhase !== 'act' && (
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, marginLeft: 'auto',
                          background: '#e3f4f1', color: '#0a766b',
                        }}>
                          {skill.skillPhase?.toUpperCase() || 'BUILD'}
                        </span>
                      )}
                      {/* Unpushed badge for edited Act skills */}
                      {skill.skillPhase === 'act' && skill.pushed === false && (
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, marginLeft: 'auto',
                          background: '#fef3c7', color: '#92400e',
                        }}>
                          EDITED — NEEDS RE-PUSH
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {pendingSkills.length > 0 && (
              <div style={{
                padding: '8px 14px', borderRadius: 6, fontSize: 11,
                background: '#e3f4f1', border: '1px solid #a5d8d0', color: '#085041',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <AlertTriangle size={12} />
                {pendingSkills.length} skill{pendingSkills.length > 1 ? 's' : ''} not yet in Act phase — advance through <strong style={{ margin: '0 3px' }}>Build</strong> → <strong style={{ margin: '0 3px' }}>Decide</strong> to activate. New skills can be added and will progress independently.
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ User & Group Assignments Section ═══ */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', margin: '0 0 4px 0' }}>
              <Users size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              User & Group Assignments ({assignedCount})
            </h3>
            {/* v5.3.0 — Read-only assignments display (rollback: restore Assign button + editable text) */}
            <p style={{ fontSize: 12, color: '#71717a', margin: 0 }}>
              View users and groups assigned to this agent. Manage assignments in Teams.
            </p>
          </div>
          {/* G380 — Deep-link to the Teams Assignments tab already opened on
              THIS agent (consumed by AssignmentsInventory's `?agent=` reader),
              instead of dumping the operator on the bare Users tab to hunt for
              the agent. Same contract the v2 AgentDetailPageV2 button uses. */}
          <a href={`/team?tab=assignments&agent=${agent.id}`} style={{
            ...btnPrimary, background: '#52525b', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Users size={14} /> Manage in Teams
          </a>
        </div>

        {agentAssignments.length === 0 && (
          <EmptyState text="No assignments yet. Go to Teams Management to assign users or groups before publishing." />
        )}

        {agentAssignments.length > 0 && (
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#e3f4f1', borderBottom: '1px solid #a5d8d0' }}>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Access Level</th>
                  <th style={thStyle}>Assigned By</th>
                  <th style={thStyle}>Assigned</th>
                </tr>
              </thead>
              <tbody>
                {assignmentPagination.paginate(agentAssignments).map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>
                      <Badge status={a.userId ? 'info' : 'warning'} useLabel={false}>
                        {a.userId ? <><User size={10} /> User</> : <><Users size={10} /> Group</>}
                      </Badge>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{resolveAssignmentName(a)}</td>
                    <td style={tdStyle}>
                      <Badge status={a.accessLevel === 'admin' ? 'critical' : a.accessLevel === 'manage' ? 'warning' : 'success'}>
                        {a.accessLevel}
                      </Badge>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#71717a' }}>
                      {resolveUserName(a.assignedBy, a.assignedByName)}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#71717a' }}>
                      {a.createdAt && new Date(a.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              totalItems={agentAssignments.length}
              page={assignmentPagination.page}
              pageSize={assignmentPagination.pageSize}
              onPageChange={assignmentPagination.setPage}
              onPageSizeChange={assignmentPagination.setPageSize}
              noun="assignments"
            />
          </div>
        )}
      </div>

      {/* ═══ Publish Section ═══ */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', margin: '0 0 4px 0' }}>
              <Upload size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Publish
            </h3>
            <p style={{ fontSize: 12, color: '#71717a', margin: 0 }}>
              Validate and deploy to selected agentlet containers.
              {agent.published && ` Current version: v${agent.publishVersion || 0}.`}
              {` ${actReadySkills.length} skill${actReadySkills.length !== 1 ? 's' : ''} ready.`}
            </p>
          </div>
          {canPublish && isCurrentPhase && (
            <button onClick={onPublish} disabled={publishing}
              style={{ ...btnSuccess, fontWeight: 600, padding: '8px 16px', fontSize: 13 }}>
              <Upload size={16} /> {publishing ? 'Publishing...' : 'Publish New Version'}
            </button>
          )}
        </div>

        {/* Publication History */}
        {publications && publications.length > 0 && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#52525b', marginBottom: 8 }}>
              Publication History
            </div>
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f5f5f4', borderBottom: '1px solid #e4e4e7' }}>
                    <th style={thStyle}>Version</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Target</th>
                    <th style={thStyle}>Published By</th>
                    <th style={thStyle}>Published</th>
                    {canRollback && <th style={thStyle}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {publicationPagination.paginate(publications).map((pub) => {
                    const pCfg = pubStatusConfig[pub.status] || pubStatusConfig.pending;
                    return (
                      <tr key={pub.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={tdStyle}><strong>v{pub.version}</strong></td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                            background: pCfg.bg, color: pCfg.color, border: `1px solid ${pCfg.border}`,
                          }}>{pCfg.label}</span>
                        </td>
                        <td style={tdStyle}>{pub.targetType || 'webchat'}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: '#71717a' }}>
                          {resolveUserName(pub.publishedBy, pub.publishedByName)}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 12, color: '#71717a' }}>
                          {pub.publishedAt && new Date(pub.publishedAt).toLocaleString()}
                        </td>
                        {canRollback && (
                          <td style={tdStyle}>
                            {pub.status === 'published' && (
                              <button onClick={() => onRollback(pub.id)} style={{ ...btnWarning, fontSize: 11, padding: '3px 8px' }}>
                                <RotateCw size={11} /> Rollback
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                totalItems={publications.length}
                page={publicationPagination.page}
                pageSize={publicationPagination.pageSize}
                onPageChange={publicationPagination.setPage}
                onPageSizeChange={publicationPagination.setPageSize}
                noun="publications"
              />
            </div>
          </div>
        )}

        {(!publications || publications.length === 0) && (
          <div style={{ fontSize: 12, color: '#a1a1aa', textAlign: 'center', padding: 12 }}>
            No publications yet. Publish to deploy the first version.
          </div>
        )}
      </div>

      {/* ═══ Recent Executions ═══ */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', margin: 0 }}>
            <Zap size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Recent Executions ({executions?.length || 0})
          </h3>
          <button onClick={onRefreshExecs} style={btnBase}>
            <RotateCw size={12} /> Refresh
          </button>
        </div>

        {loadingExecs && (
          <div style={{ textAlign: 'center', padding: 32, color: '#a1a1a6', fontSize: 14 }}>Loading executions...</div>
        )}

        {!loadingExecs && (!executions || executions.length === 0) && (
          <EmptyState text="No executions recorded yet." />
        )}

        {executions && executions.length > 0 && (
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f4', borderBottom: '1px solid #e4e4e7' }}>
                  <th style={thStyle}>Skill</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>Duration</th>
                  <th style={thStyle}>Created</th>
                </tr>
              </thead>
              <tbody>
                {executionPagination.paginate(executions).map((exec) => (
                  <tr key={exec.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>{exec.skillName || '—'}</td>
                    <td style={tdStyle}><Badge status={exec.status?.toLowerCase()}>{exec.status}</Badge></td>
                    <td style={tdStyle}>{exec.mode || '—'}</td>
                    <td style={tdStyle}>{exec.durationMs ? `${exec.durationMs}ms` : '—'}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#71717a' }}>
                      {exec.createdAt ? new Date(exec.createdAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              totalItems={executions.length}
              page={executionPagination.page}
              pageSize={executionPagination.pageSize}
              onPageChange={executionPagination.setPage}
              onPageSizeChange={executionPagination.setPageSize}
              noun="executions"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Reusable Sub-components
// =========================================================================

function ReviewStatusBadge({ status }) {
  const config = {
    pending_review: { bg: '#f5f5f4', color: '#78716c', label: 'Pending', icon: Clock },
    reviewed: { bg: '#f5f5f4', color: '#0a766b', label: 'Reviewed', icon: CheckCircle },
    promoted: { bg: '#e3f4f1', color: '#0d9488', label: 'Promoted', icon: CheckCircle },
    blocked: { bg: '#fef2f2', color: '#dc2626', label: 'Blocked', icon: XCircle },
  };
  const c = config[status] || config.pending_review;
  const Icon = c.icon;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
      background: c.bg, color: c.color, display: 'inline-flex', alignItems: 'center', gap: 3,
    }}>
      <Icon size={10} /> {c.label}
    </span>
  );
}

function AdvancePhaseButton({ label, sublabel, onClick, loading, color }) {
  return (
    <div style={{
      marginTop: 32, padding: 22, borderRadius: 14,
      background: `linear-gradient(135deg, ${color.bg} 0%, #ffffff 100%)`,
      border: `1px solid ${color.border}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      boxShadow: `0 2px 12px ${color.accent}15`,
    }}>
      <div>
        <div style={{ fontWeight: 650, fontSize: 15, color: color.text, marginBottom: 4, letterSpacing: '-0.01em' }}>
          {label}
        </div>
        <div style={{ fontSize: 12, color: '#78716c', lineHeight: 1.5 }}>{sublabel}</div>
      </div>
      <button
        onClick={onClick}
        disabled={loading}
        style={{
          padding: '11px 24px', borderRadius: 10, border: 'none',
          background: `linear-gradient(135deg, ${color.accent} 0%, ${color.accent}dd 100%)`,
          color: '#ffffff', fontSize: 13,
          fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 7,
          opacity: loading ? 0.7 : 1,
          fontFamily: 'inherit',
          boxShadow: `0 4px 12px ${color.accent}30`,
          transition: 'all 0.2s ease',
        }}
      >
        {loading ? <RotateCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <ArrowRight size={14} />}
        {loading ? 'Checking...' : label}
      </button>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{
      textAlign: 'center', padding: 56, background: '#ffffff',
      border: '1px dashed #e7e5e4', borderRadius: 14, color: '#a8a29e', fontSize: 14,
      lineHeight: 1.6,
    }}>
      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>📦</div>
      {text}
    </div>
  );
}

function renderGateChecks(checksRaw) {
  let checks = [];
  try {
    checks = typeof checksRaw === 'string' ? JSON.parse(checksRaw) : (checksRaw || []);
  } catch { checks = []; }

  if (checks.length === 0) return <p style={{ color: '#a1a1aa', fontSize: 13 }}>No check details available.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {checks.map((c, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
          borderRadius: 6, background: c.passed ? '#e3f4f1' : '#fef2f2',
          border: `1px solid ${c.passed ? '#a5d8d0' : '#fecaca'}`,
        }}>
          {c.passed ? <CheckCircle size={14} color="#0d9488" /> : <XCircle size={14} color="#ef4444" />}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: c.passed ? '#065f46' : '#991b1b' }}>
              {c.check?.replace(/_/g, ' ')}
            </div>
            <div style={{ fontSize: 11, color: '#71717a' }}>{c.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =========================================================================
// Shared Form Renderers & Styles
// =========================================================================
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#57534e', marginBottom: 6, letterSpacing: '0.01em' };
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e7e5e4',
  fontSize: 13, fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  background: '#fafaf9',
};
const cardStyle = {
  background: '#ffffff', border: '1px solid #e7e5e4', borderRadius: 12, padding: 18,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
};
const thStyle = { padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: '#57534e', fontSize: 12, letterSpacing: '0.01em' };
const tdStyle = { padding: '11px 14px', fontSize: 13 };

function renderField(label, value, onChange, multiline = false) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <Tag
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, ...(multiline ? { fontFamily: 'inherit', minHeight: 80 } : {}) }}
      />
    </div>
  );
}

function SkillFormWithImport({ skillForm, setSkillForm, connectors = [], agentId }) {
  const [importExpanded, setImportExpanded] = useState(null); // connectorId or null

  // Backend-registry-driven marketplace catalogue.
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();

  // v5.3.0 — Load linked workflows for workflow_skill picker (rollback: remove this block)
  const [linkedWorkflows, setLinkedWorkflows] = useState([]);
  useEffect(() => {
    if (!agentId) return;
    (async () => {
      try {
        const { getWorkflows } = await import('../services/api');
        const result = await getWorkflows({ agentId });
        setLinkedWorkflows(result?.ok ? (result.data || []) : []);
      } catch { setLinkedWorkflows([]); }
    })();
  }, [agentId]);
  // Track individual connector guardrail IDs that are selected (not just mapped platform types)
  const [selectedConnGuardrails, setSelectedConnGuardrails] = useState(new Set());

  // Find marketplace templates for linked connectors
  const linkedMarketplace = (skillForm.connectorIds || [])
    .map((cid) => {
      const conn = connectors.find((c) => c.id === cid);
      if (!conn) return null;
      const mc = findMarketplaceForInstalled(marketplaceConnectors, conn);
      return mc ? { connectorId: cid, connector: conn, marketplace: mc } : null;
    })
    .filter(Boolean);

  const GUARDRAIL_MAPPING = {
    'g-rate': 'rate_limit', 'g-cost': 'scope_limit', 'g-auth': 'audit_log',
    'g-pii': 'audit_log', 'g-geo': 'scope_limit', 'g-consent': 'approval_required',
    'g-cache': 'rate_limit', 'g-sandbox': 'scope_limit',
  };

  const applySkillTemplate = (mSkill) => {
    setSkillForm((prev) => ({
      ...prev,
      name: mSkill.name,
      description: `${mSkill.description} [${mSkill.method} ${mSkill.endpoint}]`,
      category: 'connectors',
    }));
  };

  // Toggle individual connector guardrail — each checkbox is independent
  const toggleGuardrailFromConnector = (guardrailId) => {
    const mapped = GUARDRAIL_MAPPING[guardrailId];
    if (!mapped) return;

    setSelectedConnGuardrails((prev) => {
      const next = new Set(prev);
      const wasSelected = next.has(guardrailId);
      if (wasSelected) {
        next.delete(guardrailId);
      } else {
        next.add(guardrailId);
      }

      // Recompute platform guardrails: a platform guardrail stays ON if at least one
      // connector guardrail mapping to it is still selected
      const allMappedPlatformGuardrails = new Set();
      next.forEach((gId) => {
        const m = GUARDRAIL_MAPPING[gId];
        if (m) allMappedPlatformGuardrails.add(m);
      });

      setSkillForm((prevForm) => {
        // Start from non-connector guardrails (those the user toggled directly)
        const directGuardrails = prevForm.guardrails.filter((g) => {
          // Keep guardrails that are NOT controlled by any connector mapping
          return !Object.values(GUARDRAIL_MAPPING).includes(g) || allMappedPlatformGuardrails.has(g);
        });
        // Add any newly mapped guardrails
        allMappedPlatformGuardrails.forEach((pg) => {
          if (!directGuardrails.includes(pg)) directGuardrails.push(pg);
        });
        return { ...prevForm, guardrails: directGuardrails };
      });

      return next;
    });
  };

  const toggleEval = (evalItem) => {
    setSkillForm((prev) => {
      const imported = prev.importedEvals || [];
      const has = imported.some((e) => e.name === evalItem.name);
      return {
        ...prev,
        importedEvals: has
          ? imported.filter((e) => e.name !== evalItem.name)
          : [...imported, { id: evalItem.type, name: evalItem.name, description: evalItem.description }],
      };
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {renderField('Skill Name', skillForm.name, (v) => setSkillForm({ ...skillForm, name: v }))}
      {renderField('Description', skillForm.description, (v) => setSkillForm({ ...skillForm, description: v }), true)}

      {/* v5.3.0 — Skill Type Selector (rollback: remove this block) */}
      <div>
        <label style={labelStyle}>Skill Type</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SKILL_TYPES.map((st) => {
            const isSelected = (skillForm.skillType || 'standard') === st.id;
            return (
              <button
                key={st.id}
                type="button"
                onClick={() => setSkillForm({ ...skillForm, skillType: st.id })}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: isSelected ? `2px solid ${st.color}` : '1px solid #e4e4e7',
                  background: isSelected ? '#fafaf9' : '#ffffff',
                  color: isSelected ? st.color : '#71717a',
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                  minWidth: 90, transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 18 }}>{st.icon}</span>
                <span>{st.label}</span>
                <span style={{ fontSize: 9, fontWeight: 400, color: '#a1a1aa' }}>{st.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* v5.3.0 — Policy Skill: document upload hint (rollback: remove this block) */}
      {skillForm.skillType === 'policy_skill' && (
        <div style={{
          padding: '12px 14px', borderRadius: 8,
          background: '#e3f4f1', border: '1px solid #a5d8d0',
          fontSize: 12, color: '#92400e',
        }}>
          <strong>📋 Policy Skill</strong> — This skill type enforces uploaded security policy documents during execution.
          After creation, attach policy documents (PDF, DOCX, or text) through the skill detail panel.
          The agent will use RAG-style retrieval to reference these policies when this skill runs.
        </div>
      )}

      {/* v5.3.0 — Workflow Skill: workflow picker (rollback: remove this block) */}
      {skillForm.skillType === 'workflow_skill' && (
        <div style={{
          padding: '12px 14px', borderRadius: 8,
          background: '#f5f5f4', border: '1px solid #d6d3d1',
        }}>
          <div style={{ fontSize: 12, color: '#1e40af', marginBottom: 10 }}>
            <strong>🔀 Workflow Skill</strong> — Select the canvas workflow this skill will execute when invoked by the agent.
          </div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#1e40af', marginBottom: 4, display: 'block' }}>
            Linked Workflow
          </label>
          {linkedWorkflows.length === 0 ? (
            <div style={{ fontSize: 11, color: '#71717a', fontStyle: 'italic' }}>
              No workflows linked to this agent yet. Link workflows in the <em>Linked Workflows</em> section first.
            </div>
          ) : (
            <select
              value={skillForm.linkedResourceId || ''}
              onChange={(e) => setSkillForm({ ...skillForm, linkedResourceId: e.target.value || '' })}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 6,
                border: '1px solid #d6d3d1', fontSize: 12,
                background: '#ffffff', color: '#18181b',
              }}
            >
              <option value="">— Select a workflow —</option>
              {linkedWorkflows.map((wf) => (
                <option key={wf.id} value={wf.id}>
                  {wf.name} (v{wf.version || 1} · {wf.status})
                </option>
              ))}
            </select>
          )}
          {skillForm.linkedResourceId && (
            <div style={{ fontSize: 10, color: '#0d9488', marginTop: 4 }}>
              ✓ Workflow linked — the agent will trigger this workflow when executing this skill.
            </div>
          )}
        </div>
      )}
      {/* END v5.3.0 — Skill Type Selector */}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Category</label>
          <select value={skillForm.category} onChange={(e) => setSkillForm({ ...skillForm, category: e.target.value })} style={inputStyle}>
            <option value="iam">IAM</option>
            <option value="security">Security</option>
            <option value="connectors">Connectors</option>
            <option value="analytics">Analytics</option>
            <option value="nlp">NLP</option>
            <option value="research">Research</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Risk Level</label>
          <select value={skillForm.riskLevel} onChange={(e) => setSkillForm({ ...skillForm, riskLevel: e.target.value })} style={inputStyle}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>
      {/* Autonomy & Inference Mode */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Autonomy Level</label>
          <select value={skillForm.autonomy} onChange={(e) => setSkillForm({ ...skillForm, autonomy: e.target.value })} style={inputStyle}>
            <option value="HUMAN_CONTROLLED">Human Controlled</option>
            <option value="PLATFORM_CONTROLLED">Platform Controlled</option>
            <option value="SELF_AUTONOMOUS">Self Autonomous</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Inference Mode</label>
          <select value={skillForm.inferenceMode} onChange={(e) => setSkillForm({ ...skillForm, inferenceMode: e.target.value })} style={inputStyle}>
            <option value="PLATFORM_INFERENCE">Platform Inference</option>
            <option value="SELF_INFERENCE">Self Inference</option>
            <option value="HUMAN_APPROVAL">Human Approval</option>
          </select>
        </div>
      </div>
      <div>
        <label style={labelStyle}>Guardrails</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { id: 'rate_limit', label: 'Rate Limit' }, { id: 'audit_log', label: 'Audit Log' },
            { id: 'scope_limit', label: 'Scope Limit' }, { id: 'approval_required', label: 'Approval Required' },
            { id: 'dual_approval', label: 'Dual Approval' }, { id: 'rbac_check', label: 'RBAC Check' },
          ].map((g) => (
            <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#52525b', cursor: 'pointer' }}>
              <input type="checkbox" checked={skillForm.guardrails.includes(g.id)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...skillForm.guardrails, g.id]
                    : skillForm.guardrails.filter((x) => x !== g.id);
                  setSkillForm({ ...skillForm, guardrails: next });
                }}
              />
              {g.label}
            </label>
          ))}
        </div>
      </div>
      {/* Connector Linking */}
      <div>
        <label style={labelStyle}>
          Data Connectors
          <span style={{ fontWeight: 400, color: '#a1a1aa', marginLeft: 6 }}>
            ({(skillForm.connectorIds || []).length} linked)
          </span>
        </label>
        {connectors.length === 0 ? (
          <div style={{ fontSize: 12, color: '#a1a1aa', padding: '8px 0' }}>
            No connectors assigned. Assign connectors in Team Management first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
            {connectors.map((c) => {
              const linked = (skillForm.connectorIds || []).includes(c.id);
              return (
                <label key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                  color: '#52525b', cursor: 'pointer', padding: '6px 8px',
                  borderRadius: 6, background: linked ? '#e3f4f1' : '#fafaf9',
                  border: `1px solid ${linked ? '#a5d8d0' : '#f4f4f5'}`,
                }}>
                  <input type="checkbox" checked={linked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...(skillForm.connectorIds || []), c.id]
                        : (skillForm.connectorIds || []).filter((x) => x !== c.id);
                      setSkillForm({ ...skillForm, connectorIds: next });
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#18181b' }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: '#71717a' }}>{c.type} · {c.authMethod || 'N/A'}</div>
                  </div>
                  <span style={{
                    padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                    background: c.status === 'active' ? '#e3f4f1' : '#f4f4f5',
                    color: c.status === 'active' ? '#0a766b' : '#71717a',
                  }}>{c.status}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Import from Connector — shown when linked connectors have marketplace templates */}
      {linkedMarketplace.length > 0 && (
        <div style={{
          border: '1px solid #e0e7ff', borderRadius: 8, background: '#f8faff', overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px', fontSize: 12, fontWeight: 600, color: '#3730a3',
            borderBottom: '1px solid #e0e7ff', background: '#eef2ff',
          }}>
            Import from Connector ({linkedMarketplace.length} template{linkedMarketplace.length > 1 ? 's' : ''} available)
          </div>

          {linkedMarketplace.map(({ connectorId, connector, marketplace: mc }) => {
            const isOpen = importExpanded === connectorId;
            return (
              <div key={connectorId} style={{ borderBottom: '1px solid #e0e7ff' }}>
                <button
                  onClick={() => setImportExpanded(isOpen ? null : connectorId)}
                  style={{
                    width: '100%', padding: '10px 14px', border: 'none', background: 'transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 16 }}>{mc.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#18181b' }}>{mc.name}</div>
                    <div style={{ fontSize: 10, color: '#71717a' }}>
                      {mc.skills.length} skills · {mc.guardrails.length} guardrails · {mc.evals.length} evals
                    </div>
                  </div>
                  <ChevronRight size={14} style={{
                    color: '#a1a1aa', transition: 'transform 0.2s',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
                  }} />
                </button>

                {isOpen && (
                  <div style={{ padding: '0 14px 14px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Skills Templates */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#52525b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Skill Templates ({mc.skills.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {mc.skills.map((s) => {
                          const isApplied = skillForm.name === s.name;
                          return (
                            <div key={s.id} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                              borderRadius: 6, background: isApplied ? '#e3f4f1' : '#fff',
                              border: `1px solid ${isApplied ? '#a5d8d0' : '#e4e4e7'}`, fontSize: 12,
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#18181b' }}>{s.name}</div>
                                <div style={{ fontSize: 10, color: '#71717a' }}>{s.method} {s.endpoint}</div>
                              </div>
                              <button
                                onClick={() => applySkillTemplate(s)}
                                style={{
                                  ...btnBase, fontSize: 10, padding: '2px 8px',
                                  ...(isApplied ? { background: '#e3f4f1', border: '1px solid #a5d8d0', color: '#0a766b' } : {}),
                                }}
                              >
                                {isApplied ? 'Applied' : 'Apply'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Guardrails */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#52525b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Guardrails ({mc.guardrails.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {mc.guardrails.map((g) => {
                          const mapped = GUARDRAIL_MAPPING[g.id];
                          const isActive = selectedConnGuardrails.has(g.id);
                          const sevColor = g.severity === 'critical' ? '#ef4444'
                            : g.severity === 'high' ? '#d97706' : '#78716c';
                          return (
                            <label key={g.id} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                              borderRadius: 6, cursor: mapped ? 'pointer' : 'default',
                              background: isActive ? '#ede9fe' : '#fff',
                              border: `1px solid ${isActive ? '#ddd6fe' : '#e4e4e7'}`,
                              opacity: mapped ? 1 : 0.5, fontSize: 12,
                            }}>
                              <input
                                type="checkbox"
                                checked={isActive}
                                disabled={!mapped}
                                onChange={() => toggleGuardrailFromConnector(g.id)}
                              />
                              <span style={{
                                width: 7, height: 7, borderRadius: '50%', background: sevColor, flexShrink: 0,
                              }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ color: '#18181b', fontSize: 11 }}>{g.rule}</div>
                                <div style={{ fontSize: 9, color: '#a1a1aa' }}>
                                  {g.severity} {mapped ? `→ ${mapped}` : '(no mapping)'}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Evals */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#52525b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Evals ({mc.evals.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {mc.evals.map((e) => {
                          const isSelected = (skillForm.importedEvals || []).some((ie) => ie.name === e.name);
                          const typeColor = { functional: '#78716c', negative: '#ef4444', guardrail: '#8b5cf6', cost: '#d97706', security: '#0d9488' }[e.type] || '#71717a';
                          return (
                            <label key={e.name} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                              borderRadius: 6, cursor: 'pointer',
                              background: isSelected ? '#f5f5f4' : '#fff',
                              border: `1px solid ${isSelected ? '#d6d3d1' : '#e4e4e7'}`, fontSize: 12,
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleEval(e)}
                              />
                              <span style={{
                                padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700,
                                background: `${typeColor}15`, color: typeColor, border: `1px solid ${typeColor}30`,
                              }}>{e.type}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: '#18181b', fontSize: 11 }}>{e.name}</div>
                                <div style={{ fontSize: 10, color: '#71717a' }}>{e.description}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {(skillForm.importedEvals || []).length > 0 && (
                        <div style={{ fontSize: 10, color: '#3730a3', marginTop: 6, fontStyle: 'italic' }}>
                          {(skillForm.importedEvals || []).length} eval(s) will be generated on save
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
